import type { UserRole, WorkflowCapabilityId, WorkflowSurface } from '@statura/types';

export type WorkflowProgram = 'SAH' | 'RAC' | 'PRIVATE' | 'CROSS_PROGRAM';
export type WorkflowFundingProgram = 'SAH' | 'RAC' | 'MIXED' | 'PRIVATE';
export type WorkflowOrgType = 'HOME_CARE' | 'RESIDENTIAL' | 'MIXED';
export type WorkflowCriticality = 'mandatory' | 'strongly_recommended' | 'optional';
export type WorkflowBlockTier = WorkflowCriticality;
export type WorkflowCompileStatus = 'ready' | 'ready_with_fallbacks' | 'blocked';
export type WorkflowResponsibility =
  | 'care_coordination'
  | 'clinical_review'
  | 'quality_governance'
  | 'finance_review'
  | 'workforce_roster'
  | 'frontline_assigned_worker'
  | 'manual_communication'
  | 'tenant_accountable_person';
export type WorkflowConditionKey =
  | 'always'
  | 'scheduledWorkersAffected'
  | 'clientVisibleChange'
  | 'safetyCriticalCareContextChange'
  | 'frontlineLodgementReceived'
  | 'portalLodgementReceived'
  | 'managerConfirmationConfigured'
  | 'clientCallConfigured'
  | 'visitServiceEvidenceReady'
  | 'statementClaimReadinessRequired'
  | 'clientSignatureRequired'
  | 'staffPolicyAcknowledgementRequired'
  | 'invoiceDisputeRaised'
  | 'workerEvidenceRequired'
  | 'workforceComplianceReviewRequired'
  | 'onboardingDocumentAcknowledgementRequired'
  | 'financeApprovalRequired'
  | 'financeReconciliationRequired'
  | 'independentApprovalRequired'
  | 'successorPlanOrSahReadinessRequired'
  // SAH EOL activation (fail-closed like the funding-transition keys above:
  // no facts mapping, so conditionMatches defaults to false until the
  // runtime bridge supplies them).
  | 'readinessDriftOrBlocked'
  | 'independentActivatorRequired'
  | 'closeoutRequired';
export type WorkflowActionType =
  | 'create_task'
  | 'request_acknowledgement'
  | 'notify'
  | 'record_evidence'
  | 'manual_evidence'
  | 'surface_projection'
  | 'configuration_gap';
export type WorkflowBlockerCode =
  | 'SUBSCRIPTION_INACTIVE'
  | 'ORG_TYPE_UNSUPPORTED'
  | 'MISSING_MANDATORY_MODULE'
  | 'NO_AVAILABLE_SURFACE'
  | 'NO_ACCOUNTABLE_ACTOR'
  | 'NO_QUALIFIED_ACTOR';
export type WorkflowFallbackCode =
  | 'SURFACE_UNAVAILABLE'
  | 'ROLE_FALLBACK_USED'
  | 'STRONGLY_RECOMMENDED_MODULE_MISSING'
  // D2 (external clinical-review satisfier): the qualified-clinical-review step
  // is satisfied by an external clinician's report on a CLINICAL_REVIEW referral
  // because the HOME_CARE tenant has no in-house CLINICAL_LEAD/REGISTERED_NURSE
  // and has explicitly opted in. Emitted only by the per-step external satisfier
  // gate — never widens professional scope; the clinical step's allowedCompleters
  // stay CLINICAL_LEAD|REGISTERED_NURSE.
  | 'EXTERNAL_PROFESSIONAL_SATISFIER_USED';

/**
 * D2: allowed external clinical-review provider types (delivery_providers.provider_type).
 * Must stay byte-identical to the DB gate in
 * set_external_clinical_review_fallback_controlled /
 * create_or_link_external_clinical_review_referral_controlled.
 */
export const EXTERNAL_CLINICAL_REVIEW_PROVIDER_TYPES = [
  'CLINICAL_PROVIDER',
  'GP_PRACTICE',
  'SPECIALIST',
  'HOSPICE_PALLIATIVE',
  'ALLIED_HEALTH',
] as const;
export type ExternalClinicalReviewProviderType =
  (typeof EXTERNAL_CLINICAL_REVIEW_PROVIDER_TYPES)[number];

export interface WorkflowExternalClinicalReviewConfig {
  enabled: boolean;
  providerId: string | null;
  providerStatus: 'ACTIVE' | 'INACTIVE' | null;
  providerType: string | null;
}

export interface WorkflowCompileContext {
  orgType: WorkflowOrgType;
  enabledModules: readonly string[] | null;
  availableSurfaces: Partial<Record<WorkflowSurface, boolean>>;
  activeRoles: readonly UserRole[];
  // Required (no default) — every caller must explicitly resolve the tenant's
  // subscription state. A previous version made this optional with `false`
  // gating only on explicit false, which meant a caller that forgot to pass
  // it would activate workflows for a frozen tenant. The runtime now blocks
  // any context whose subscriptionActive is not exactly `true`.
  subscriptionActive: boolean;
  // D2: per-tenant external clinical-review fallback configuration. Absent /
  // disabled everywhere except a HOME_CARE tenant that has opted in with an
  // ACTIVE clinical provider. Only consulted by the qualified-clinical-review
  // external satisfier gate.
  externalClinicalReview?: WorkflowExternalClinicalReviewConfig;
}

export interface WorkflowEventFacts {
  scheduledWorkersAffected?: boolean;
  clientVisibleChange?: boolean;
  safetyCriticalCareContextChange?: boolean;
  frontlineLodgementReceived?: boolean;
  portalLodgementReceived?: boolean;
  managerConfirmationConfigured?: boolean;
  clientCallConfigured?: boolean;
  visitServiceEvidenceReady?: boolean;
  statementClaimReadinessRequired?: boolean;
  clientSignatureRequired?: boolean;
  staffPolicyAcknowledgementRequired?: boolean;
  invoiceDisputeRaised?: boolean;
  workerEvidenceRequired?: boolean;
  workforceComplianceReviewRequired?: boolean;
  onboardingDocumentAcknowledgementRequired?: boolean;
  financeApprovalRequired?: boolean;
}

export interface WorkflowModuleRequirement {
  path: string;
  criticality: WorkflowCriticality;
}

export interface WorkflowStepFallback {
  surface: WorkflowSurface;
  actionType: WorkflowActionType;
  responsibility?: WorkflowResponsibility;
  reason: string;
}

export interface WorkflowStepDefinition {
  id: string;
  label: string;
  actionType: WorkflowActionType;
  responsibility: WorkflowResponsibility;
  capabilityId: WorkflowCapabilityId;
  preferredSurfaces: readonly WorkflowSurface[];
  condition?: WorkflowConditionKey;
  professionalScope?: boolean;
  legalCritical?: boolean;
  fallback?: WorkflowStepFallback;
  evidenceType?: string;
  relatedResourceType?: string;
}

export interface WorkflowDefinition {
  id: string;
  title: string;
  program: WorkflowProgram;
  triggerEvent: string;
  supportedOrgTypes: readonly WorkflowOrgType[];
  moduleRequirements: readonly WorkflowModuleRequirement[];
  steps: readonly WorkflowStepDefinition[];
}

export type WorkflowManifestActivationOwner = 'notification_events_dispatcher';
export type WorkflowManifestTaskCreationBoundary = 'create_workflow_task_controlled';
export type WorkflowManifestCompletionBoundary = 'complete_workflow_task_controlled';
export type WorkflowManifestNoQualifiedActorBehavior =
  | 'fallback_to_accountable_actor'
  | 'fail_closed'
  | 'manual_evidence_required';
export type WorkflowManifestAllocationStrategy =
  | 'not_applicable'
  | 'first_acceptance_by_acceptance_time';

export interface WorkflowManifestTriggerContract {
  eventName: string;
  schemaVersion: number;
  sourceTable: string;
  sourceResourceType: string;
  requiredFields: readonly string[];
  idempotencyKey: string;
  activationOwner: WorkflowManifestActivationOwner;
}

export interface WorkflowManifestRuntimeOwnership {
  activationOwner: WorkflowManifestActivationOwner;
  taskCreation: WorkflowManifestTaskCreationBoundary;
  completion: WorkflowManifestCompletionBoundary;
  directWorkflowTableMutationAllowed: false;
  notificationAcknowledgementCompletesWorkflow: false;
}

export interface WorkflowManifestProgramApplicability {
  fundingPrograms: readonly WorkflowFundingProgram[];
  orgTypes: readonly WorkflowOrgType[];
}

export interface WorkflowManifestWorkflowBlock {
  id: string;
  tier: WorkflowBlockTier;
}

export interface WorkflowManifestSurfaceContract {
  surface: WorkflowSurface;
  capabilityId: WorkflowCapabilityId;
  required: boolean;
  fallbackSurface?: WorkflowSurface;
  fallbackAction?: WorkflowActionType;
  degradedUatCaseId: string;
}

export interface WorkflowManifestResponsibilityContract {
  responsibility: WorkflowResponsibility;
  allowedRoles: readonly UserRole[];
  fallbackCascade: readonly UserRole[];
  noQualifiedActorBehavior: WorkflowManifestNoQualifiedActorBehavior;
}

export interface WorkflowManifestTaskContract {
  stepId: string;
  category:
    | 'task'
    | 'notification'
    | 'approval'
    | 'acknowledgement'
    | 'document'
    | 'timer'
    | 'evidence';
  createsWorkflowTask: boolean;
  requiredEvidenceType?: string;
}

export interface WorkflowManifestNotificationContract {
  id: string;
  stepId?: string;
  recipientResponsibility: WorkflowResponsibility;
  surface: WorkflowSurface;
  acknowledgementCompletesWorkflow: false;
  fallbackStepId?: string;
}

export interface WorkflowManifestApprovalContract {
  id: string;
  stepId: string;
  responsibility: WorkflowResponsibility;
  providerConfigured: boolean;
}

export interface WorkflowManifestAcknowledgementContract {
  id: string;
  stepId: string;
  responsibility: WorkflowResponsibility;
  completesWorkflow: false;
  evidenceType: string;
}

export interface WorkflowManifestDocumentContract {
  id: string;
  stepId?: string;
  required: boolean;
  documentType: string;
  fallbackEvidenceType?: string;
}

export interface WorkflowManifestTimerContract {
  id: string;
  stepId: string;
  dueDaysFromStart: number;
  dueDateType: 'HARD' | 'SOFT';
  escalationResponsibility?: WorkflowResponsibility;
}

export interface WorkflowManifestEvidenceContract {
  stepId: string;
  evidenceType: string;
  required: boolean;
  linkedRecords: readonly string[];
}

export interface WorkflowManifestProviderConfigurationItem {
  key: string;
  description: string;
}

export interface WorkflowManifestProviderConfigurationBoundaries {
  mandatory: readonly WorkflowManifestProviderConfigurationItem[];
  stronglyRecommended: readonly WorkflowManifestProviderConfigurationItem[];
  optional: readonly WorkflowManifestProviderConfigurationItem[];
}

export interface WorkflowManifestIdempotencyContract {
  sourceEventKey: string;
  workflowInstanceGuard: string;
  runtimeStepGuard: string;
  generatedTaskGuard: string;
  notificationGuard: string;
  retryContract: string;
}

export interface WorkflowManifestLegalSourceNote {
  scope: string;
  source: string;
  note: string;
}

export interface WorkflowManifestUatCase {
  id: string;
  title: string;
  path: string;
  expected: string;
  degraded: boolean;
}

export interface WorkflowManifestAllocationPolicy {
  strategy: WorkflowManifestAllocationStrategy;
  biddingAllowed: boolean;
  managerConfirmation: 'not_applicable' | 'provider_configured';
}

export interface WorkflowManifest {
  id: string;
  version: number;
  title: string;
  definition: WorkflowDefinition;
  trigger: WorkflowManifestTriggerContract;
  runtimeOwnership: WorkflowManifestRuntimeOwnership;
  programApplicability: WorkflowManifestProgramApplicability;
  workflowBlock: WorkflowManifestWorkflowBlock;
  requiredModules: readonly WorkflowModuleRequirement[];
  surfaces: readonly WorkflowManifestSurfaceContract[];
  responsibilities: readonly WorkflowManifestResponsibilityContract[];
  tasks: readonly WorkflowManifestTaskContract[];
  notifications: readonly WorkflowManifestNotificationContract[];
  approvals: readonly WorkflowManifestApprovalContract[];
  acknowledgements: readonly WorkflowManifestAcknowledgementContract[];
  documents: readonly WorkflowManifestDocumentContract[];
  timers: readonly WorkflowManifestTimerContract[];
  evidence: readonly WorkflowManifestEvidenceContract[];
  providerConfiguration: WorkflowManifestProviderConfigurationBoundaries;
  idempotency: WorkflowManifestIdempotencyContract;
  legalSourceNotes: readonly WorkflowManifestLegalSourceNote[];
  uatCases: readonly WorkflowManifestUatCase[];
  allocationPolicy?: WorkflowManifestAllocationPolicy;
  status: 'runtime_ready' | 'runtime_activation_ready' | 'manifest_only';
}

export interface WorkflowManifestValidationIssue {
  code: string;
  message: string;
  path: string;
}

export interface WorkflowCompiledAction {
  stepId: string;
  label: string;
  actionType: WorkflowActionType;
  surface: WorkflowSurface;
  responsibility: WorkflowResponsibility;
  assignedRole: UserRole;
  allowedCompleterRoles: readonly UserRole[];
  capabilityId: WorkflowCapabilityId;
  evidenceType?: string;
  relatedResourceType?: string;
  fallbackReason?: string;
  // D2 external clinical-review satisfier — administrative overlay on a
  // professional-scope step. `responsibility` and `allowedCompleterRoles` stay
  // clinical (CLINICAL_LEAD|REGISTERED_NURSE); these separate fields carry the
  // administrative ownership + event-only completion contract so the runtime
  // assigns/routes it administratively and the DB gate keys off satisfierKind.
  professionalScope?: boolean;
  satisfierKind?: 'EXTERNAL_PROFESSIONAL_EVIDENCE';
  administrativeResponsibility?: WorkflowResponsibility;
  completionMode?: 'REFERRAL_REPORT_EVENT_ONLY';
  requiredReferralKind?: 'CLINICAL_REVIEW';
}

export interface WorkflowCompileBlocker {
  code: WorkflowBlockerCode;
  message: string;
  stepId?: string;
  modulePath?: string;
  responsibility?: WorkflowResponsibility;
}

export interface WorkflowCompileFallback {
  code: WorkflowFallbackCode;
  message: string;
  stepId?: string;
  fromSurface?: WorkflowSurface;
  toSurface?: WorkflowSurface;
  fromRole?: UserRole;
  toRole?: UserRole;
  modulePath?: string;
}

export interface WorkflowCompileResult {
  definitionId: string;
  status: WorkflowCompileStatus;
  actions: readonly WorkflowCompiledAction[];
  blockers: readonly WorkflowCompileBlocker[];
  fallbacks: readonly WorkflowCompileFallback[];
}

interface ResponsibilityRule {
  cascade: readonly UserRole[];
  allowedCompleters: readonly UserRole[];
}

const RESPONSIBILITY_RULES: Record<WorkflowResponsibility, ResponsibilityRule> = {
  care_coordination: {
    cascade: ['COORDINATOR', 'CASE_MANAGER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
    allowedCompleters: ['COORDINATOR', 'CASE_MANAGER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
  },
  clinical_review: {
    cascade: ['CLINICAL_LEAD', 'REGISTERED_NURSE', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
    allowedCompleters: ['CLINICAL_LEAD', 'REGISTERED_NURSE'],
  },
  quality_governance: {
    cascade: ['QUALITY_OFFICER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
    allowedCompleters: ['QUALITY_OFFICER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
  },
  finance_review: {
    cascade: ['FINANCE_OFFICER', 'TENANT_ADMIN'],
    allowedCompleters: ['FINANCE_OFFICER', 'TENANT_ADMIN'],
  },
  workforce_roster: {
    cascade: ['COORDINATOR', 'TEAM_LEADER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
    allowedCompleters: ['COORDINATOR', 'TEAM_LEADER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
  },
  frontline_assigned_worker: {
    cascade: [
      'CARE_WORKER',
      'AGENCY_CONTRACTOR',
      'TEAM_LEADER',
      'REGISTERED_NURSE',
      'ENROLLED_NURSE',
      'COORDINATOR',
      'TENANT_ADMIN',
    ],
    allowedCompleters: [
      'CARE_WORKER',
      'AGENCY_CONTRACTOR',
      'TEAM_LEADER',
      'REGISTERED_NURSE',
      'ENROLLED_NURSE',
      'ALLIED_HEALTH',
    ],
  },
  manual_communication: {
    cascade: ['COORDINATOR', 'CASE_MANAGER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
    allowedCompleters: ['COORDINATOR', 'CASE_MANAGER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
  },
  tenant_accountable_person: {
    cascade: ['TENANT_ADMIN', 'SYSTEM_ADMIN'],
    allowedCompleters: ['TENANT_ADMIN', 'SYSTEM_ADMIN'],
  },
};

const REQUIRED_MANIFEST_SURFACES: readonly WorkflowSurface[] = [
  'web',
  'care-worker-app',
  'client-portal',
];

function hasItems(value: readonly unknown[] | undefined): value is readonly unknown[] {
  return Array.isArray(value) && value.length > 0;
}

function hasText(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function pushIssue(
  issues: WorkflowManifestValidationIssue[],
  code: string,
  path: string,
  message: string,
) {
  issues.push({ code, path, message });
}

export function validateWorkflowManifestGateD(
  manifest: WorkflowManifest,
): WorkflowManifestValidationIssue[] {
  const issues: WorkflowManifestValidationIssue[] = [];

  if (!manifest.id) pushIssue(issues, 'manifest.id.required', 'id', 'Workflow id is required.');
  if (!Number.isInteger(manifest.version) || manifest.version < 1) {
    pushIssue(issues, 'manifest.version.required', 'version', 'Workflow version must be >= 1.');
  }
  if (manifest.definition.id !== manifest.id) {
    pushIssue(
      issues,
      'manifest.definition.id_mismatch',
      'definition.id',
      'Manifest id and workflow definition id must match.',
    );
  }
  if (manifest.trigger.eventName !== manifest.definition.triggerEvent) {
    pushIssue(
      issues,
      'manifest.trigger.mismatch',
      'trigger.eventName',
      'Trigger event contract must match the compiler definition trigger event.',
    );
  }
  if (manifest.trigger.schemaVersion < 1) {
    pushIssue(
      issues,
      'manifest.trigger.schema_version',
      'trigger.schemaVersion',
      'Trigger schema version must be explicit.',
    );
  }
  if (!hasItems(manifest.trigger.requiredFields)) {
    pushIssue(
      issues,
      'manifest.trigger.required_fields',
      'trigger.requiredFields',
      'Trigger contract must declare required payload fields.',
    );
  }
  if (!hasText(manifest.trigger.sourceTable)) {
    pushIssue(
      issues,
      'manifest.trigger.source_table',
      'trigger.sourceTable',
      'Trigger contract must declare the source table.',
    );
  }
  if (!hasText(manifest.trigger.sourceResourceType)) {
    pushIssue(
      issues,
      'manifest.trigger.source_resource_type',
      'trigger.sourceResourceType',
      'Trigger contract must declare the source resource type.',
    );
  }
  if (!hasText(manifest.trigger.idempotencyKey)) {
    pushIssue(
      issues,
      'manifest.trigger.idempotency_key',
      'trigger.idempotencyKey',
      'Trigger contract must declare its idempotency key.',
    );
  }
  if (manifest.trigger.activationOwner !== 'notification_events_dispatcher') {
    pushIssue(
      issues,
      'manifest.activation.owner',
      'trigger.activationOwner',
      'Workflow activation must be dispatcher-owned through notification_events.',
    );
  }

  if (manifest.runtimeOwnership.activationOwner !== 'notification_events_dispatcher') {
    pushIssue(
      issues,
      'manifest.runtime.activation_owner',
      'runtimeOwnership.activationOwner',
      'Runtime activation owner must be notification_events_dispatcher.',
    );
  }
  if (manifest.runtimeOwnership.taskCreation !== 'create_workflow_task_controlled') {
    pushIssue(
      issues,
      'manifest.runtime.task_creation',
      'runtimeOwnership.taskCreation',
      'Runtime task creation must use create_workflow_task_controlled.',
    );
  }
  if (manifest.runtimeOwnership.completion !== 'complete_workflow_task_controlled') {
    pushIssue(
      issues,
      'manifest.runtime.completion',
      'runtimeOwnership.completion',
      'Task completion/progression must use complete_workflow_task_controlled.',
    );
  }
  if (manifest.runtimeOwnership.directWorkflowTableMutationAllowed !== false) {
    pushIssue(
      issues,
      'manifest.runtime.direct_mutation',
      'runtimeOwnership.directWorkflowTableMutationAllowed',
      'Direct workflow_instances/workflow_tasks mutation is not allowed.',
    );
  }
  if (manifest.runtimeOwnership.notificationAcknowledgementCompletesWorkflow !== false) {
    pushIssue(
      issues,
      'manifest.runtime.notification_ack',
      'runtimeOwnership.notificationAcknowledgementCompletesWorkflow',
      'Notification acknowledgement cannot complete workflow work.',
    );
  }

  if (!hasItems(manifest.programApplicability.fundingPrograms)) {
    pushIssue(
      issues,
      'manifest.programs.required',
      'programApplicability.fundingPrograms',
      'Program applicability must include SAH/RAC/MIXED/private coverage.',
    );
  }
  if (
    manifest.definition.program !== 'CROSS_PROGRAM' &&
    !manifest.programApplicability.fundingPrograms.includes(manifest.definition.program)
  ) {
    pushIssue(
      issues,
      'manifest.programs.definition_mismatch',
      'programApplicability.fundingPrograms',
      'Program applicability must include the compiler definition program.',
    );
  }
  if (!hasItems(manifest.programApplicability.orgTypes)) {
    pushIssue(
      issues,
      'manifest.org_types.required',
      'programApplicability.orgTypes',
      'Organisation type applicability is required.',
    );
  }
  for (const orgType of manifest.definition.supportedOrgTypes) {
    if (!manifest.programApplicability.orgTypes.includes(orgType)) {
      pushIssue(
        issues,
        'manifest.org_types.definition_mismatch',
        'programApplicability.orgTypes',
        `Organisation type ${orgType} is supported by the definition but missing from the manifest.`,
      );
    }
  }
  if (!manifest.workflowBlock.id) {
    pushIssue(
      issues,
      'manifest.workflow_block.id',
      'workflowBlock.id',
      'Workflow block id is required for Gate D.',
    );
  }
  if (!manifest.workflowBlock.tier) {
    pushIssue(
      issues,
      'manifest.workflow_block.tier',
      'workflowBlock.tier',
      'Workflow block tier is required for Gate D.',
    );
  }

  const modulePaths = new Set(manifest.requiredModules.map((module) => module.path));
  for (const moduleRequirement of manifest.definition.moduleRequirements) {
    if (!modulePaths.has(moduleRequirement.path)) {
      pushIssue(
        issues,
        'manifest.module.missing',
        'requiredModules',
        `Module ${moduleRequirement.path} is present in the compiler definition but missing from the manifest.`,
      );
    }
  }

  const uatCaseIds = new Set(manifest.uatCases.map((uatCase) => uatCase.id));
  const surfaces = new Set(manifest.surfaces.map((surface) => surface.surface));
  for (const surface of REQUIRED_MANIFEST_SURFACES) {
    if (!surfaces.has(surface)) {
      pushIssue(
        issues,
        'manifest.surface.missing',
        'surfaces',
        `Surface contract for ${surface} is required.`,
      );
    }
  }
  for (const surface of manifest.surfaces) {
    if (!surface.degradedUatCaseId) {
      pushIssue(
        issues,
        'manifest.surface.degraded_uat',
        `surfaces.${surface.surface}.degradedUatCaseId`,
        'Each surface contract must link to a degraded UAT case.',
      );
    }
    if (surface.degradedUatCaseId && !uatCaseIds.has(surface.degradedUatCaseId)) {
      pushIssue(
        issues,
        'manifest.surface.degraded_uat_missing',
        `surfaces.${surface.surface}.degradedUatCaseId`,
        `Surface degraded UAT case ${surface.degradedUatCaseId} is not declared in uatCases.`,
      );
    }
    if (!surface.required && !surface.fallbackSurface) {
      pushIssue(
        issues,
        'manifest.surface.fallback',
        `surfaces.${surface.surface}.fallbackSurface`,
        'Optional or unavailable surfaces must declare a fallback surface.',
      );
    }
    if (!surface.required && !surface.fallbackAction) {
      pushIssue(
        issues,
        'manifest.surface.fallback_action',
        `surfaces.${surface.surface}.fallbackAction`,
        'Optional or unavailable surfaces must declare a fallback action.',
      );
    }
    if (surface.fallbackSurface && !surfaces.has(surface.fallbackSurface)) {
      pushIssue(
        issues,
        'manifest.surface.fallback_surface_missing',
        `surfaces.${surface.surface}.fallbackSurface`,
        `Fallback surface ${surface.fallbackSurface} must also have a surface contract.`,
      );
    }
  }

  const responsibilityContracts = new Map(
    manifest.responsibilities.map((responsibility) => [
      responsibility.responsibility,
      responsibility,
    ]),
  );
  for (const step of manifest.definition.steps) {
    const contract = responsibilityContracts.get(step.responsibility);
    if (!contract) {
      pushIssue(
        issues,
        'manifest.responsibility.missing',
        `steps.${step.id}.responsibility`,
        `Responsibility ${step.responsibility} is used by a step but missing from the manifest.`,
      );
    } else {
      if (!hasItems(contract.allowedRoles)) {
        pushIssue(
          issues,
          'manifest.responsibility.allowed_roles',
          `responsibilities.${contract.responsibility}.allowedRoles`,
          'Allowed completer roles must be explicit.',
        );
      }
      if (!hasItems(contract.fallbackCascade)) {
        pushIssue(
          issues,
          'manifest.responsibility.fallback_cascade',
          `responsibilities.${contract.responsibility}.fallbackCascade`,
          'Fallback cascade must be explicit for providers without every role.',
        );
      }
      if (step.professionalScope && contract.noQualifiedActorBehavior !== 'fail_closed') {
        pushIssue(
          issues,
          'manifest.responsibility.professional_scope',
          `responsibilities.${contract.responsibility}.noQualifiedActorBehavior`,
          'Professional-scope steps must fail closed when no qualified actor exists.',
        );
      }
    }

    if (
      step.fallback?.responsibility &&
      !responsibilityContracts.has(step.fallback.responsibility)
    ) {
      pushIssue(
        issues,
        'manifest.responsibility.fallback_missing',
        `steps.${step.id}.fallback.responsibility`,
        `Fallback responsibility ${step.fallback.responsibility} is missing from the manifest.`,
      );
    }
  }

  const stepIds = new Set(manifest.definition.steps.map((step) => step.id));
  const taskStepIds = new Set(manifest.tasks.map((task) => task.stepId));
  for (const step of manifest.definition.steps) {
    if (!taskStepIds.has(step.id)) {
      pushIssue(
        issues,
        'manifest.task.missing',
        `tasks.${step.id}`,
        `Step ${step.id} must have a task/notification/evidence manifest entry.`,
      );
    }
  }
  for (const task of manifest.tasks) {
    if (!stepIds.has(task.stepId)) {
      pushIssue(
        issues,
        'manifest.task.step_missing',
        `tasks.${task.stepId}`,
        `Task manifest entry references unknown step ${task.stepId}.`,
      );
    }
  }

  for (const notification of manifest.notifications) {
    if (notification.stepId && !stepIds.has(notification.stepId)) {
      pushIssue(
        issues,
        'manifest.notification.step_missing',
        `notifications.${notification.id}.stepId`,
        `Notification references unknown step ${notification.stepId}.`,
      );
    }
    if (notification.fallbackStepId && !stepIds.has(notification.fallbackStepId)) {
      pushIssue(
        issues,
        'manifest.notification.fallback_step_missing',
        `notifications.${notification.id}.fallbackStepId`,
        `Notification fallback references unknown step ${notification.fallbackStepId}.`,
      );
    }
    if (!responsibilityContracts.has(notification.recipientResponsibility)) {
      pushIssue(
        issues,
        'manifest.notification.responsibility_missing',
        `notifications.${notification.id}.recipientResponsibility`,
        `Notification recipient responsibility ${notification.recipientResponsibility} is missing from the manifest.`,
      );
    }
    if (notification.acknowledgementCompletesWorkflow !== false) {
      pushIssue(
        issues,
        'manifest.notification.ack_completion',
        `notifications.${notification.id}.acknowledgementCompletesWorkflow`,
        'Notification acknowledgement cannot be workflow completion.',
      );
    }
  }
  for (const approval of manifest.approvals) {
    if (!stepIds.has(approval.stepId)) {
      pushIssue(
        issues,
        'manifest.approval.step_missing',
        `approvals.${approval.id}.stepId`,
        `Approval references unknown step ${approval.stepId}.`,
      );
    }
    if (!responsibilityContracts.has(approval.responsibility)) {
      pushIssue(
        issues,
        'manifest.approval.responsibility_missing',
        `approvals.${approval.id}.responsibility`,
        `Approval responsibility ${approval.responsibility} is missing from the manifest.`,
      );
    }
  }
  for (const acknowledgement of manifest.acknowledgements) {
    if (!stepIds.has(acknowledgement.stepId)) {
      pushIssue(
        issues,
        'manifest.acknowledgement.step_missing',
        `acknowledgements.${acknowledgement.id}.stepId`,
        `Acknowledgement references unknown step ${acknowledgement.stepId}.`,
      );
    }
    if (!responsibilityContracts.has(acknowledgement.responsibility)) {
      pushIssue(
        issues,
        'manifest.acknowledgement.responsibility_missing',
        `acknowledgements.${acknowledgement.id}.responsibility`,
        `Acknowledgement responsibility ${acknowledgement.responsibility} is missing from the manifest.`,
      );
    }
    if (acknowledgement.completesWorkflow !== false) {
      pushIssue(
        issues,
        'manifest.acknowledgement.completion',
        `acknowledgements.${acknowledgement.id}.completesWorkflow`,
        'Acknowledgement records must not complete the workflow action.',
      );
    }
  }
  for (const document of manifest.documents) {
    if (document.stepId && !stepIds.has(document.stepId)) {
      pushIssue(
        issues,
        'manifest.document.step_missing',
        `documents.${document.id}.stepId`,
        `Document references unknown step ${document.stepId}.`,
      );
    }
  }
  for (const timer of manifest.timers) {
    if (!stepIds.has(timer.stepId)) {
      pushIssue(
        issues,
        'manifest.timer.step_missing',
        `timers.${timer.id}.stepId`,
        `Timer references unknown step ${timer.stepId}.`,
      );
    }
    if (
      timer.escalationResponsibility &&
      !responsibilityContracts.has(timer.escalationResponsibility)
    ) {
      pushIssue(
        issues,
        'manifest.timer.responsibility_missing',
        `timers.${timer.id}.escalationResponsibility`,
        `Timer escalation responsibility ${timer.escalationResponsibility} is missing from the manifest.`,
      );
    }
  }

  const evidenceByStepId = new Map(
    manifest.evidence.map((evidence) => [evidence.stepId, evidence]),
  );
  for (const evidence of manifest.evidence) {
    if (!stepIds.has(evidence.stepId)) {
      pushIssue(
        issues,
        'manifest.evidence.step_missing',
        `evidence.${evidence.stepId}`,
        `Evidence references unknown step ${evidence.stepId}.`,
      );
    }
  }
  for (const step of manifest.definition.steps) {
    if (!step.legalCritical) continue;
    const evidence = evidenceByStepId.get(step.id);
    if (!evidence?.required) {
      pushIssue(
        issues,
        'manifest.evidence.required',
        `evidence.${step.id}`,
        `Legal-critical step ${step.id} requires explicit evidence linkage.`,
      );
      continue;
    }
    for (const linkedRecord of ['source_record', 'workflow_task', 'workflow_instance']) {
      if (!evidence.linkedRecords.includes(linkedRecord)) {
        pushIssue(
          issues,
          'manifest.evidence.linkage',
          `evidence.${step.id}.linkedRecords`,
          `Evidence for ${step.id} must link ${linkedRecord}.`,
        );
      }
    }
  }

  if (!hasItems(manifest.providerConfiguration.mandatory)) {
    pushIssue(
      issues,
      'manifest.provider_config.mandatory',
      'providerConfiguration.mandatory',
      'Mandatory provider configuration boundary must be explicit.',
    );
  }
  if (!manifest.providerConfiguration.stronglyRecommended) {
    pushIssue(
      issues,
      'manifest.provider_config.strongly_recommended',
      'providerConfiguration.stronglyRecommended',
      'Strongly recommended provider configuration boundary must be present.',
    );
  }
  if (!manifest.providerConfiguration.optional) {
    pushIssue(
      issues,
      'manifest.provider_config.optional',
      'providerConfiguration.optional',
      'Optional provider configuration boundary must be present.',
    );
  }

  for (const [path, value] of Object.entries(manifest.idempotency)) {
    if (!value) {
      pushIssue(
        issues,
        'manifest.idempotency.required',
        `idempotency.${path}`,
        'Every idempotency and duplicate guard field must be explicit.',
      );
    }
  }

  if (!hasItems(manifest.legalSourceNotes)) {
    pushIssue(
      issues,
      'manifest.legal_sources.required',
      'legalSourceNotes',
      'Legal source notes are required, even when no rates/caps/deadlines are encoded.',
    );
  }
  if (!manifest.legalSourceNotes.some((note) => /no rates|no numeric/i.test(note.note))) {
    pushIssue(
      issues,
      'manifest.legal_sources.no_rates_note',
      'legalSourceNotes',
      'Manifest must state whether rates, caps, deadlines, or pay treatment are encoded.',
    );
  }

  if (!manifest.uatCases.some((uatCase) => !uatCase.degraded)) {
    pushIssue(
      issues,
      'manifest.uat.normal_path',
      'uatCases',
      'At least one normal-path UAT case is required.',
    );
  }
  if (!manifest.uatCases.some((uatCase) => uatCase.degraded)) {
    pushIssue(
      issues,
      'manifest.uat.degraded_path',
      'uatCases',
      'At least one degraded module/surface UAT case is required.',
    );
  }

  if (manifest.allocationPolicy?.biddingAllowed) {
    pushIssue(
      issues,
      'manifest.allocation.bidding',
      'allocationPolicy.biddingAllowed',
      'Bidding is not part of the current workflow allocation model.',
    );
  }

  return issues;
}

const SAH_REASSESSMENT_CARE_CONTEXT_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'sah.reassessment-care-context.v1',
  title: 'SAH reassessment to care-context follow-through',
  program: 'SAH',
  triggerEvent: 'sah.reassessment_completed',
  supportedOrgTypes: ['HOME_CARE', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/home-care/assessments', criticality: 'mandatory' },
    { path: '/home-care/service-delivery', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'care-context-evidence',
      label: 'Record care-context change evidence',
      actionType: 'record_evidence',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'CARE_CONTEXT_CHANGED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'worker-acknowledgement',
      label: 'Affected worker acknowledgement',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'scheduledWorkersAffected',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'care_coordination',
        reason:
          'care-worker app unavailable; coordinator must brief affected workers and record evidence',
      },
      evidenceType: 'CARE_CONTEXT_WORKER_ACKNOWLEDGED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'clinical-safety-review',
      label: 'Clinical safety review',
      actionType: 'create_task',
      responsibility: 'clinical_review',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'safetyCriticalCareContextChange',
      professionalScope: true,
      evidenceType: 'CLINICAL_SAFETY_REVIEW_REQUIRED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'care-manager-review',
      label: 'Care-manager follow-through task',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'CARE_MANAGER_REVIEW_REQUIRED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'client-visibility',
      label: 'Client and representative visibility',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.care.view',
      preferredSurfaces: ['client-portal'],
      condition: 'clientVisibleChange',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason: 'client portal unavailable; provider must issue/update communication manually',
      },
      evidenceType: 'CLIENT_VISIBILITY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
  ],
} as const;

const CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP = {
  activationOwner: 'notification_events_dispatcher',
  taskCreation: 'create_workflow_task_controlled',
  completion: 'complete_workflow_task_controlled',
  directWorkflowTableMutationAllowed: false,
  notificationAcknowledgementCompletesWorkflow: false,
} as const satisfies WorkflowManifestRuntimeOwnership;

export const SAH_REASSESSMENT_CARE_CONTEXT_MANIFEST = {
  id: SAH_REASSESSMENT_CARE_CONTEXT_WORKFLOW_DEFINITION.id,
  version: 1,
  title: SAH_REASSESSMENT_CARE_CONTEXT_WORKFLOW_DEFINITION.title,
  definition: SAH_REASSESSMENT_CARE_CONTEXT_WORKFLOW_DEFINITION,
  trigger: {
    eventName: SAH_REASSESSMENT_CARE_CONTEXT_WORKFLOW_DEFINITION.triggerEvent,
    schemaVersion: 1,
    sourceTable: 'home_assessments',
    sourceResourceType: 'home_assessment',
    requiredFields: ['tenant_id', 'client_id', 'assessment_id', 'source_surface'],
    idempotencyKey: 'tenant_id:home_assessment_id:updated_at:care_context_hash',
    activationOwner: 'notification_events_dispatcher',
  },
  runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
  programApplicability: {
    fundingPrograms: ['SAH'],
    orgTypes: ['HOME_CARE', 'MIXED'],
  },
  workflowBlock: {
    id: 'sah_care_context_follow_through',
    tier: 'mandatory',
  },
  requiredModules: SAH_REASSESSMENT_CARE_CONTEXT_WORKFLOW_DEFINITION.moduleRequirements,
  surfaces: [
    {
      surface: 'web',
      capabilityId: 'client.care.workspace',
      required: true,
      degradedUatCaseId: 'sah-reassessment-web-only',
    },
    {
      surface: 'care-worker-app',
      capabilityId: 'frontline.shift.execute',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-reassessment-mobile-disabled',
    },
    {
      surface: 'client-portal',
      capabilityId: 'portal.care.view',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-reassessment-portal-disabled',
    },
  ],
  responsibilities: [
    {
      responsibility: 'care_coordination',
      allowedRoles: RESPONSIBILITY_RULES.care_coordination.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.care_coordination.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'frontline_assigned_worker',
      allowedRoles: RESPONSIBILITY_RULES.frontline_assigned_worker.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.frontline_assigned_worker.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
    {
      responsibility: 'clinical_review',
      allowedRoles: RESPONSIBILITY_RULES.clinical_review.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.clinical_review.cascade,
      noQualifiedActorBehavior: 'fail_closed',
    },
    {
      responsibility: 'manual_communication',
      allowedRoles: RESPONSIBILITY_RULES.manual_communication.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.manual_communication.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
  ],
  tasks: [
    {
      stepId: 'care-context-evidence',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'CARE_CONTEXT_CHANGED',
    },
    {
      stepId: 'worker-acknowledgement',
      category: 'acknowledgement',
      createsWorkflowTask: true,
      requiredEvidenceType: 'CARE_CONTEXT_WORKER_ACKNOWLEDGED',
    },
    {
      stepId: 'clinical-safety-review',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'CLINICAL_SAFETY_REVIEW_REQUIRED',
    },
    {
      stepId: 'care-manager-review',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'CARE_MANAGER_REVIEW_REQUIRED',
    },
    {
      stepId: 'client-visibility',
      category: 'notification',
      createsWorkflowTask: true,
      requiredEvidenceType: 'CLIENT_VISIBILITY_OR_MANUAL_COMMUNICATION',
    },
  ],
  notifications: [
    {
      id: 'affected-worker-care-context-notice',
      stepId: 'worker-acknowledgement',
      recipientResponsibility: 'frontline_assigned_worker',
      surface: 'care-worker-app',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'worker-acknowledgement',
    },
    {
      id: 'client-care-context-visibility',
      stepId: 'client-visibility',
      recipientResponsibility: 'manual_communication',
      surface: 'client-portal',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'client-visibility',
    },
  ],
  approvals: [],
  acknowledgements: [
    {
      id: 'worker-care-context-acknowledgement',
      stepId: 'worker-acknowledgement',
      responsibility: 'frontline_assigned_worker',
      completesWorkflow: false,
      evidenceType: 'CARE_CONTEXT_WORKER_ACKNOWLEDGED',
    },
  ],
  documents: [
    {
      id: 'care-plan-update-or-manual-communication-record',
      stepId: 'client-visibility',
      required: false,
      documentType: 'care_plan_or_manual_communication',
      fallbackEvidenceType: 'CLIENT_VISIBILITY_OR_MANUAL_COMMUNICATION',
    },
  ],
  timers: [
    {
      id: 'worker-acknowledgement-due',
      stepId: 'worker-acknowledgement',
      dueDaysFromStart: 1,
      dueDateType: 'HARD',
      escalationResponsibility: 'care_coordination',
    },
    {
      id: 'clinical-safety-review-due',
      stepId: 'clinical-safety-review',
      dueDaysFromStart: 1,
      dueDateType: 'HARD',
      escalationResponsibility: 'clinical_review',
    },
    {
      id: 'client-visibility-due',
      stepId: 'client-visibility',
      dueDaysFromStart: 3,
      dueDateType: 'SOFT',
      escalationResponsibility: 'manual_communication',
    },
  ],
  evidence: [
    {
      stepId: 'care-context-evidence',
      evidenceType: 'CARE_CONTEXT_CHANGED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_plan'],
    },
    {
      stepId: 'worker-acknowledgement',
      evidenceType: 'CARE_CONTEXT_WORKER_ACKNOWLEDGED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_plan'],
    },
    {
      stepId: 'clinical-safety-review',
      evidenceType: 'CLINICAL_SAFETY_REVIEW_REQUIRED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_plan'],
    },
    {
      stepId: 'care-manager-review',
      evidenceType: 'CARE_MANAGER_REVIEW_REQUIRED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_plan'],
    },
    {
      stepId: 'client-visibility',
      evidenceType: 'CLIENT_VISIBILITY_OR_MANUAL_COMMUNICATION',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_plan'],
    },
  ],
  providerConfiguration: {
    mandatory: [
      {
        key: 'enabledModules.clients',
        description:
          'Client workspace must be available for the source assessment and care context.',
      },
      {
        key: 'enabledModules.carePlans',
        description: 'Care plan workspace must be available for follow-through and evidence.',
      },
      {
        key: 'responsibility.careCoordination',
        description: 'Provider must have an accountable care-coordination fallback.',
      },
    ],
    stronglyRecommended: [
      {
        key: 'surface.careWorkerApp',
        description: 'Worker acknowledgement should be available in the care-worker app.',
      },
      {
        key: 'surface.clientPortal',
        description:
          'Client/family visibility should be available through the portal where enabled.',
      },
    ],
    optional: [
      {
        key: 'clinical.safetyReviewEscalation',
        description:
          'Providers can configure additional escalation for safety-critical review tasks.',
      },
    ],
  },
  idempotency: {
    sourceEventKey: 'home_assessment_care_context_event_id',
    workflowInstanceGuard: 'tenant_id:workflow_definition_id:source_resource_id',
    runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
    generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
    notificationGuard: 'notification_events.idempotency_key',
    retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
  },
  legalSourceNotes: [
    {
      scope: 'SAH care-context follow-through',
      source:
        'Aged care primary-law and provider-record obligations must be verified before adding rates, caps, deadlines, or fee logic.',
      note: 'No numeric rates, caps, deadlines, fees, or pay treatment are encoded in this manifest.',
    },
  ],
  uatCases: [
    {
      id: 'sah-reassessment-full-surfaces',
      title: 'SAH reassessment routes through web, care-worker app, and client portal',
      path: 'Complete reassessment with worker-affecting, safety-critical, client-visible changes.',
      expected:
        'Compiler returns ready; tasks/evidence/portal projection are generated through controlled RPCs.',
      degraded: false,
    },
    {
      id: 'sah-reassessment-mobile-disabled',
      title: 'SAH reassessment mobile fallback',
      path: 'Disable care-worker app surface and complete worker-affecting reassessment.',
      expected: 'Worker acknowledgement compiles to web manual-evidence fallback.',
      degraded: true,
    },
    {
      id: 'sah-reassessment-portal-disabled',
      title: 'SAH reassessment portal fallback',
      path: 'Disable client portal and complete client-visible reassessment.',
      expected: 'Client visibility compiles to web manual-communication evidence.',
      degraded: true,
    },
    {
      id: 'sah-reassessment-web-only',
      title: 'SAH reassessment web-only provider',
      path: 'Run with web only and no optional surfaces.',
      expected: 'Mandatory actions remain web-routed and degraded actions require evidence.',
      degraded: true,
    },
  ],
  allocationPolicy: {
    strategy: 'not_applicable',
    biddingAllowed: false,
    managerConfirmation: 'not_applicable',
  },
  status: 'runtime_ready',
} as const satisfies WorkflowManifest;

export const SAH_REASSESSMENT_CARE_CONTEXT_WORKFLOW =
  SAH_REASSESSMENT_CARE_CONTEXT_MANIFEST.definition;

const SAH_VACANCY_FIRST_ACCEPTANCE_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'sah.vacancy-first-acceptance.v1',
  title: 'SAH vacancy first-acceptance allocation',
  program: 'SAH',
  triggerEvent: 'sah.roster_shift_vacant',
  supportedOrgTypes: ['HOME_CARE', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/roster', criticality: 'mandatory' },
    { path: '/home-care/service-delivery', criticality: 'mandatory' },
    { path: '/home-care/statements', criticality: 'strongly_recommended' },
    { path: '/finance/claims', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'eligible-worker-invitation',
      label: 'Invite eligible workers for vacant shift',
      actionType: 'notify',
      responsibility: 'workforce_roster',
      capabilityId: 'roster.manage',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SHIFT_OFFER_INVITED',
      relatedResourceType: 'roster_shift',
      legalCritical: true,
    },
    {
      id: 'first-acceptance-recorded',
      label: 'Record first worker acceptance',
      actionType: 'record_evidence',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'always',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'workforce_roster',
        reason: 'care-worker app unavailable; roster owner must record worker acceptance evidence',
      },
      evidenceType: 'SHIFT_OFFER_FIRST_ACCEPTANCE',
      relatedResourceType: 'shift_offer',
      legalCritical: true,
    },
    {
      id: 'manager-confirmation',
      label: 'Manager confirmation after first acceptance',
      actionType: 'create_task',
      responsibility: 'workforce_roster',
      capabilityId: 'roster.manage',
      preferredSurfaces: ['web'],
      condition: 'managerConfirmationConfigured',
      evidenceType: 'SHIFT_ALLOCATION_MANAGER_CONFIRMED',
      relatedResourceType: 'open_shift',
      legalCritical: true,
    },
    {
      id: 'client-call-task',
      label: 'Client call before roster confirmation',
      actionType: 'create_task',
      responsibility: 'manual_communication',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'clientCallConfigured',
      evidenceType: 'CLIENT_SHIFT_CHANGE_CONTACTED',
      relatedResourceType: 'roster_shift',
      legalCritical: true,
    },
    {
      id: 'roster-confirmation',
      label: 'Confirm roster allocation',
      actionType: 'record_evidence',
      responsibility: 'workforce_roster',
      capabilityId: 'roster.manage',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'ROSTER_ALLOCATION_CONFIRMED',
      relatedResourceType: 'roster_shift',
      legalCritical: true,
    },
    {
      id: 'visit-service-evidence',
      label: 'Record visit and service evidence',
      actionType: 'record_evidence',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'visitServiceEvidenceReady',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'care_coordination',
        reason: 'care-worker app unavailable; coordinator must verify visit/service evidence',
      },
      evidenceType: 'VISIT_SERVICE_EVIDENCE_RECORDED',
      relatedResourceType: 'services_delivered',
      legalCritical: true,
    },
    {
      id: 'statement-claim-readiness',
      label: 'Project delivered service into statement and claim readiness',
      actionType: 'surface_projection',
      responsibility: 'finance_review',
      capabilityId: 'sah.claim.prepare',
      preferredSurfaces: ['web'],
      condition: 'statementClaimReadinessRequired',
      evidenceType: 'SAH_STATEMENT_CLAIM_PREREQUISITE_READY',
      relatedResourceType: 'services_delivered',
      legalCritical: false,
    },
  ],
} as const;

export const SAH_VACANCY_FIRST_ACCEPTANCE_MANIFEST = {
  id: SAH_VACANCY_FIRST_ACCEPTANCE_WORKFLOW_DEFINITION.id,
  version: 1,
  title: SAH_VACANCY_FIRST_ACCEPTANCE_WORKFLOW_DEFINITION.title,
  definition: SAH_VACANCY_FIRST_ACCEPTANCE_WORKFLOW_DEFINITION,
  trigger: {
    eventName: SAH_VACANCY_FIRST_ACCEPTANCE_WORKFLOW_DEFINITION.triggerEvent,
    schemaVersion: 1,
    sourceTable: 'open_shifts',
    sourceResourceType: 'open_shift',
    requiredFields: ['tenant_id', 'open_shift_id', 'shift_id', 'broadcast_strategy'],
    idempotencyKey: 'tenant_id:open_shift_id:sah.roster_shift_vacant',
    activationOwner: 'notification_events_dispatcher',
  },
  runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
  programApplicability: {
    fundingPrograms: ['SAH', 'PRIVATE'],
    orgTypes: ['HOME_CARE', 'MIXED'],
  },
  workflowBlock: {
    id: 'sah_vacancy_allocation',
    tier: 'mandatory',
  },
  requiredModules: SAH_VACANCY_FIRST_ACCEPTANCE_WORKFLOW_DEFINITION.moduleRequirements,
  surfaces: [
    {
      surface: 'web',
      capabilityId: 'roster.manage',
      required: true,
      degradedUatCaseId: 'sah-vacancy-web-only',
    },
    {
      surface: 'care-worker-app',
      capabilityId: 'frontline.shift.execute',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-vacancy-mobile-disabled',
    },
    {
      surface: 'client-portal',
      capabilityId: 'portal.care.view',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-vacancy-client-call-manual',
    },
  ],
  responsibilities: [
    {
      responsibility: 'workforce_roster',
      allowedRoles: RESPONSIBILITY_RULES.workforce_roster.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.workforce_roster.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'frontline_assigned_worker',
      allowedRoles: RESPONSIBILITY_RULES.frontline_assigned_worker.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.frontline_assigned_worker.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
    {
      responsibility: 'manual_communication',
      allowedRoles: RESPONSIBILITY_RULES.manual_communication.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.manual_communication.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
    {
      responsibility: 'care_coordination',
      allowedRoles: RESPONSIBILITY_RULES.care_coordination.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.care_coordination.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'finance_review',
      allowedRoles: RESPONSIBILITY_RULES.finance_review.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.finance_review.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
  ],
  tasks: [
    {
      stepId: 'eligible-worker-invitation',
      category: 'notification',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SHIFT_OFFER_INVITED',
    },
    {
      stepId: 'first-acceptance-recorded',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SHIFT_OFFER_FIRST_ACCEPTANCE',
    },
    {
      stepId: 'manager-confirmation',
      category: 'approval',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SHIFT_ALLOCATION_MANAGER_CONFIRMED',
    },
    {
      stepId: 'client-call-task',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'CLIENT_SHIFT_CHANGE_CONTACTED',
    },
    {
      stepId: 'roster-confirmation',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'ROSTER_ALLOCATION_CONFIRMED',
    },
    {
      stepId: 'visit-service-evidence',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'VISIT_SERVICE_EVIDENCE_RECORDED',
    },
    {
      stepId: 'statement-claim-readiness',
      category: 'evidence',
      createsWorkflowTask: false,
      requiredEvidenceType: 'SAH_STATEMENT_CLAIM_PREREQUISITE_READY',
    },
  ],
  notifications: [
    {
      id: 'eligible-worker-shift-offer',
      stepId: 'eligible-worker-invitation',
      recipientResponsibility: 'frontline_assigned_worker',
      surface: 'care-worker-app',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'eligible-worker-invitation',
    },
    {
      id: 'client-call-visibility',
      stepId: 'client-call-task',
      recipientResponsibility: 'manual_communication',
      surface: 'client-portal',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'client-call-task',
    },
  ],
  approvals: [
    {
      id: 'manager-confirmation-after-acceptance',
      stepId: 'manager-confirmation',
      responsibility: 'workforce_roster',
      providerConfigured: true,
    },
  ],
  acknowledgements: [
    {
      id: 'worker-offer-acceptance',
      stepId: 'first-acceptance-recorded',
      responsibility: 'frontline_assigned_worker',
      completesWorkflow: false,
      evidenceType: 'SHIFT_OFFER_FIRST_ACCEPTANCE',
    },
  ],
  documents: [
    {
      id: 'visit-service-evidence-document',
      stepId: 'visit-service-evidence',
      required: false,
      documentType: 'visit_note_or_service_delivery_record',
      fallbackEvidenceType: 'VISIT_SERVICE_EVIDENCE_RECORDED',
    },
    {
      id: 'statement-claim-readiness-record',
      stepId: 'statement-claim-readiness',
      required: false,
      documentType: 'statement_claim_readiness_evidence',
      fallbackEvidenceType: 'SAH_STATEMENT_CLAIM_PREREQUISITE_READY',
    },
  ],
  timers: [
    {
      id: 'worker-offer-expiry',
      stepId: 'eligible-worker-invitation',
      dueDaysFromStart: 1,
      dueDateType: 'HARD',
      escalationResponsibility: 'workforce_roster',
    },
    {
      id: 'manager-confirmation-due',
      stepId: 'manager-confirmation',
      dueDaysFromStart: 1,
      dueDateType: 'HARD',
      escalationResponsibility: 'workforce_roster',
    },
  ],
  evidence: [
    {
      stepId: 'eligible-worker-invitation',
      evidenceType: 'SHIFT_OFFER_INVITED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'shift_offer'],
    },
    {
      stepId: 'first-acceptance-recorded',
      evidenceType: 'SHIFT_OFFER_FIRST_ACCEPTANCE',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'shift_offer'],
    },
    {
      stepId: 'manager-confirmation',
      evidenceType: 'SHIFT_ALLOCATION_MANAGER_CONFIRMED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'open_shift'],
    },
    {
      stepId: 'client-call-task',
      evidenceType: 'CLIENT_SHIFT_CHANGE_CONTACTED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'roster_shift'],
    },
    {
      stepId: 'roster-confirmation',
      evidenceType: 'ROSTER_ALLOCATION_CONFIRMED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'roster_shift'],
    },
    {
      stepId: 'visit-service-evidence',
      evidenceType: 'VISIT_SERVICE_EVIDENCE_RECORDED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'services_delivered',
        'sah_service_details',
      ],
    },
    {
      stepId: 'statement-claim-readiness',
      evidenceType: 'SAH_STATEMENT_CLAIM_PREREQUISITE_READY',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_instance',
        'services_delivered',
        'sah_monthly_statements',
        'sah_claim_batches',
      ],
    },
  ],
  providerConfiguration: {
    mandatory: [
      {
        key: 'enabledModules.roster',
        description: 'Roster module must be active for vacancy detection and allocation.',
      },
      {
        key: 'enabledModules.serviceDelivery',
        description: 'Service delivery module must be active for visit/service evidence.',
      },
      {
        key: 'responsibility.workforceRoster',
        description: 'Provider must have an accountable roster owner or tenant-admin fallback.',
      },
    ],
    stronglyRecommended: [
      {
        key: 'surface.careWorkerApp',
        description: 'Worker offer acceptance should happen in the care-worker app.',
      },
      {
        key: 'enabledModules.homeCareStatements',
        description: 'Statement readiness should be checked before claim preparation.',
      },
      {
        key: 'enabledModules.financeClaims',
        description: 'Claim readiness should be checked before finance handoff.',
      },
    ],
    optional: [
      {
        key: 'workflowConfiguration.vacancy.requireManagerConfirmationAfterAcceptance',
        description:
          'Exact tenant opt-in may require manager confirmation only after an accepted offer and canonical assignment.',
      },
      {
        key: 'workflowConfiguration.vacancy.requireClientCallAfterAcceptance',
        description:
          'Exact tenant opt-in may require a client call only after an accepted offer and canonical assignment.',
      },
    ],
  },
  idempotency: {
    sourceEventKey: 'open_shift_vacancy_event_id',
    workflowInstanceGuard: 'tenant_id:workflow_definition_id:open_shift_id',
    runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
    generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
    notificationGuard: 'tenant_id:shift_offer_id:staff_id',
    retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
  },
  legalSourceNotes: [
    {
      scope: 'SAH vacancy allocation and service evidence',
      source:
        'Aged care primary-law, award/pay, and funding source verification required before adding any rate, cap, deadline, or pay treatment logic.',
      note: 'No numeric rates, caps, deadlines, fees, or pay treatment are encoded in this manifest.',
    },
  ],
  uatCases: [
    {
      id: 'sah-vacancy-first-acceptance-happy-path',
      title: 'Vacancy fills by first acceptance and progresses to roster confirmation',
      path: 'Vacant shift emits event, eligible workers are invited, first acceptance wins, roster is confirmed, visit evidence is recorded, then statement/claim readiness is checked.',
      expected:
        'OPEN demand creates no workflow task; acceptance evidence is deterministic; exact provider opt-in may add post-acceptance manager/client work; delivered evidence projects into the existing finance queue.',
      degraded: false,
    },
    {
      id: 'sah-vacancy-mobile-disabled',
      title: 'Vacancy acceptance mobile fallback',
      path: 'Disable care-worker app surface before worker acceptance.',
      expected: 'First acceptance evidence compiles to roster-owner web manual-evidence fallback.',
      degraded: true,
    },
    {
      id: 'sah-vacancy-client-call-manual',
      title: 'Vacancy client call with portal unavailable',
      path: 'Enable the exact post-acceptance client-call setting while client portal is unavailable.',
      expected:
        'No task exists at OPEN; after accepted-offer and assignment evidence, the client call is a web manual-communication task with evidence.',
      degraded: true,
    },
    {
      id: 'sah-vacancy-web-only',
      title: 'Vacancy web-only provider',
      path: 'Run with web surface only and missing strongly recommended finance/statement modules.',
      expected:
        'Mandatory roster/service evidence remains blocked only by mandatory modules; finance/statement gaps compile as fallbacks.',
      degraded: true,
    },
  ],
  allocationPolicy: {
    strategy: 'first_acceptance_by_acceptance_time',
    biddingAllowed: false,
    managerConfirmation: 'provider_configured',
  },
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const SAH_VACANCY_FIRST_ACCEPTANCE_WORKFLOW =
  SAH_VACANCY_FIRST_ACCEPTANCE_MANIFEST.definition;

const SAH_MONTHLY_STATEMENT_ISSUE_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'sah.monthly-statement-issue.v1',
  title: 'SAH monthly statement issue and delivery evidence',
  program: 'SAH',
  triggerEvent: 'sah.monthly_statement_period_ready',
  supportedOrgTypes: ['HOME_CARE', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/home-care/service-delivery', criticality: 'mandatory' },
    { path: '/home-care/statements', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'statement-source-evidence-review',
      label: 'Review statement source evidence',
      actionType: 'record_evidence',
      responsibility: 'finance_review',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_STATEMENT_SOURCE_EVIDENCE_REVIEWED',
      relatedResourceType: 'sah_monthly_statements',
      legalCritical: true,
    },
    {
      id: 'statement-recipient-branch-review',
      label: 'Review statement recipient and branch continuity',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_STATEMENT_RECIPIENT_BRANCH_REVIEWED',
      relatedResourceType: 'sah_monthly_statements',
      legalCritical: true,
    },
    {
      id: 'statement-issue-or-send',
      label: 'Issue monthly statement',
      actionType: 'record_evidence',
      responsibility: 'finance_review',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_MONTHLY_STATEMENT_ISSUED',
      relatedResourceType: 'sah_monthly_statements',
      legalCritical: true,
    },
    {
      id: 'statement-client-visibility',
      label: 'Client statement visibility or manual delivery evidence',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.financial.view',
      preferredSurfaces: ['client-portal'],
      condition: 'always',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason:
          'client portal unavailable; provider must record email, post, phone, or manual statement delivery evidence',
      },
      evidenceType: 'SAH_STATEMENT_CLIENT_VISIBILITY_OR_MANUAL_DELIVERY',
      relatedResourceType: 'sah_monthly_statements',
      legalCritical: true,
    },
    {
      id: 'failed-statement-remediation',
      label: 'Remediate failed statement delivery',
      actionType: 'create_task',
      responsibility: 'manual_communication',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_STATEMENT_DELIVERY_REMEDIATED_OR_NOT_REQUIRED',
      relatedResourceType: 'sah_monthly_statements',
      legalCritical: true,
    },
  ],
} as const;

export const SAH_MONTHLY_STATEMENT_ISSUE_MANIFEST = {
  id: SAH_MONTHLY_STATEMENT_ISSUE_WORKFLOW_DEFINITION.id,
  version: 1,
  title: SAH_MONTHLY_STATEMENT_ISSUE_WORKFLOW_DEFINITION.title,
  definition: SAH_MONTHLY_STATEMENT_ISSUE_WORKFLOW_DEFINITION,
  trigger: {
    eventName: SAH_MONTHLY_STATEMENT_ISSUE_WORKFLOW_DEFINITION.triggerEvent,
    schemaVersion: 1,
    sourceTable: 'sah_monthly_statements',
    sourceResourceType: 'sah_monthly_statement',
    requiredFields: ['tenant_id', 'client_id', 'statement_id', 'period_year', 'period_month'],
    idempotencyKey:
      'tenant_id:client_id:period_year:period_month:sah.monthly_statement_period_ready',
    activationOwner: 'notification_events_dispatcher',
  },
  runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
  programApplicability: {
    fundingPrograms: ['SAH'],
    orgTypes: ['HOME_CARE', 'MIXED'],
  },
  workflowBlock: {
    id: 'sah_monthly_statement_issue',
    tier: 'mandatory',
  },
  requiredModules: SAH_MONTHLY_STATEMENT_ISSUE_WORKFLOW_DEFINITION.moduleRequirements,
  surfaces: [
    {
      surface: 'web',
      capabilityId: 'sah.statement.issue',
      required: true,
      degradedUatCaseId: 'sah-statement-web-only',
    },
    {
      surface: 'care-worker-app',
      capabilityId: 'frontline.shift.execute',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-statement-mobile-irrelevant',
    },
    {
      surface: 'client-portal',
      capabilityId: 'portal.financial.view',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-statement-portal-disabled',
    },
  ],
  responsibilities: [
    {
      responsibility: 'finance_review',
      allowedRoles: RESPONSIBILITY_RULES.finance_review.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.finance_review.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'manual_communication',
      allowedRoles: RESPONSIBILITY_RULES.manual_communication.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.manual_communication.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
  ],
  tasks: [
    {
      stepId: 'statement-source-evidence-review',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_STATEMENT_SOURCE_EVIDENCE_REVIEWED',
    },
    {
      stepId: 'statement-recipient-branch-review',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_STATEMENT_RECIPIENT_BRANCH_REVIEWED',
    },
    {
      stepId: 'statement-issue-or-send',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_MONTHLY_STATEMENT_ISSUED',
    },
    {
      stepId: 'statement-client-visibility',
      category: 'notification',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_STATEMENT_CLIENT_VISIBILITY_OR_MANUAL_DELIVERY',
    },
    {
      stepId: 'failed-statement-remediation',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_STATEMENT_DELIVERY_REMEDIATED_OR_NOT_REQUIRED',
    },
  ],
  notifications: [
    {
      id: 'statement-client-financial-visibility',
      stepId: 'statement-client-visibility',
      recipientResponsibility: 'manual_communication',
      surface: 'client-portal',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'statement-client-visibility',
    },
  ],
  approvals: [],
  acknowledgements: [],
  documents: [
    {
      id: 'statement-rendered-document-or-delivery-record',
      stepId: 'statement-issue-or-send',
      required: true,
      documentType: 'monthly_statement_payload_or_rendered_statement',
      fallbackEvidenceType: 'SAH_MONTHLY_STATEMENT_ISSUED',
    },
    {
      id: 'manual-statement-delivery-record',
      stepId: 'statement-client-visibility',
      required: false,
      documentType: 'statement_manual_delivery_evidence',
      fallbackEvidenceType: 'SAH_STATEMENT_CLIENT_VISIBILITY_OR_MANUAL_DELIVERY',
    },
  ],
  timers: [
    {
      id: 'statement-issue-review-due',
      stepId: 'statement-source-evidence-review',
      dueDaysFromStart: 1,
      dueDateType: 'SOFT',
      escalationResponsibility: 'finance_review',
    },
    {
      id: 'failed-statement-remediation-due',
      stepId: 'failed-statement-remediation',
      dueDaysFromStart: 2,
      dueDateType: 'SOFT',
      escalationResponsibility: 'manual_communication',
    },
  ],
  evidence: [
    {
      stepId: 'statement-source-evidence-review',
      evidenceType: 'SAH_STATEMENT_SOURCE_EVIDENCE_REVIEWED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'services_delivered',
        'sah_service_details',
        'sah_contribution_events',
        'sah_monthly_statements',
      ],
    },
    {
      stepId: 'statement-recipient-branch-review',
      evidenceType: 'SAH_STATEMENT_RECIPIENT_BRANCH_REVIEWED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'clients',
        'service_areas',
      ],
    },
    {
      stepId: 'statement-issue-or-send',
      evidenceType: 'SAH_MONTHLY_STATEMENT_ISSUED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'sah_monthly_statements',
      ],
    },
    {
      stepId: 'statement-client-visibility',
      evidenceType: 'SAH_STATEMENT_CLIENT_VISIBILITY_OR_MANUAL_DELIVERY',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'sah_monthly_statements',
      ],
    },
    {
      stepId: 'failed-statement-remediation',
      evidenceType: 'SAH_STATEMENT_DELIVERY_REMEDIATED_OR_NOT_REQUIRED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'sah_monthly_statements',
      ],
    },
  ],
  providerConfiguration: {
    mandatory: [
      {
        key: 'enabledModules.homeCareStatements',
        description:
          'SAH statement module must be active for statement issue and delivery evidence.',
      },
      {
        key: 'responsibility.financeReview',
        description: 'Provider must have a finance reviewer or tenant-admin fallback.',
      },
    ],
    stronglyRecommended: [
      {
        key: 'surface.clientPortal',
        description:
          'Portal financial visibility should be available where the provider uses the client/family portal.',
      },
    ],
    optional: [
      {
        key: 'statement.manualDeliveryEvidence',
        description:
          'Providers can record non-portal delivery evidence such as email, phone, post, or representative contact.',
      },
    ],
  },
  idempotency: {
    sourceEventKey: 'monthly_statement_period_ready_event_id',
    workflowInstanceGuard: 'tenant_id:workflow_definition_id:statement_id',
    runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
    generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
    notificationGuard: 'notification_events.idempotency_key',
    retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
  },
  legalSourceNotes: [
    {
      scope: 'SAH monthly statement issue and delivery evidence',
      source:
        'Aged care primary-law and provider-record obligations must be verified before adding statement deadlines or delivery rules.',
      note: 'No numeric rates, caps, deadlines, fees, or pay treatment are encoded in this manifest.',
    },
  ],
  uatCases: [
    {
      id: 'sah-statement-full-portal',
      title: 'Monthly statement issue with portal visibility',
      path: 'Generate monthly statement, review source evidence, issue statement, and project financial visibility to portal.',
      expected:
        'Manifest compiles ready; statement issue and portal/manual evidence stay controlled and auditable.',
      degraded: false,
    },
    {
      id: 'sah-statement-portal-disabled',
      title: 'Monthly statement issue without portal',
      path: 'Disable client portal and issue the monthly statement.',
      expected:
        'Portal visibility compiles to web manual delivery evidence, not a skipped statement.',
      degraded: true,
    },
    {
      id: 'sah-statement-mobile-irrelevant',
      title: 'Monthly statement issue with no care-worker app',
      path: 'Disable care-worker app for a finance-only statement workflow.',
      expected: 'Workflow remains web-owned because no finance step depends on mobile completion.',
      degraded: true,
    },
    {
      id: 'sah-statement-web-only',
      title: 'Monthly statement issue for web-only provider',
      path: 'Run with web surface only and tenant-admin finance fallback.',
      expected:
        'Statement issue stays available through web/manual evidence with role fallback where needed.',
      degraded: true,
    },
  ],
  allocationPolicy: {
    strategy: 'not_applicable',
    biddingAllowed: false,
    managerConfirmation: 'not_applicable',
  },
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const SAH_MONTHLY_STATEMENT_ISSUE_WORKFLOW = SAH_MONTHLY_STATEMENT_ISSUE_MANIFEST.definition;

const SAH_CLAIM_BATCH_LIFECYCLE_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'sah.claim-batch-lifecycle.v1',
  title: 'SAH claim batch lifecycle and reconciliation evidence',
  program: 'SAH',
  triggerEvent: 'sah.claim_batch_created',
  supportedOrgTypes: ['HOME_CARE', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/home-care/service-delivery', criticality: 'mandatory' },
    { path: '/home-care/claims', criticality: 'mandatory' },
    { path: '/home-care/statements', criticality: 'strongly_recommended' },
    { path: '/finance/claims', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'claim-source-evidence-validation',
      label: 'Validate claim source evidence',
      actionType: 'record_evidence',
      responsibility: 'finance_review',
      capabilityId: 'sah.claim.prepare',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_CLAIM_SOURCE_EVIDENCE_VALIDATED',
      relatedResourceType: 'sah_claim_batches',
      legalCritical: true,
    },
    {
      id: 'claim-ledger-sync-review',
      label: 'Review controlled claim ledger sync',
      actionType: 'record_evidence',
      responsibility: 'finance_review',
      capabilityId: 'sah.claim.prepare',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_CLAIM_BATCH_LEDGER_SYNCED',
      relatedResourceType: 'sah_claim_batches',
      legalCritical: true,
    },
    {
      id: 'claim-export-review',
      label: 'Review claim export before external submission',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'sah.claim.prepare',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_CLAIM_BATCH_EXPORTED',
      relatedResourceType: 'sah_claim_batches',
      legalCritical: true,
    },
    {
      id: 'claim-external-submission-record',
      label: 'Record external claim submission evidence',
      actionType: 'record_evidence',
      responsibility: 'finance_review',
      capabilityId: 'sah.claim.prepare',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_CLAIM_BATCH_SUBMITTED',
      relatedResourceType: 'sah_claim_batches',
      legalCritical: true,
    },
    {
      id: 'claim-payment-reconciliation',
      label: 'Record payment reconciliation or rejection evidence',
      actionType: 'record_evidence',
      responsibility: 'finance_review',
      capabilityId: 'sah.claim.prepare',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SAH_CLAIM_BATCH_RECONCILED_OR_REJECTED',
      relatedResourceType: 'sah_claim_batches',
      legalCritical: true,
    },
  ],
} as const;

export const SAH_CLAIM_BATCH_LIFECYCLE_MANIFEST = {
  id: SAH_CLAIM_BATCH_LIFECYCLE_WORKFLOW_DEFINITION.id,
  version: 1,
  title: SAH_CLAIM_BATCH_LIFECYCLE_WORKFLOW_DEFINITION.title,
  definition: SAH_CLAIM_BATCH_LIFECYCLE_WORKFLOW_DEFINITION,
  trigger: {
    eventName: SAH_CLAIM_BATCH_LIFECYCLE_WORKFLOW_DEFINITION.triggerEvent,
    schemaVersion: 1,
    sourceTable: 'sah_claim_batches',
    sourceResourceType: 'sah_claim_batch',
    requiredFields: ['tenant_id', 'batch_id', 'period_start', 'period_end', 'rows_count'],
    idempotencyKey: 'tenant_id:sah_claim_batch_id:sah.claim_batch_created',
    activationOwner: 'notification_events_dispatcher',
  },
  runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
  programApplicability: {
    fundingPrograms: ['SAH'],
    orgTypes: ['HOME_CARE', 'MIXED'],
  },
  workflowBlock: {
    id: 'sah_claim_batch_lifecycle',
    tier: 'mandatory',
  },
  requiredModules: SAH_CLAIM_BATCH_LIFECYCLE_WORKFLOW_DEFINITION.moduleRequirements,
  surfaces: [
    {
      surface: 'web',
      capabilityId: 'sah.claim.prepare',
      required: true,
      degradedUatCaseId: 'sah-claim-web-only',
    },
    {
      surface: 'care-worker-app',
      capabilityId: 'frontline.shift.execute',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-claim-mobile-disabled',
    },
    {
      surface: 'client-portal',
      capabilityId: 'portal.financial.view',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'sah-claim-portal-disabled',
    },
  ],
  responsibilities: [
    {
      responsibility: 'finance_review',
      allowedRoles: RESPONSIBILITY_RULES.finance_review.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.finance_review.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
  ],
  tasks: [
    {
      stepId: 'claim-source-evidence-validation',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_CLAIM_SOURCE_EVIDENCE_VALIDATED',
    },
    {
      stepId: 'claim-ledger-sync-review',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_CLAIM_BATCH_LEDGER_SYNCED',
    },
    {
      stepId: 'claim-export-review',
      category: 'approval',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_CLAIM_BATCH_EXPORTED',
    },
    {
      stepId: 'claim-external-submission-record',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_CLAIM_BATCH_SUBMITTED',
    },
    {
      stepId: 'claim-payment-reconciliation',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'SAH_CLAIM_BATCH_RECONCILED_OR_REJECTED',
    },
  ],
  notifications: [],
  approvals: [
    {
      id: 'finance-export-review-before-submission',
      stepId: 'claim-export-review',
      responsibility: 'finance_review',
      providerConfigured: true,
    },
  ],
  acknowledgements: [],
  documents: [
    {
      id: 'claim-export-csv-or-payload',
      stepId: 'claim-export-review',
      required: true,
      documentType: 'claim_batch_export_payload',
      fallbackEvidenceType: 'SAH_CLAIM_BATCH_EXPORTED',
    },
    {
      id: 'claim-submission-reference',
      stepId: 'claim-external-submission-record',
      required: true,
      documentType: 'external_claim_submission_reference',
      fallbackEvidenceType: 'SAH_CLAIM_BATCH_SUBMITTED',
    },
    {
      id: 'claim-payment-or-rejection-evidence',
      stepId: 'claim-payment-reconciliation',
      required: true,
      documentType: 'claim_payment_or_rejection_evidence',
      fallbackEvidenceType: 'SAH_CLAIM_BATCH_RECONCILED_OR_REJECTED',
    },
  ],
  timers: [
    {
      id: 'claim-export-review-due',
      stepId: 'claim-export-review',
      dueDaysFromStart: 1,
      dueDateType: 'SOFT',
      escalationResponsibility: 'finance_review',
    },
    {
      id: 'claim-reconciliation-follow-up-due',
      stepId: 'claim-payment-reconciliation',
      dueDaysFromStart: 7,
      dueDateType: 'SOFT',
      escalationResponsibility: 'finance_review',
    },
  ],
  evidence: [
    {
      stepId: 'claim-source-evidence-validation',
      evidenceType: 'SAH_CLAIM_SOURCE_EVIDENCE_VALIDATED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'services_delivered',
        'sah_service_details',
        'sah_contribution_events',
        'sah_claim_batches',
      ],
    },
    {
      stepId: 'claim-ledger-sync-review',
      evidenceType: 'SAH_CLAIM_BATCH_LEDGER_SYNCED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'claims',
        'sah_claim_batches',
      ],
    },
    {
      stepId: 'claim-export-review',
      evidenceType: 'SAH_CLAIM_BATCH_EXPORTED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'sah_claim_batches'],
    },
    {
      stepId: 'claim-external-submission-record',
      evidenceType: 'SAH_CLAIM_BATCH_SUBMITTED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'claims',
        'sah_claim_batches',
      ],
    },
    {
      stepId: 'claim-payment-reconciliation',
      evidenceType: 'SAH_CLAIM_BATCH_RECONCILED_OR_REJECTED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'claims',
        'services_delivered',
        'sah_claim_batches',
      ],
    },
  ],
  providerConfiguration: {
    mandatory: [
      {
        key: 'enabledModules.homeCareClaims',
        description:
          'SAH claim module must be active for claim batch preparation and reconciliation.',
      },
      {
        key: 'responsibility.financeReview',
        description: 'Provider must have a finance reviewer or tenant-admin fallback.',
      },
    ],
    stronglyRecommended: [
      {
        key: 'enabledModules.homeCareStatements',
        description: 'Statement issue evidence should be available before claim submission review.',
      },
      {
        key: 'enabledModules.financeClaims',
        description:
          'Full finance claims view should be enabled where the provider uses the broader finance module.',
      },
    ],
    optional: [
      {
        key: 'claimBatch.exportApproval',
        description:
          'Providers can require explicit finance approval before external claim submission.',
      },
    ],
  },
  idempotency: {
    sourceEventKey: 'sah_claim_batch_created_event_id',
    workflowInstanceGuard: 'tenant_id:workflow_definition_id:sah_claim_batch_id',
    runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
    generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
    notificationGuard: 'notification_events.idempotency_key',
    retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
  },
  legalSourceNotes: [
    {
      scope: 'SAH claim batch lifecycle and reconciliation evidence',
      source:
        'Services Australia, Department of Health and Aged Care, and primary aged-care funding sources must be verified before adding claim-format, rate, deadline, or submission rules.',
      note: 'No numeric rates, caps, deadlines, fees, or pay treatment are encoded in this manifest.',
    },
  ],
  uatCases: [
    {
      id: 'sah-claim-batch-full-finance',
      title: 'Claim batch lifecycle through controlled finance actions',
      path: 'Generate claim batch, validate source evidence, sync claim ledger, export, submit, and reconcile.',
      expected:
        'Manifest compiles ready; financial amounts stay in existing controlled RPCs and workflow only tracks action/evidence lifecycle.',
      degraded: false,
    },
    {
      id: 'sah-claim-web-only',
      title: 'Claim batch lifecycle for web-only provider',
      path: 'Run with only the web surface and no dedicated finance officer.',
      expected:
        'Finance review routes to tenant admin without moving amount calculation out of controlled RPCs.',
      degraded: true,
    },
    {
      id: 'sah-claim-mobile-disabled',
      title: 'Claim batch lifecycle without care-worker app',
      path: 'Disable care-worker app for a finance-only claim workflow.',
      expected: 'Workflow remains web-owned because no claim step depends on mobile completion.',
      degraded: true,
    },
    {
      id: 'sah-claim-portal-disabled',
      title: 'Claim batch lifecycle without portal',
      path: 'Disable client portal while preparing and submitting a claim batch.',
      expected:
        'Claim lifecycle remains web-owned; client visibility remains covered by the statement workflow.',
      degraded: true,
    },
  ],
  allocationPolicy: {
    strategy: 'not_applicable',
    biddingAllowed: false,
    managerConfirmation: 'not_applicable',
  },
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const SAH_CLAIM_BATCH_LIFECYCLE_WORKFLOW = SAH_CLAIM_BATCH_LIFECYCLE_MANIFEST.definition;

const RAC_ADMISSION_GOVERNANCE_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'rac.admission-governance.v1',
  title: 'RAC admission governance and family visibility',
  program: 'RAC',
  triggerEvent: 'rac.admission_started',
  supportedOrgTypes: ['RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/residential', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/assessments', criticality: 'strongly_recommended' },
    { path: '/residential/rooms', criticality: 'strongly_recommended' },
    { path: '/compliance/prudential', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'resident-profile-admission-record',
      label: 'Record resident profile and admission evidence',
      actionType: 'record_evidence',
      responsibility: 'care_coordination',
      capabilityId: 'client.create',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'RAC_RESIDENT_PROFILE_ADMISSION_RECORDED',
      relatedResourceType: 'clients',
      legalCritical: true,
    },
    {
      id: 'room-bed-allocation',
      label: 'Confirm room and bed allocation',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'RAC_ROOM_BED_ALLOCATION_CONFIRMED',
      relatedResourceType: 'rooms',
      legalCritical: true,
    },
    {
      id: 'fee-accommodation-review',
      label: 'Review fee and accommodation arrangement',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'prudential.access',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'RAC_FEE_ACCOMMODATION_REVIEWED',
      relatedResourceType: 'residential_accommodation_agreements',
      legalCritical: true,
    },
    {
      id: 'admission-documents-issued',
      label: 'Issue admission and accommodation documents',
      actionType: 'create_task',
      responsibility: 'manual_communication',
      capabilityId: 'portal.documents.view',
      preferredSurfaces: ['client-portal'],
      condition: 'always',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason:
          'client portal unavailable; provider must issue admission/accommodation documents manually and record evidence',
      },
      evidenceType: 'RAC_ADMISSION_DOCUMENTS_ISSUED',
      relatedResourceType: 'documents',
      legalCritical: true,
    },
    {
      id: 'resident-assessment-care-plan',
      label: 'Complete resident assessment and initial care-plan review',
      actionType: 'create_task',
      responsibility: 'clinical_review',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      professionalScope: true,
      evidenceType: 'RAC_INITIAL_ASSESSMENT_CARE_PLAN_REVIEWED',
      relatedResourceType: 'care_plans',
      legalCritical: true,
    },
    {
      id: 'family-visibility-or-manual-communication',
      label: 'Family visibility or manual communication evidence',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.care.view',
      preferredSurfaces: ['client-portal'],
      condition: 'always',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason:
          'client portal unavailable; provider must record resident/family admission communication evidence',
      },
      evidenceType: 'RAC_FAMILY_VISIBILITY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'clients',
      legalCritical: true,
    },
  ],
} as const;

export const RAC_ADMISSION_GOVERNANCE_MANIFEST = {
  id: RAC_ADMISSION_GOVERNANCE_WORKFLOW_DEFINITION.id,
  version: 1,
  title: RAC_ADMISSION_GOVERNANCE_WORKFLOW_DEFINITION.title,
  definition: RAC_ADMISSION_GOVERNANCE_WORKFLOW_DEFINITION,
  trigger: {
    eventName: RAC_ADMISSION_GOVERNANCE_WORKFLOW_DEFINITION.triggerEvent,
    schemaVersion: 1,
    sourceTable: 'clients',
    sourceResourceType: 'client',
    requiredFields: ['tenant_id', 'client_id', 'facility_id', 'admission_date', 'funding_program'],
    idempotencyKey: 'tenant_id:client_id:admission_date:rac.admission_started',
    activationOwner: 'notification_events_dispatcher',
  },
  runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
  programApplicability: {
    fundingPrograms: ['RAC'],
    orgTypes: ['RESIDENTIAL', 'MIXED'],
  },
  workflowBlock: {
    id: 'rac_admission_governance',
    tier: 'mandatory',
  },
  requiredModules: RAC_ADMISSION_GOVERNANCE_WORKFLOW_DEFINITION.moduleRequirements,
  surfaces: [
    {
      surface: 'web',
      capabilityId: 'client.care.workspace',
      required: true,
      degradedUatCaseId: 'rac-admission-web-only',
    },
    {
      surface: 'care-worker-app',
      capabilityId: 'frontline.shift.execute',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'rac-admission-mobile-disabled',
    },
    {
      surface: 'client-portal',
      capabilityId: 'portal.care.view',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'rac-admission-portal-disabled',
    },
  ],
  responsibilities: [
    {
      responsibility: 'care_coordination',
      allowedRoles: RESPONSIBILITY_RULES.care_coordination.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.care_coordination.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'finance_review',
      allowedRoles: RESPONSIBILITY_RULES.finance_review.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.finance_review.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'clinical_review',
      allowedRoles: RESPONSIBILITY_RULES.clinical_review.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.clinical_review.cascade,
      noQualifiedActorBehavior: 'fail_closed',
    },
    {
      responsibility: 'manual_communication',
      allowedRoles: RESPONSIBILITY_RULES.manual_communication.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.manual_communication.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
  ],
  tasks: [
    {
      stepId: 'resident-profile-admission-record',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_RESIDENT_PROFILE_ADMISSION_RECORDED',
    },
    {
      stepId: 'room-bed-allocation',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_ROOM_BED_ALLOCATION_CONFIRMED',
    },
    {
      stepId: 'fee-accommodation-review',
      category: 'approval',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_FEE_ACCOMMODATION_REVIEWED',
    },
    {
      stepId: 'admission-documents-issued',
      category: 'document',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_ADMISSION_DOCUMENTS_ISSUED',
    },
    {
      stepId: 'resident-assessment-care-plan',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_INITIAL_ASSESSMENT_CARE_PLAN_REVIEWED',
    },
    {
      stepId: 'family-visibility-or-manual-communication',
      category: 'notification',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_FAMILY_VISIBILITY_OR_MANUAL_COMMUNICATION',
    },
  ],
  notifications: [
    {
      id: 'rac-admission-family-visibility',
      stepId: 'family-visibility-or-manual-communication',
      recipientResponsibility: 'manual_communication',
      surface: 'client-portal',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'family-visibility-or-manual-communication',
    },
  ],
  approvals: [
    {
      id: 'rac-fee-accommodation-review',
      stepId: 'fee-accommodation-review',
      responsibility: 'finance_review',
      providerConfigured: true,
    },
  ],
  acknowledgements: [],
  documents: [
    {
      id: 'admission-and-accommodation-document-pack',
      stepId: 'admission-documents-issued',
      required: true,
      documentType: 'rac_admission_accommodation_document_pack',
      fallbackEvidenceType: 'RAC_ADMISSION_DOCUMENTS_ISSUED',
    },
  ],
  timers: [
    {
      id: 'rac-admission-document-pack-due',
      stepId: 'admission-documents-issued',
      dueDaysFromStart: 1,
      dueDateType: 'SOFT',
      escalationResponsibility: 'manual_communication',
    },
    {
      id: 'rac-initial-care-plan-review-due',
      stepId: 'resident-assessment-care-plan',
      dueDaysFromStart: 1,
      dueDateType: 'HARD',
      escalationResponsibility: 'clinical_review',
    },
  ],
  evidence: [
    {
      stepId: 'resident-profile-admission-record',
      evidenceType: 'RAC_RESIDENT_PROFILE_ADMISSION_RECORDED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'clients'],
    },
    {
      stepId: 'room-bed-allocation',
      evidenceType: 'RAC_ROOM_BED_ALLOCATION_CONFIRMED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'clients', 'rooms'],
    },
    {
      stepId: 'fee-accommodation-review',
      evidenceType: 'RAC_FEE_ACCOMMODATION_REVIEWED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'residential_accommodation_agreements',
        'accommodation_deposits',
        'prudential_resident_disclosures',
      ],
    },
    {
      stepId: 'admission-documents-issued',
      evidenceType: 'RAC_ADMISSION_DOCUMENTS_ISSUED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'documents'],
    },
    {
      stepId: 'resident-assessment-care-plan',
      evidenceType: 'RAC_INITIAL_ASSESSMENT_CARE_PLAN_REVIEWED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_plans'],
    },
    {
      stepId: 'family-visibility-or-manual-communication',
      evidenceType: 'RAC_FAMILY_VISIBILITY_OR_MANUAL_COMMUNICATION',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'clients',
        'documents',
      ],
    },
  ],
  providerConfiguration: {
    mandatory: [
      {
        key: 'enabledModules.residential',
        description:
          'Residential module must be active before RAC admission workflows can activate.',
      },
      {
        key: 'responsibility.clinicalReview',
        description:
          'Provider must have an RN/clinical lead for professional admission care-plan review.',
      },
    ],
    stronglyRecommended: [
      {
        key: 'enabledModules.residentialRooms',
        description: 'Room pricing and bed/room allocation should be available for admission.',
      },
      {
        key: 'enabledModules.prudential',
        description:
          'Prudential evidence should be available for accommodation-related admissions.',
      },
      {
        key: 'surface.clientPortal',
        description: 'Family visibility should use portal where the provider has enabled it.',
      },
    ],
    optional: [
      {
        key: 'admission.familyManualCommunication',
        description:
          'Manual family communication evidence is required when portal visibility is unavailable.',
      },
    ],
  },
  idempotency: {
    sourceEventKey: 'rac_admission_started_event_id',
    workflowInstanceGuard: 'tenant_id:workflow_definition_id:client_id:admission_date',
    runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
    generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
    notificationGuard: 'notification_events.idempotency_key',
    retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
  },
  legalSourceNotes: [
    {
      scope: 'RAC admission governance and family visibility',
      source:
        'Residential aged care primary-law, My Aged Care residential fees, accommodation, AN-ACC, and ACQSC sources must be verified before adding deadlines, rates, classifications, or fee rules.',
      note: 'No numeric rates, caps, deadlines, fees, or pay treatment are encoded in this manifest.',
    },
  ],
  uatCases: [
    {
      id: 'rac-admission-full-portal',
      title: 'RAC admission with portal and qualified clinical actor',
      path: 'Admit resident, confirm room, review fees/accommodation, issue documents, complete initial care-plan review, and surface family visibility.',
      expected:
        'Manifest compiles ready when residential, care-plan, clinical, finance, and portal capabilities are present.',
      degraded: false,
    },
    {
      id: 'rac-admission-portal-disabled',
      title: 'RAC admission without family portal',
      path: 'Disable portal and run admission workflow.',
      expected:
        'Admission documents and family visibility compile to web manual communication/evidence tasks.',
      degraded: true,
    },
    {
      id: 'rac-admission-mobile-disabled',
      title: 'RAC admission without care-worker app',
      path: 'Disable care-worker app for admission workflow.',
      expected:
        'Workflow remains web-owned because no admission governance step requires mobile completion.',
      degraded: true,
    },
    {
      id: 'rac-admission-web-only',
      title: 'RAC admission for web-only provider',
      path: 'Run with web only and no dedicated finance officer.',
      expected:
        'Finance review falls back to tenant admin, while clinical review still requires qualified clinical role.',
      degraded: true,
    },
  ],
  allocationPolicy: {
    strategy: 'not_applicable',
    biddingAllowed: false,
    managerConfirmation: 'not_applicable',
  },
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const RAC_ADMISSION_GOVERNANCE_WORKFLOW = RAC_ADMISSION_GOVERNANCE_MANIFEST.definition;

const RAC_DAILY_CARE_EXCEPTION_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'rac.daily-care-exception.v1',
  title: 'RAC daily care exception review and closure',
  program: 'RAC',
  triggerEvent: 'rac.residential_round_escalated',
  supportedOrgTypes: ['RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/residential', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/dashboards/don-clinical', criticality: 'strongly_recommended' },
    { path: '/compliance/incidents', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'round-exception-source-evidence',
      label: 'Record residential round exception evidence',
      actionType: 'record_evidence',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'always',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'care_coordination',
        reason:
          'care-worker app unavailable; coordinator or nurse must record round exception evidence manually',
      },
      evidenceType: 'RAC_ROUND_EXCEPTION_SOURCE_EVIDENCE',
      relatedResourceType: 'residential_round_assignments',
      legalCritical: true,
    },
    {
      id: 'nurse-clinical-review',
      label: 'Nurse clinical review of daily care exception',
      actionType: 'create_task',
      responsibility: 'clinical_review',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      professionalScope: true,
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_CLINICAL_REVIEWED',
      relatedResourceType: 'residential_round_assignments',
      legalCritical: true,
    },
    {
      id: 'care-plan-review-if-needed',
      label: 'Care-plan review after daily care exception',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_CARE_PLAN_REVIEWED',
      relatedResourceType: 'care_plans',
      legalCritical: true,
    },
    {
      id: 'family-notification-if-required',
      label: 'Family notification or manual communication evidence',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.care.view',
      preferredSurfaces: ['client-portal'],
      condition: 'clientVisibleChange',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason:
          'client portal unavailable; provider must record family/resident communication evidence manually',
      },
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_FAMILY_VISIBILITY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'residential_round_assignments',
      legalCritical: true,
    },
    {
      id: 'care-minute-funding-evidence',
      label: 'Review care-minute and funding evidence impact',
      actionType: 'create_task',
      responsibility: 'quality_governance',
      capabilityId: 'quality.gpms.access',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'RAC_CARE_MINUTE_FUNDING_EVIDENCE_REVIEWED',
      relatedResourceType: 'care_minute_targets',
      legalCritical: true,
    },
    {
      id: 'round-exception-closure',
      label: 'Close daily care exception with evidence',
      actionType: 'record_evidence',
      responsibility: 'clinical_review',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      professionalScope: true,
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_CLOSED',
      relatedResourceType: 'residential_round_assignments',
      legalCritical: true,
    },
  ],
} as const;

export const RAC_DAILY_CARE_EXCEPTION_MANIFEST = {
  id: RAC_DAILY_CARE_EXCEPTION_WORKFLOW_DEFINITION.id,
  version: 1,
  title: RAC_DAILY_CARE_EXCEPTION_WORKFLOW_DEFINITION.title,
  definition: RAC_DAILY_CARE_EXCEPTION_WORKFLOW_DEFINITION,
  trigger: {
    eventName: RAC_DAILY_CARE_EXCEPTION_WORKFLOW_DEFINITION.triggerEvent,
    schemaVersion: 1,
    sourceTable: 'residential_round_assignments',
    sourceResourceType: 'residential_round_assignment',
    requiredFields: ['tenant_id', 'client_id', 'assignment_id', 'round_type', 'status'],
    idempotencyKey:
      'tenant_id:residential_round_assignment_id:status:rac.residential_round_escalated',
    activationOwner: 'notification_events_dispatcher',
  },
  runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
  programApplicability: {
    fundingPrograms: ['RAC'],
    orgTypes: ['RESIDENTIAL', 'MIXED'],
  },
  workflowBlock: {
    id: 'rac_daily_care_exception',
    tier: 'mandatory',
  },
  requiredModules: RAC_DAILY_CARE_EXCEPTION_WORKFLOW_DEFINITION.moduleRequirements,
  surfaces: [
    {
      surface: 'web',
      capabilityId: 'client.care.workspace',
      required: true,
      degradedUatCaseId: 'rac-daily-care-web-only',
    },
    {
      surface: 'care-worker-app',
      capabilityId: 'frontline.shift.execute',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'rac-daily-care-mobile-disabled',
    },
    {
      surface: 'client-portal',
      capabilityId: 'portal.care.view',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'rac-daily-care-portal-disabled',
    },
  ],
  responsibilities: [
    {
      responsibility: 'frontline_assigned_worker',
      allowedRoles: RESPONSIBILITY_RULES.frontline_assigned_worker.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.frontline_assigned_worker.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
    {
      responsibility: 'care_coordination',
      allowedRoles: RESPONSIBILITY_RULES.care_coordination.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.care_coordination.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'clinical_review',
      allowedRoles: RESPONSIBILITY_RULES.clinical_review.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.clinical_review.cascade,
      noQualifiedActorBehavior: 'fail_closed',
    },
    {
      responsibility: 'manual_communication',
      allowedRoles: RESPONSIBILITY_RULES.manual_communication.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.manual_communication.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
    {
      responsibility: 'quality_governance',
      allowedRoles: RESPONSIBILITY_RULES.quality_governance.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.quality_governance.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
  ],
  tasks: [
    {
      stepId: 'round-exception-source-evidence',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_ROUND_EXCEPTION_SOURCE_EVIDENCE',
    },
    {
      stepId: 'nurse-clinical-review',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_DAILY_CARE_EXCEPTION_CLINICAL_REVIEWED',
    },
    {
      stepId: 'care-plan-review-if-needed',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_DAILY_CARE_EXCEPTION_CARE_PLAN_REVIEWED',
    },
    {
      stepId: 'family-notification-if-required',
      category: 'notification',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_DAILY_CARE_EXCEPTION_FAMILY_VISIBILITY_OR_MANUAL_COMMUNICATION',
    },
    {
      stepId: 'care-minute-funding-evidence',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_CARE_MINUTE_FUNDING_EVIDENCE_REVIEWED',
    },
    {
      stepId: 'round-exception-closure',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'RAC_DAILY_CARE_EXCEPTION_CLOSED',
    },
  ],
  notifications: [
    {
      id: 'rac-daily-care-family-visibility',
      stepId: 'family-notification-if-required',
      recipientResponsibility: 'manual_communication',
      surface: 'client-portal',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'family-notification-if-required',
    },
  ],
  approvals: [
    {
      id: 'rac-daily-care-clinical-review',
      stepId: 'nurse-clinical-review',
      responsibility: 'clinical_review',
      providerConfigured: false,
    },
  ],
  acknowledgements: [],
  documents: [
    {
      id: 'rac-round-exception-note-or-observation',
      stepId: 'round-exception-source-evidence',
      required: false,
      documentType: 'residential_round_exception_note_or_observation',
      fallbackEvidenceType: 'RAC_ROUND_EXCEPTION_SOURCE_EVIDENCE',
    },
  ],
  timers: [
    {
      id: 'rac-daily-care-clinical-review-due',
      stepId: 'nurse-clinical-review',
      dueDaysFromStart: 1,
      dueDateType: 'HARD',
      escalationResponsibility: 'clinical_review',
    },
    {
      id: 'rac-daily-care-family-visibility-due',
      stepId: 'family-notification-if-required',
      dueDaysFromStart: 2,
      dueDateType: 'SOFT',
      escalationResponsibility: 'manual_communication',
    },
  ],
  evidence: [
    {
      stepId: 'round-exception-source-evidence',
      evidenceType: 'RAC_ROUND_EXCEPTION_SOURCE_EVIDENCE',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'residential_round_assignments',
      ],
    },
    {
      stepId: 'nurse-clinical-review',
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_CLINICAL_REVIEWED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'residential_round_assignments',
      ],
    },
    {
      stepId: 'care-plan-review-if-needed',
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_CARE_PLAN_REVIEWED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_plans'],
    },
    {
      stepId: 'family-notification-if-required',
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_FAMILY_VISIBILITY_OR_MANUAL_COMMUNICATION',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'residential_round_assignments',
      ],
    },
    {
      stepId: 'care-minute-funding-evidence',
      evidenceType: 'RAC_CARE_MINUTE_FUNDING_EVIDENCE_REVIEWED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'care_minute_targets'],
    },
    {
      stepId: 'round-exception-closure',
      evidenceType: 'RAC_DAILY_CARE_EXCEPTION_CLOSED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'residential_round_assignments',
      ],
    },
  ],
  providerConfiguration: {
    mandatory: [
      {
        key: 'enabledModules.residential',
        description:
          'Residential module must be active before daily care exception workflows can activate.',
      },
      {
        key: 'responsibility.clinicalReview',
        description:
          'Provider must have an RN/clinical lead for professional exception review and closure.',
      },
    ],
    stronglyRecommended: [
      {
        key: 'surface.careWorkerApp',
        description:
          'Frontline exception evidence should be captured in the care-worker app where enabled.',
      },
      {
        key: 'enabledModules.donClinicalDashboard',
        description: 'Clinical leadership dashboard should surface exception review tasks.',
      },
      {
        key: 'enabledModules.incidents',
        description:
          'Incident module should be available where a care exception becomes reportable or corrective-action work.',
      },
    ],
    optional: [
      {
        key: 'exception.familyVisibility',
        description:
          'Providers can configure when family visibility is required for daily care exceptions.',
      },
    ],
  },
  idempotency: {
    sourceEventKey: 'rac_residential_round_escalated_event_id',
    workflowInstanceGuard:
      'tenant_id:workflow_definition_id:residential_round_assignment_id:status',
    runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
    generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
    notificationGuard: 'notification_events.idempotency_key',
    retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
  },
  legalSourceNotes: [
    {
      scope: 'RAC daily care exception review and closure',
      source:
        'Aged care quality, care-minute, AN-ACC, incident, and clinical governance sources must be verified before adding clinical thresholds, reporting deadlines, or funding rules.',
      note: 'No numeric rates, caps, deadlines, fees, clinical thresholds, or pay treatment are encoded in this manifest.',
    },
  ],
  uatCases: [
    {
      id: 'rac-daily-care-full-surfaces',
      title: 'Daily care exception with mobile evidence and portal visibility',
      path: 'Escalate residential round, capture worker evidence, complete nurse review, review care plan/funding impact, notify family where required, and close.',
      expected:
        'Manifest compiles ready when mobile, portal, clinical, care-plan, and quality capabilities are present.',
      degraded: false,
    },
    {
      id: 'rac-daily-care-mobile-disabled',
      title: 'Daily care exception without care-worker app',
      path: 'Disable care-worker app and escalate a residential round.',
      expected:
        'Source evidence compiles to web manual evidence while clinical review still fails closed without qualified actor.',
      degraded: true,
    },
    {
      id: 'rac-daily-care-portal-disabled',
      title: 'Daily care exception without family portal',
      path: 'Require family visibility while portal is disabled.',
      expected: 'Family visibility compiles to web manual communication evidence.',
      degraded: true,
    },
    {
      id: 'rac-daily-care-web-only',
      title: 'Daily care exception for web-only provider',
      path: 'Run with web only and tenant-admin operational fallback.',
      expected:
        'Non-professional steps can route to tenant admin or care coordination, but clinical review/closure require RN or clinical lead.',
      degraded: true,
    },
  ],
  allocationPolicy: {
    strategy: 'not_applicable',
    biddingAllowed: false,
    managerConfirmation: 'not_applicable',
  },
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const RAC_DAILY_CARE_EXCEPTION_WORKFLOW = RAC_DAILY_CARE_EXCEPTION_MANIFEST.definition;

const GOVERNANCE_EVENT_CLOSEOUT_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'governance.event-closeout.v1',
  title: 'Governance event lodgement to quality closeout',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'governance.event_reported',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/compliance/incidents', criticality: 'mandatory' },
    { path: '/compliance/complaints', criticality: 'strongly_recommended' },
    { path: '/compliance/quality', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'frontline-lodgement-evidence',
      label: 'Frontline incident lodgement evidence',
      actionType: 'record_evidence',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'incident.create',
      preferredSurfaces: ['care-worker-app'],
      condition: 'frontlineLodgementReceived',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'quality_governance',
        reason:
          'care-worker app unavailable; quality owner must attach source lodgement evidence manually',
      },
      evidenceType: 'GOVERNANCE_FRONTLINE_LODGEMENT_EVIDENCE',
      relatedResourceType: 'incident',
      legalCritical: true,
    },
    {
      id: 'portal-lodgement-evidence',
      label: 'Portal feedback or complaint lodgement evidence',
      actionType: 'record_evidence',
      responsibility: 'manual_communication',
      capabilityId: 'portal.feedback',
      preferredSurfaces: ['client-portal'],
      condition: 'portalLodgementReceived',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason:
          'client portal unavailable; provider must record phone, email, letter, or representative lodgement evidence',
      },
      evidenceType: 'GOVERNANCE_PORTAL_OR_MANUAL_LODGEMENT_EVIDENCE',
      relatedResourceType: 'complaint',
      legalCritical: true,
    },
    {
      id: 'quality-triage-classification',
      label: 'Quality triage and reportability classification',
      actionType: 'create_task',
      responsibility: 'quality_governance',
      capabilityId: 'incident.review',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'GOVERNANCE_EVENT_TRIAGED_CLASSIFIED',
      relatedResourceType: 'incident',
      legalCritical: true,
    },
    {
      id: 'governance-action-plan',
      label: 'Governance action and improvement plan',
      actionType: 'create_task',
      responsibility: 'quality_governance',
      capabilityId: 'incident.review',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'GOVERNANCE_ACTION_PLAN_RECORDED',
      relatedResourceType: 'incident',
      legalCritical: true,
    },
    {
      id: 'client-family-response',
      label: 'Client and family response or manual communication',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.feedback',
      preferredSurfaces: ['client-portal'],
      condition: 'clientVisibleChange',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason:
          'client portal unavailable; provider must record the client/family response pathway manually',
      },
      evidenceType: 'GOVERNANCE_CLIENT_FAMILY_RESPONSE_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'complaint',
      legalCritical: true,
    },
    {
      id: 'quality-closeout-evidence',
      label: 'Quality closeout evidence',
      actionType: 'record_evidence',
      responsibility: 'quality_governance',
      capabilityId: 'incident.review',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'GOVERNANCE_EVENT_CLOSED_WITH_EVIDENCE',
      relatedResourceType: 'incident',
      legalCritical: true,
    },
  ],
} as const;

export const GOVERNANCE_EVENT_CLOSEOUT_MANIFEST = {
  id: GOVERNANCE_EVENT_CLOSEOUT_WORKFLOW_DEFINITION.id,
  version: 1,
  title: GOVERNANCE_EVENT_CLOSEOUT_WORKFLOW_DEFINITION.title,
  definition: GOVERNANCE_EVENT_CLOSEOUT_WORKFLOW_DEFINITION,
  trigger: {
    eventName: GOVERNANCE_EVENT_CLOSEOUT_WORKFLOW_DEFINITION.triggerEvent,
    schemaVersion: 1,
    sourceTable: 'incidents',
    sourceResourceType: 'governance_event',
    requiredFields: ['tenant_id', 'source_record_id', 'source_record_type', 'reported_at'],
    idempotencyKey: 'tenant_id:source_record_type:source_record_id:governance.event_reported',
    activationOwner: 'notification_events_dispatcher',
  },
  runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
  programApplicability: {
    fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
    orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  },
  workflowBlock: {
    id: 'governance_event_closeout',
    tier: 'mandatory',
  },
  requiredModules: GOVERNANCE_EVENT_CLOSEOUT_WORKFLOW_DEFINITION.moduleRequirements,
  surfaces: [
    {
      surface: 'web',
      capabilityId: 'incident.review',
      required: true,
      degradedUatCaseId: 'governance-web-only-small-provider',
    },
    {
      surface: 'care-worker-app',
      capabilityId: 'incident.create',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'governance-mobile-disabled',
    },
    {
      surface: 'client-portal',
      capabilityId: 'portal.feedback',
      required: false,
      fallbackSurface: 'web',
      fallbackAction: 'manual_evidence',
      degradedUatCaseId: 'governance-portal-disabled',
    },
  ],
  responsibilities: [
    {
      responsibility: 'frontline_assigned_worker',
      allowedRoles: RESPONSIBILITY_RULES.frontline_assigned_worker.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.frontline_assigned_worker.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
    {
      responsibility: 'quality_governance',
      allowedRoles: RESPONSIBILITY_RULES.quality_governance.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.quality_governance.cascade,
      noQualifiedActorBehavior: 'fallback_to_accountable_actor',
    },
    {
      responsibility: 'manual_communication',
      allowedRoles: RESPONSIBILITY_RULES.manual_communication.allowedCompleters,
      fallbackCascade: RESPONSIBILITY_RULES.manual_communication.cascade,
      noQualifiedActorBehavior: 'manual_evidence_required',
    },
  ],
  tasks: [
    {
      stepId: 'frontline-lodgement-evidence',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'GOVERNANCE_FRONTLINE_LODGEMENT_EVIDENCE',
    },
    {
      stepId: 'portal-lodgement-evidence',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'GOVERNANCE_PORTAL_OR_MANUAL_LODGEMENT_EVIDENCE',
    },
    {
      stepId: 'quality-triage-classification',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'GOVERNANCE_EVENT_TRIAGED_CLASSIFIED',
    },
    {
      stepId: 'governance-action-plan',
      category: 'task',
      createsWorkflowTask: true,
      requiredEvidenceType: 'GOVERNANCE_ACTION_PLAN_RECORDED',
    },
    {
      stepId: 'client-family-response',
      category: 'notification',
      createsWorkflowTask: true,
      requiredEvidenceType: 'GOVERNANCE_CLIENT_FAMILY_RESPONSE_OR_MANUAL_COMMUNICATION',
    },
    {
      stepId: 'quality-closeout-evidence',
      category: 'evidence',
      createsWorkflowTask: true,
      requiredEvidenceType: 'GOVERNANCE_EVENT_CLOSED_WITH_EVIDENCE',
    },
  ],
  notifications: [
    {
      id: 'governance-client-family-response',
      stepId: 'client-family-response',
      recipientResponsibility: 'manual_communication',
      surface: 'client-portal',
      acknowledgementCompletesWorkflow: false,
      fallbackStepId: 'client-family-response',
    },
  ],
  approvals: [
    {
      id: 'governance-closeout-approval',
      stepId: 'quality-closeout-evidence',
      responsibility: 'quality_governance',
      providerConfigured: true,
    },
  ],
  acknowledgements: [],
  documents: [
    {
      id: 'governance-source-record',
      stepId: 'quality-triage-classification',
      required: true,
      documentType: 'incident_or_complaint_source_record',
      fallbackEvidenceType: 'GOVERNANCE_EVENT_TRIAGED_CLASSIFIED',
    },
    {
      id: 'governance-response-record',
      stepId: 'client-family-response',
      required: false,
      documentType: 'client_family_response_or_manual_communication_record',
      fallbackEvidenceType: 'GOVERNANCE_CLIENT_FAMILY_RESPONSE_OR_MANUAL_COMMUNICATION',
    },
  ],
  timers: [
    {
      id: 'governance-triage-review-due',
      stepId: 'quality-triage-classification',
      dueDaysFromStart: 1,
      dueDateType: 'SOFT',
      escalationResponsibility: 'quality_governance',
    },
    {
      id: 'governance-closeout-review-due',
      stepId: 'quality-closeout-evidence',
      dueDaysFromStart: 7,
      dueDateType: 'SOFT',
      escalationResponsibility: 'quality_governance',
    },
  ],
  evidence: [
    {
      stepId: 'frontline-lodgement-evidence',
      evidenceType: 'GOVERNANCE_FRONTLINE_LODGEMENT_EVIDENCE',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'incidents'],
    },
    {
      stepId: 'portal-lodgement-evidence',
      evidenceType: 'GOVERNANCE_PORTAL_OR_MANUAL_LODGEMENT_EVIDENCE',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'complaints',
        'portal_feedback',
      ],
    },
    {
      stepId: 'quality-triage-classification',
      evidenceType: 'GOVERNANCE_EVENT_TRIAGED_CLASSIFIED',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'incidents'],
    },
    {
      stepId: 'governance-action-plan',
      evidenceType: 'GOVERNANCE_ACTION_PLAN_RECORDED',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'complaints',
        'continuous_improvement_actions',
      ],
    },
    {
      stepId: 'client-family-response',
      evidenceType: 'GOVERNANCE_CLIENT_FAMILY_RESPONSE_OR_MANUAL_COMMUNICATION',
      required: true,
      linkedRecords: ['source_record', 'workflow_task', 'workflow_instance', 'complaints'],
    },
    {
      stepId: 'quality-closeout-evidence',
      evidenceType: 'GOVERNANCE_EVENT_CLOSED_WITH_EVIDENCE',
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        'incidents',
        'complaints',
      ],
    },
  ],
  providerConfiguration: {
    mandatory: [
      {
        key: 'enabledModules.complianceIncidents',
        description: 'Incident/governance review module must remain active for all provider types.',
      },
      {
        key: 'rules.reportabilityClassification',
        description:
          'Reportability and classification decisions must be deterministic and evidence-backed.',
      },
      {
        key: 'responsibility.qualityGovernance',
        description:
          'Provider must have a quality-governance owner or tenant-admin accountable fallback.',
      },
    ],
    stronglyRecommended: [
      {
        key: 'surface.clientPortalFeedback',
        description:
          'Portal feedback/complaint visibility should be enabled where the provider uses the family portal.',
      },
      {
        key: 'surface.careWorkerIncidentCapture',
        description:
          'Mobile incident capture should be enabled where frontline staff use the care-worker app.',
      },
      {
        key: 'enabledModules.complianceQuality',
        description: 'Quality hub should be enabled for improvement and closeout follow-through.',
      },
    ],
    optional: [
      {
        key: 'governance.localCloseoutApproval',
        description:
          'Providers can add local closeout approval steps without weakening mandatory evidence.',
      },
      {
        key: 'governance.responseTemplates',
        description: 'Providers can configure local client/family response templates.',
      },
    ],
  },
  idempotency: {
    sourceEventKey: 'governance_event_reported_notification_event_id',
    workflowInstanceGuard: 'tenant_id:workflow_definition_id:source_record_type:source_record_id',
    runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
    generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
    notificationGuard: 'notification_events.idempotency_key',
    retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
  },
  legalSourceNotes: [
    {
      scope: 'Aged-care governance event triage, response, and closeout',
      source:
        'Aged Care Act 2024 compilation C2026C00301 (Compilation No. 2, 1 Jul 2026) and Aged Care Rules 2025 compilation F2026C00802 (Compilation No. 10, 1 Aug 2026), verified against Federal Register of Legislation on 2026-09-21.',
      note: 'No numeric rates, caps, regulatory deadlines, fees, or pay treatment are encoded in this manifest; reportability and deadline rules must be separately source-verified before runtime activation.',
    },
  ],
  uatCases: [
    {
      id: 'governance-full-cross-surface',
      title: 'Frontline event to quality closeout with portal response',
      path: 'Care worker lodges event, quality owner triages, action plan is recorded, and client/family response is projected to portal.',
      expected:
        'Manifest compiles ready; evidence links source record, tasks, workflow instance, quality action, and response path.',
      degraded: false,
    },
    {
      id: 'governance-mobile-disabled',
      title: 'Governance event with mobile disabled',
      path: 'Disable care-worker app and attach source lodgement evidence through web.',
      expected:
        'Frontline source evidence becomes web manual evidence; quality triage and closeout remain mandatory.',
      degraded: true,
    },
    {
      id: 'governance-portal-disabled',
      title: 'Governance event with portal disabled',
      path: 'Disable client portal and record the client/family response manually.',
      expected:
        'Portal response step falls back to web manual communication evidence, not skipped visibility.',
      degraded: true,
    },
    {
      id: 'governance-web-only-small-provider',
      title: 'Governance event for small web-only provider',
      path: 'Run with web only and no quality officer.',
      expected:
        'Quality-governance tasks route to tenant-admin accountable fallback while preserving evidence.',
      degraded: true,
    },
  ],
  allocationPolicy: {
    strategy: 'not_applicable',
    biddingAllowed: false,
    managerConfirmation: 'not_applicable',
  },
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const GOVERNANCE_EVENT_CLOSEOUT_WORKFLOW = GOVERNANCE_EVENT_CLOSEOUT_MANIFEST.definition;

function responsibilityManifestContract(
  responsibility: WorkflowResponsibility,
  noQualifiedActorBehavior: WorkflowManifestNoQualifiedActorBehavior,
): WorkflowManifestResponsibilityContract {
  return {
    responsibility,
    allowedRoles: RESPONSIBILITY_RULES[responsibility].allowedCompleters,
    fallbackCascade: RESPONSIBILITY_RULES[responsibility].cascade,
    noQualifiedActorBehavior,
  };
}

function categoryForAction(
  actionType: WorkflowActionType,
): WorkflowManifestTaskContract['category'] {
  switch (actionType) {
    case 'notify':
    case 'surface_projection':
      return 'notification';
    case 'request_acknowledgement':
      return 'acknowledgement';
    case 'record_evidence':
    case 'manual_evidence':
      return 'evidence';
    default:
      return 'task';
  }
}

function taskContractsForDefinition(
  definition: WorkflowDefinition,
): WorkflowManifestTaskContract[] {
  return definition.steps.map((step) => ({
    stepId: step.id,
    category: categoryForAction(step.actionType),
    createsWorkflowTask: true,
    requiredEvidenceType: step.evidenceType,
  }));
}

function evidenceContractsForDefinition(
  definition: WorkflowDefinition,
  linkedRecords: Partial<Record<string, readonly string[]>>,
): WorkflowManifestEvidenceContract[] {
  return definition.steps
    .filter((step) => step.legalCritical)
    .map((step) => ({
      stepId: step.id,
      evidenceType: step.evidenceType ?? `${step.id.toUpperCase()}_EVIDENCE`,
      required: true,
      linkedRecords: [
        'source_record',
        'workflow_task',
        'workflow_instance',
        ...(linkedRecords[step.id] ?? []),
      ],
    }));
}

function legalSourceNote(scope: string): WorkflowManifestLegalSourceNote {
  return {
    scope,
    source:
      'Aged Care Act 2024 compilation C2026C00301 (Compilation No. 2, 1 Jul 2026) and Aged Care Rules 2025 compilation F2026C00802 (Compilation No. 10, 1 Aug 2026), verified on 2026-09-21.',
    note: 'No numeric rates, caps, regulatory deadlines, fees, or pay treatment are encoded in this manifest; those values and thresholds must be source-verified before runtime activation.',
  };
}

interface PlatformManifestSpec {
  definition: WorkflowDefinition;
  sourceTable: string;
  sourceResourceType: string;
  requiredFields: readonly string[];
  idempotencyKey: string;
  programApplicability: WorkflowManifestProgramApplicability;
  workflowBlock: WorkflowManifestWorkflowBlock;
  surfaces: readonly WorkflowManifestSurfaceContract[];
  responsibilities: readonly WorkflowManifestResponsibilityContract[];
  notifications?: readonly WorkflowManifestNotificationContract[];
  approvals?: readonly WorkflowManifestApprovalContract[];
  acknowledgements?: readonly WorkflowManifestAcknowledgementContract[];
  documents?: readonly WorkflowManifestDocumentContract[];
  timers?: readonly WorkflowManifestTimerContract[];
  linkedRecords: Partial<Record<string, readonly string[]>>;
  providerConfiguration: WorkflowManifestProviderConfigurationBoundaries;
  legalScope: string;
  uatCases: readonly WorkflowManifestUatCase[];
}

function makeManifestOnlyWorkflow(spec: PlatformManifestSpec): WorkflowManifest {
  const eventKey = spec.definition.triggerEvent.replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  return {
    id: spec.definition.id,
    version: 1,
    title: spec.definition.title,
    definition: spec.definition,
    trigger: {
      eventName: spec.definition.triggerEvent,
      schemaVersion: 1,
      sourceTable: spec.sourceTable,
      sourceResourceType: spec.sourceResourceType,
      requiredFields: spec.requiredFields,
      idempotencyKey: spec.idempotencyKey,
      activationOwner: 'notification_events_dispatcher',
    },
    runtimeOwnership: CONTROLLED_WORKFLOW_RUNTIME_OWNERSHIP,
    programApplicability: spec.programApplicability,
    workflowBlock: spec.workflowBlock,
    requiredModules: spec.definition.moduleRequirements,
    surfaces: spec.surfaces,
    responsibilities: spec.responsibilities,
    tasks: taskContractsForDefinition(spec.definition),
    notifications: spec.notifications ?? [],
    approvals: spec.approvals ?? [],
    acknowledgements: spec.acknowledgements ?? [],
    documents: spec.documents ?? [],
    timers: spec.timers ?? [],
    evidence: evidenceContractsForDefinition(spec.definition, spec.linkedRecords),
    providerConfiguration: spec.providerConfiguration,
    idempotency: {
      sourceEventKey: `${eventKey}_notification_event_id`,
      workflowInstanceGuard: 'tenant_id:workflow_definition_id:source_record_id',
      runtimeStepGuard: 'workflow_instance_id:runtime_step_id',
      generatedTaskGuard: 'workflow_instance_id:workflow_step_id:assignee_id',
      notificationGuard: 'notification_events.idempotency_key',
      retryContract: 'notification_events.attempt_count:locked_at:MAX_ATTEMPTS',
    },
    legalSourceNotes: [legalSourceNote(spec.legalScope)],
    uatCases: spec.uatCases,
    allocationPolicy: {
      strategy: 'not_applicable',
      biddingAllowed: false,
      managerConfirmation: 'not_applicable',
    },
    status: 'manifest_only',
  };
}

const WEB_MOBILE_PORTAL_CLINICAL_SURFACES = [
  {
    surface: 'web',
    capabilityId: 'client.care.workspace',
    required: true,
    degradedUatCaseId: 'platform-web-only',
  },
  {
    surface: 'care-worker-app',
    capabilityId: 'frontline.observation.record',
    required: false,
    fallbackSurface: 'web',
    fallbackAction: 'manual_evidence',
    degradedUatCaseId: 'platform-mobile-disabled',
  },
  {
    surface: 'client-portal',
    capabilityId: 'portal.safety.view',
    required: false,
    fallbackSurface: 'web',
    fallbackAction: 'manual_evidence',
    degradedUatCaseId: 'platform-portal-disabled',
  },
] as const satisfies readonly WorkflowManifestSurfaceContract[];

const QUALITY_WEB_MOBILE_PORTAL_SURFACES = [
  {
    surface: 'web',
    capabilityId: 'quality.gpms.access',
    required: true,
    degradedUatCaseId: 'platform-web-only',
  },
  {
    surface: 'care-worker-app',
    capabilityId: 'incident.create',
    required: false,
    fallbackSurface: 'web',
    fallbackAction: 'manual_evidence',
    degradedUatCaseId: 'platform-mobile-disabled',
  },
  {
    surface: 'client-portal',
    capabilityId: 'portal.safety.view',
    required: false,
    fallbackSurface: 'web',
    fallbackAction: 'manual_evidence',
    degradedUatCaseId: 'platform-portal-disabled',
  },
] as const satisfies readonly WorkflowManifestSurfaceContract[];

const FINANCE_WEB_PORTAL_SURFACES = [
  {
    surface: 'web',
    capabilityId: 'sah.statement.issue',
    required: true,
    degradedUatCaseId: 'platform-web-only',
  },
  {
    surface: 'care-worker-app',
    capabilityId: 'frontline.shift.execute',
    required: false,
    fallbackSurface: 'web',
    fallbackAction: 'manual_evidence',
    degradedUatCaseId: 'platform-mobile-disabled',
  },
  {
    surface: 'client-portal',
    capabilityId: 'portal.financial.view',
    required: false,
    fallbackSurface: 'web',
    fallbackAction: 'manual_evidence',
    degradedUatCaseId: 'platform-portal-disabled',
  },
] as const satisfies readonly WorkflowManifestSurfaceContract[];

const COMMON_PLATFORM_UAT_CASES = [
  {
    id: 'platform-full-cross-surface',
    title: 'Cross-surface workflow with all modules available',
    path: 'Trigger the workflow with web, care-worker app, and client portal available.',
    expected:
      'Manifest compiles ready; every legal-critical task has source, task, instance, and evidence linkage.',
    degraded: false,
  },
  {
    id: 'platform-mobile-disabled',
    title: 'Workflow with care-worker app disabled',
    path: 'Disable the care-worker app and trigger the same source event.',
    expected: 'Mobile-origin work falls back to a web manual-evidence task and is not skipped.',
    degraded: true,
  },
  {
    id: 'platform-portal-disabled',
    title: 'Workflow with client portal disabled',
    path: 'Disable the family/client portal and trigger the same source event.',
    expected: 'Client/family visibility falls back to web manual communication evidence.',
    degraded: true,
  },
  {
    id: 'platform-web-only',
    title: 'Workflow with only web available',
    path: 'Run the workflow for a small provider with no dedicated mobile or portal surface.',
    expected: 'Mandatory work routes to accountable web roles with explicit fallback evidence.',
    degraded: true,
  },
] as const satisfies readonly WorkflowManifestUatCase[];

const SAH_END_OF_LIFE_PATHWAY_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'sah.end-of-life-pathway.v1',
  title: 'SAH End-of-Life pathway funding and care-plan follow-through',
  program: 'SAH',
  triggerEvent: 'sah.end_of_life_pathway_transitioned',
  supportedOrgTypes: ['HOME_CARE', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/home-care/budgets', criticality: 'mandatory' },
    { path: '/home-care/service-delivery', criticality: 'strongly_recommended' },
    { path: '/home-care/statements', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'pathway-transition-evidence',
      label: 'Record approved pathway transition and funding evidence',
      actionType: 'record_evidence',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'SAH_END_OF_LIFE_FUNDING_TRANSITIONED',
      relatedResourceType: 'sah_pathway_funding_envelope',
      legalCritical: true,
    },
    {
      id: 'qualified-clinical-review',
      label: 'Complete qualified palliative clinical review',
      actionType: 'create_task',
      responsibility: 'clinical_review',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      professionalScope: true,
      evidenceType: 'SAH_END_OF_LIFE_CLINICAL_REVIEW_REQUIRED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'budgeted-care-plan-binding',
      label: 'Bind the governed pathway envelope to the care plan',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'SAH_END_OF_LIFE_CARE_PLAN_BOUND',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'worker-point-of-care-visibility',
      label: 'Project pathway care context to assigned workers',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.handover.access',
      preferredSurfaces: ['care-worker-app'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'care_coordination',
        reason:
          'care-worker app unavailable; coordinator must brief assigned workers and record evidence',
      },
      evidenceType: 'CARE_CONTEXT_WORKER_ACKNOWLEDGED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'client-family-budget-visibility',
      label: 'Provide pathway budget visibility to the client or representative',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.financial.view',
      preferredSurfaces: ['client-portal'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason:
          'client portal unavailable; provider must issue the pathway budget and care-plan summary manually',
      },
      evidenceType: 'CLIENT_VISIBILITY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'sah_pathway_funding_envelope',
      legalCritical: true,
    },
    {
      id: 'pathway-financial-closeout',
      label: 'Close or expire the pathway envelope and supersede pathway context',
      actionType: 'record_evidence',
      responsibility: 'finance_review',
      capabilityId: 'sah.claim.prepare',
      preferredSurfaces: ['web'],
      evidenceType: 'SAH_END_OF_LIFE_FUNDING_ENVELOPE_CLOSED',
      relatedResourceType: 'sah_pathway_funding_envelope',
      legalCritical: true,
    },
  ],
} as const;

export const SAH_END_OF_LIFE_PATHWAY_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: SAH_END_OF_LIFE_PATHWAY_WORKFLOW_DEFINITION,
    sourceTable: 'sah_pathway_funding_envelopes',
    sourceResourceType: 'sah_pathway_funding_envelope',
    requiredFields: [
      'tenant_id',
      'client_id',
      'envelope_id',
      'funded_period_start',
      'access_end_date',
    ],
    idempotencyKey: 'tenant_id:envelope_id:status:updated_at',
    programApplicability: {
      fundingPrograms: ['SAH'],
      orgTypes: ['HOME_CARE', 'MIXED'],
    },
    workflowBlock: {
      id: 'sah_end_of_life_pathway_follow_through',
      tier: 'mandatory',
    },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'client.care.workspace',
        required: true,
        degradedUatCaseId: 'sah-eol-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.handover.access',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'sah-eol-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.financial.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'sah-eol-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('clinical_review', 'fail_closed'),
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
      responsibilityManifestContract('manual_communication', 'manual_evidence_required'),
      responsibilityManifestContract('finance_review', 'fallback_to_accountable_actor'),
    ],
    notifications: [
      {
        id: 'sah-eol-worker-care-context',
        stepId: 'worker-point-of-care-visibility',
        recipientResponsibility: 'frontline_assigned_worker',
        surface: 'care-worker-app',
        acknowledgementCompletesWorkflow: false,
        fallbackStepId: 'worker-point-of-care-visibility',
      },
      {
        id: 'sah-eol-client-budget-visibility',
        stepId: 'client-family-budget-visibility',
        recipientResponsibility: 'manual_communication',
        surface: 'client-portal',
        acknowledgementCompletesWorkflow: false,
        fallbackStepId: 'client-family-budget-visibility',
      },
    ],
    acknowledgements: [
      {
        id: 'sah-eol-worker-acknowledgement',
        stepId: 'worker-point-of-care-visibility',
        responsibility: 'frontline_assigned_worker',
        completesWorkflow: false,
        evidenceType: 'CARE_CONTEXT_WORKER_ACKNOWLEDGED',
      },
    ],
    documents: [
      {
        id: 'sah-eol-approval-evidence',
        stepId: 'pathway-transition-evidence',
        required: true,
        documentType: 'notice_of_decision_or_pathway_eligibility_evidence',
      },
      {
        id: 'sah-eol-budget-and-care-plan-summary',
        stepId: 'client-family-budget-visibility',
        required: false,
        documentType: 'pathway_budget_and_care_plan_summary',
        fallbackEvidenceType: 'CLIENT_VISIBILITY_OR_MANUAL_COMMUNICATION',
      },
    ],
    timers: [
      {
        id: 'sah-eol-clinical-review-due',
        stepId: 'qualified-clinical-review',
        dueDaysFromStart: 1,
        dueDateType: 'HARD',
        escalationResponsibility: 'clinical_review',
      },
      {
        id: 'sah-eol-care-plan-binding-due',
        stepId: 'budgeted-care-plan-binding',
        dueDaysFromStart: 1,
        dueDateType: 'HARD',
        escalationResponsibility: 'care_coordination',
      },
    ],
    linkedRecords: {
      'pathway-transition-evidence': [
        'client',
        'sah_client_decision',
        'client_funding_context',
        'sah_pathway_funding_envelope',
      ],
      'qualified-clinical-review': ['client', 'care_plan', 'advance_care_document'],
      'budgeted-care-plan-binding': ['care_plan', 'sah_pathway_funding_envelope'],
      'worker-point-of-care-visibility': ['care_plan', 'worker_assignment'],
      'client-family-budget-visibility': ['care_plan', 'client_contact'],
      'pathway-financial-closeout': [
        'care_plan',
        'sah_pathway_funding_envelope',
        'client_funding_context',
      ],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'responsibility.careCoordination',
          description: 'Provider must assign accountable care coordination for pathway transition.',
        },
        {
          key: 'responsibility.clinicalReview',
          description:
            'Provider must have a qualified clinical reviewer; this responsibility fails closed.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.careWorkerApp',
          description:
            'Assigned workers should receive current pathway care context at point of care.',
        },
        {
          key: 'surface.clientPortal',
          description:
            'Clients and representatives should receive pathway budget visibility through the portal where enabled.',
        },
      ],
      optional: [
        {
          key: 'finance.localCloseoutApproval',
          description:
            'Providers may add a local finance approval before pathway-envelope closeout evidence.',
        },
      ],
    },
    legalScope:
      'SAH End-of-Life pathway eligibility evidence, budget binding, care-plan follow-through, point-of-care visibility, and financial closeout',
    uatCases: [
      {
        id: 'sah-eol-full-cross-surface',
        title: 'End-of-Life pathway transition across all provider surfaces',
        path: 'Transition an eligible client, bind the envelope to the care plan, deliver services, and close the pathway.',
        expected:
          'Provider, worker, and portal surfaces show the same current envelope; closeout supersedes pathway context and preserves immutable evidence.',
        degraded: false,
      },
      {
        id: 'sah-eol-mobile-disabled',
        title: 'End-of-Life pathway with mobile disabled',
        path: 'Disable the care-worker app before pathway transition.',
        expected:
          'Worker briefing becomes a web manual-evidence responsibility and is not skipped.',
        degraded: true,
      },
      {
        id: 'sah-eol-portal-disabled',
        title: 'End-of-Life pathway with portal disabled',
        path: 'Disable the client portal before pathway transition.',
        expected:
          'Client/family budget visibility becomes a web manual-communication evidence responsibility.',
        degraded: true,
      },
      {
        id: 'sah-eol-web-only',
        title: 'End-of-Life pathway for a web-only provider',
        path: 'Run with web only and accountable provider roles available.',
        expected:
          'Mandatory transition, clinical, budget, and closeout work remains visible with explicit degraded evidence.',
        degraded: true,
      },
    ],
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const SAH_END_OF_LIFE_PATHWAY_WORKFLOW = SAH_END_OF_LIFE_PATHWAY_MANIFEST.definition;

const CLINICAL_REVIEW_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'clinical.review-continuity.v1',
  title: 'Clinical review and care-continuity follow-through',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'clinical.review_required',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/clinical', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'frontline-clinical-record-capture',
      label: 'Frontline clinical record capture',
      actionType: 'record_evidence',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.observation.record',
      preferredSurfaces: ['care-worker-app'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'clinical_review',
        reason: 'care-worker app unavailable; clinician records source evidence in web',
      },
      evidenceType: 'CLINICAL_SOURCE_CAPTURED',
      relatedResourceType: 'observation',
      legalCritical: true,
    },
    {
      id: 'qualified-clinical-review',
      label: 'Qualified clinical review',
      actionType: 'create_task',
      responsibility: 'clinical_review',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      professionalScope: true,
      evidenceType: 'QUALIFIED_CLINICAL_REVIEW_RECORDED',
      relatedResourceType: 'clinical_alert',
      legalCritical: true,
    },
    {
      id: 'care-plan-impact-check',
      label: 'Care-plan impact check',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'CARE_PLAN_IMPACT_CHECK_RECORDED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'client-family-clinical-visibility',
      label: 'Client and family clinical visibility',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.safety.view',
      preferredSurfaces: ['client-portal'],
      condition: 'clientVisibleChange',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason: 'client portal unavailable; provider records manual clinical update evidence',
      },
      evidenceType: 'CLINICAL_VISIBILITY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'clinical_alert',
      legalCritical: true,
    },
  ],
};

export const CLINICAL_REVIEW_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: CLINICAL_REVIEW_WORKFLOW_DEFINITION,
    sourceTable: 'clinical_alerts',
    sourceResourceType: 'clinical_review',
    requiredFields: ['tenant_id', 'client_id', 'source_id', 'clinical_domain'],
    idempotencyKey: 'tenant_id:source_table:source_id:clinical.review_required',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'clinical_review_continuity', tier: 'mandatory' },
    surfaces: WEB_MOBILE_PORTAL_CLINICAL_SURFACES,
    responsibilities: [
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
      responsibilityManifestContract('clinical_review', 'fail_closed'),
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('manual_communication', 'manual_evidence_required'),
    ],
    notifications: [
      {
        id: 'clinical-client-family-visibility',
        stepId: 'client-family-clinical-visibility',
        recipientResponsibility: 'manual_communication',
        surface: 'client-portal',
        acknowledgementCompletesWorkflow: false,
        fallbackStepId: 'client-family-clinical-visibility',
      },
    ],
    timers: [
      {
        id: 'clinical-review-due',
        stepId: 'qualified-clinical-review',
        dueDaysFromStart: 1,
        dueDateType: 'SOFT',
        escalationResponsibility: 'clinical_review',
      },
    ],
    linkedRecords: {
      'frontline-clinical-record-capture': [
        'clinical_alerts',
        'iot_alerts',
        'observations',
        'medication_incidents',
        'wound_assessments',
        'wounds',
      ],
      'qualified-clinical-review': [
        'clinical_alerts',
        'iot_alerts',
        'observations',
        'medication_incidents',
        'wound_assessments',
        'wounds',
        'care_plans',
      ],
      'care-plan-impact-check': [
        'care_plans',
        'care_plan_risks',
        'care_plan_interventions',
        'care_plan_worker_instructions',
      ],
      'client-family-clinical-visibility': ['client_contacts', 'care_plan_acknowledgements'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'responsibility.clinicalReview',
          description: 'Professional-scope clinical review must have qualified role coverage.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.careWorkerClinicalCapture',
          description:
            'Mobile clinical capture should be enabled where frontline staff use the app.',
        },
      ],
      optional: [
        {
          key: 'clinical.localEscalationWindows',
          description: 'Providers may tighten local review windows without weakening evidence.',
        },
      ],
    },
    legalScope:
      'Clinical records, monitoring alerts, medication, wound, observation, ACD, palliative, and telehealth review',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const CLINICAL_REVIEW_WORKFLOW = CLINICAL_REVIEW_MANIFEST.definition;

const CARE_PLAN_CHANGE_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'care-plan.change-acknowledgement.v1',
  title: 'Care-plan change, worker instruction, and client visibility',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'care_plan.updated',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'care-plan-change-evidence',
      label: 'Care-plan change evidence',
      actionType: 'record_evidence',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'CARE_PLAN_CHANGE_RECORDED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'worker-instruction-acknowledgement',
      label: 'Worker instruction acknowledgement',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'care_coordination',
        reason: 'care-worker app unavailable; coordinator records worker briefing evidence',
      },
      evidenceType: 'WORKER_INSTRUCTION_ACKNOWLEDGED',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
    {
      id: 'client-family-care-plan-visibility',
      label: 'Client and representative care-plan visibility',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.care.view',
      preferredSurfaces: ['client-portal'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason: 'client portal unavailable; provider records manual care-plan communication',
      },
      evidenceType: 'CARE_PLAN_VISIBILITY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
  ],
};

export const CARE_PLAN_CHANGE_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: CARE_PLAN_CHANGE_WORKFLOW_DEFINITION,
    sourceTable: 'care_plans',
    sourceResourceType: 'care_plan',
    requiredFields: ['tenant_id', 'client_id', 'care_plan_id', 'care_plan_version'],
    idempotencyKey: 'tenant_id:care_plan_id:care_plan_version:care_plan.updated',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'care_plan_change_acknowledgement', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'client.care.workspace',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.shift.execute',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.care.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
      responsibilityManifestContract('manual_communication', 'manual_evidence_required'),
    ],
    notifications: [
      {
        id: 'care-plan-client-family-visibility',
        stepId: 'client-family-care-plan-visibility',
        recipientResponsibility: 'manual_communication',
        surface: 'client-portal',
        acknowledgementCompletesWorkflow: false,
        fallbackStepId: 'client-family-care-plan-visibility',
      },
    ],
    acknowledgements: [
      {
        id: 'care-plan-worker-acknowledgement',
        stepId: 'worker-instruction-acknowledgement',
        responsibility: 'frontline_assigned_worker',
        completesWorkflow: false,
        evidenceType: 'WORKER_INSTRUCTION_ACKNOWLEDGED',
      },
    ],
    linkedRecords: {
      'care-plan-change-evidence': ['care_plans', 'care_plan_interventions'],
      'worker-instruction-acknowledgement': ['care_plan_worker_instructions'],
      'client-family-care-plan-visibility': ['care_plan_acknowledgements'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'responsibility.carePlanOwner',
          description: 'Every care-plan change needs an accountable care-plan owner.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.clientPortalCarePlans',
          description: 'Use portal visibility where the provider offers a family/client portal.',
        },
      ],
      optional: [
        {
          key: 'carePlan.localAcknowledgementRules',
          description:
            'Providers may require extra worker acknowledgements for selected change types.',
        },
      ],
    },
    legalScope:
      'Care and services plans, worker instructions, preferences, risks, and acknowledgements',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const CARE_PLAN_CHANGE_WORKFLOW = CARE_PLAN_CHANGE_MANIFEST.definition;

const RESTRICTIVE_PRACTICE_REVIEW_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'restrictive-practice.review-reportability.v1',
  title: 'Restrictive-practice review, consent, and reportability workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'restrictive_practice.review_required',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/compliance/restrictive-practices', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'restrictive-practice-clinical-review',
      label: 'Qualified restrictive-practice review',
      actionType: 'create_task',
      responsibility: 'clinical_review',
      capabilityId: 'quality.gpms.access',
      preferredSurfaces: ['web'],
      professionalScope: true,
      evidenceType: 'RESTRICTIVE_PRACTICE_CLINICAL_REVIEW',
      relatedResourceType: 'restrictive_practice',
      legalCritical: true,
    },
    {
      id: 'restrictive-practice-frontline-instruction',
      label: 'Frontline implementation instruction',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'clinical_review',
        reason: 'care-worker app unavailable; clinician records frontline briefing evidence',
      },
      evidenceType: 'RESTRICTIVE_PRACTICE_FRONTLINE_INSTRUCTION',
      relatedResourceType: 'restrictive_practice',
      legalCritical: true,
    },
    {
      id: 'restrictive-practice-governance-evidence',
      label: 'Governance, consent, and reportability evidence',
      actionType: 'record_evidence',
      responsibility: 'quality_governance',
      capabilityId: 'quality.gpms.access',
      preferredSurfaces: ['web'],
      evidenceType: 'RESTRICTIVE_PRACTICE_GOVERNANCE_EVIDENCE',
      relatedResourceType: 'restrictive_practice',
      legalCritical: true,
    },
    {
      id: 'restrictive-practice-rights-visibility',
      label: 'Client rights visibility or manual communication',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.rights.view',
      preferredSurfaces: ['client-portal'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason: 'client portal unavailable; rights communication is recorded manually',
      },
      evidenceType: 'RESTRICTIVE_PRACTICE_RIGHTS_VISIBILITY',
      relatedResourceType: 'restrictive_practice',
      legalCritical: true,
    },
  ],
};

export const RESTRICTIVE_PRACTICE_REVIEW_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: RESTRICTIVE_PRACTICE_REVIEW_WORKFLOW_DEFINITION,
    sourceTable: 'restrictive_practices',
    sourceResourceType: 'restrictive_practice',
    requiredFields: ['tenant_id', 'client_id', 'restrictive_practice_id', 'review_reason'],
    idempotencyKey:
      'tenant_id:restrictive_practice_id:review_reason:restrictive_practice.review_required',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'restrictive_practice_review_reportability', tier: 'mandatory' },
    surfaces: QUALITY_WEB_MOBILE_PORTAL_SURFACES,
    responsibilities: [
      responsibilityManifestContract('clinical_review', 'fail_closed'),
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
      responsibilityManifestContract('quality_governance', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('manual_communication', 'manual_evidence_required'),
    ],
    approvals: [
      {
        id: 'restrictive-practice-governance-approval',
        stepId: 'restrictive-practice-governance-evidence',
        responsibility: 'quality_governance',
        providerConfigured: true,
      },
    ],
    acknowledgements: [
      {
        id: 'restrictive-practice-frontline-acknowledgement',
        stepId: 'restrictive-practice-frontline-instruction',
        responsibility: 'frontline_assigned_worker',
        completesWorkflow: false,
        evidenceType: 'RESTRICTIVE_PRACTICE_FRONTLINE_INSTRUCTION',
      },
    ],
    linkedRecords: {
      'restrictive-practice-clinical-review': ['restrictive_practice_reviews'],
      'restrictive-practice-frontline-instruction': ['care_plan_worker_instructions'],
      'restrictive-practice-governance-evidence': [
        'restrictive_practices',
        'restrictive_practice_episodes',
        'restrictive_practice_reviews',
        'behaviour_support_plans',
        'incidents',
      ],
      'restrictive-practice-rights-visibility': ['restrictive_practices'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.restrictivePracticeDeterministicChecks',
          description: 'Reportability, consent, and review thresholds must be deterministic.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.frontlineInstructionAcknowledgement',
          description:
            'Frontline acknowledgement should be captured through mobile where available.',
        },
      ],
      optional: [
        {
          key: 'restrictivePractice.localApprovalLayers',
          description:
            'Providers can add local approval layers without weakening required evidence.',
        },
      ],
    },
    legalScope:
      'Restrictive practices, behaviour support plans, consents, reviews, and Commission reporting',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const RESTRICTIVE_PRACTICE_REVIEW_WORKFLOW = RESTRICTIVE_PRACTICE_REVIEW_MANIFEST.definition;

const WORKFORCE_ROSTER_PAYROLL_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'workforce.roster-payroll-readiness.v1',
  title: 'Roster, timesheet, leave, availability, and payroll readiness workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'workforce.payroll_exception_detected',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/roster', criticality: 'mandatory' },
    { path: '/workforce', criticality: 'mandatory' },
    { path: '/payroll', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'worker-timesheet-evidence',
      label: 'Worker timesheet or availability evidence',
      actionType: 'record_evidence',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'workerEvidenceRequired',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'workforce_roster',
        reason: 'care-worker app unavailable; roster owner records worker evidence manually',
      },
      evidenceType: 'WORKER_TIMESHEET_OR_AVAILABILITY_EVIDENCE',
      relatedResourceType: 'timesheet',
      legalCritical: true,
    },
    {
      id: 'roster-payroll-exception-review',
      label: 'Roster/payroll exception review',
      actionType: 'create_task',
      responsibility: 'workforce_roster',
      capabilityId: 'workforce.payroll.review',
      preferredSurfaces: ['web'],
      evidenceType: 'ROSTER_PAYROLL_EXCEPTION_REVIEW',
      relatedResourceType: 'timesheet',
      legalCritical: true,
    },
    {
      id: 'payroll-readiness-approval',
      label: 'Payroll readiness approval',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'workforce.payroll.review',
      preferredSurfaces: ['web'],
      condition: 'financeApprovalRequired',
      evidenceType: 'PAYROLL_READINESS_APPROVAL',
      relatedResourceType: 'pay_run',
      legalCritical: true,
    },
  ],
};

export const WORKFORCE_ROSTER_PAYROLL_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: WORKFORCE_ROSTER_PAYROLL_WORKFLOW_DEFINITION,
    sourceTable: 'timesheets',
    sourceResourceType: 'timesheet',
    requiredFields: ['tenant_id', 'staff_id', 'timesheet_id', 'exception_type'],
    idempotencyKey: 'tenant_id:timesheet_id:exception_type:workforce.payroll_exception_detected',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'workforce_roster_payroll_readiness', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'workforce.payroll.review',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.shift.execute',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.financial.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
      responsibilityManifestContract('workforce_roster', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('finance_review', 'fallback_to_accountable_actor'),
    ],
    approvals: [
      {
        id: 'payroll-readiness-approval',
        stepId: 'payroll-readiness-approval',
        responsibility: 'finance_review',
        providerConfigured: true,
      },
    ],
    linkedRecords: {
      'worker-timesheet-evidence': ['timesheets', 'worker_availability', 'leave_requests'],
      'roster-payroll-exception-review': ['roster_periods', 'shift_offers'],
      'payroll-readiness-approval': ['pay_runs', 'pay_run_items'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.awardInterpretationDeterministic',
          description: 'Award/payroll treatment must be deterministic and not AI-configured.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.workerMobileTimesheets',
          description: 'Mobile timesheet evidence should be enabled where workers use the app.',
        },
      ],
      optional: [
        {
          key: 'workforce.localPayrollApprovalLayers',
          description: 'Providers may add local review layers before payroll export.',
        },
      ],
    },
    legalScope: 'Timesheets, availability, leave, roster exceptions, and payroll readiness',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const WORKFORCE_ROSTER_PAYROLL_WORKFLOW = WORKFORCE_ROSTER_PAYROLL_MANIFEST.definition;

const WORKER_COMPLIANCE_ONBOARDING_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'workforce.compliance-onboarding.v1',
  title: 'Worker compliance, onboarding, credential, and contractor evidence workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'workforce.compliance_expiry_detected',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/staff', criticality: 'mandatory' },
    { path: '/compliance/screening', criticality: 'mandatory' },
    { path: '/hr/onboarding', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'worker-evidence-request',
      label: 'Worker credential or onboarding evidence request',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'workerEvidenceRequired',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'workforce_roster',
        reason: 'care-worker app unavailable; workforce owner records evidence manually',
      },
      evidenceType: 'WORKER_COMPLIANCE_EVIDENCE_REQUESTED',
      relatedResourceType: 'training_assignment',
      legalCritical: true,
    },
    {
      id: 'workforce-compliance-review',
      label: 'Workforce compliance review',
      actionType: 'create_task',
      responsibility: 'workforce_roster',
      capabilityId: 'roster.manage',
      preferredSurfaces: ['web'],
      condition: 'workforceComplianceReviewRequired',
      evidenceType: 'WORKFORCE_COMPLIANCE_REVIEW_RECORDED',
      relatedResourceType: 'worker_screening',
      legalCritical: true,
    },
    {
      id: 'onboarding-document-acknowledgement',
      label: 'Policy or onboarding document acknowledgement',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'onboardingDocumentAcknowledgementRequired',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'workforce_roster',
        reason: 'care-worker app unavailable; acknowledgement is recorded manually',
      },
      evidenceType: 'ONBOARDING_DOCUMENT_ACKNOWLEDGED',
      relatedResourceType: 'policy_acknowledgements',
      legalCritical: true,
    },
  ],
};

export const WORKER_COMPLIANCE_ONBOARDING_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: WORKER_COMPLIANCE_ONBOARDING_WORKFLOW_DEFINITION,
    sourceTable: 'worker_screenings',
    sourceResourceType: 'worker_screening',
    requiredFields: ['tenant_id', 'staff_id', 'source_record_id', 'status'],
    idempotencyKey:
      'tenant_id:staff_id:source_record_type:source_record_id:status:workforce.compliance_expiry_detected',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'worker_compliance_onboarding', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'roster.manage',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.shift.execute',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.documents.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
      responsibilityManifestContract('workforce_roster', 'fallback_to_accountable_actor'),
    ],
    acknowledgements: [
      {
        id: 'worker-compliance-evidence-acknowledgement',
        stepId: 'worker-evidence-request',
        responsibility: 'frontline_assigned_worker',
        completesWorkflow: false,
        evidenceType: 'WORKER_COMPLIANCE_EVIDENCE_REQUESTED',
      },
      {
        id: 'onboarding-document-acknowledgement',
        stepId: 'onboarding-document-acknowledgement',
        responsibility: 'frontline_assigned_worker',
        completesWorkflow: false,
        evidenceType: 'ONBOARDING_DOCUMENT_ACKNOWLEDGED',
      },
    ],
    linkedRecords: {
      'worker-evidence-request': [
        'worker_screenings',
        'training_assignments',
        'worker_onboarding_form_responses',
      ],
      'workforce-compliance-review': [
        'worker_screenings',
        'training_assignments',
        'contractor_evidence_submissions',
      ],
      'onboarding-document-acknowledgement': [
        'worker_onboarding_documents',
        'policy_acknowledgements',
      ],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.complianceBeforeRostering',
          description: 'Non-compliant workers must not be eligible for regulated shifts.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.workerEvidenceSubmission',
          description:
            'Mobile worker evidence submission should be enabled where workers use the app.',
        },
      ],
      optional: [
        {
          key: 'workforce.localCredentialReminderCadence',
          description: 'Providers may configure reminder timing without weakening roster gating.',
        },
      ],
    },
    legalScope:
      'Worker screening, training, onboarding documents, contractors, and performance evidence',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const WORKER_COMPLIANCE_ONBOARDING_WORKFLOW =
  WORKER_COMPLIANCE_ONBOARDING_MANIFEST.definition;

const WORKER_COMPLIANCE_RENEWAL_BOOKING_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'workforce.compliance-renewal-booking.v1',
  title: 'Worker compliance renewal booking follow-up workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'compliance.renewal.booked',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/staff', criticality: 'mandatory' },
    { path: '/compliance/certifications', criticality: 'mandatory' },
    { path: '/compliance/workforce', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'renewal-evidence-request',
      label: 'Worker renewal evidence request',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'workerEvidenceRequired',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'workforce_roster',
        reason: 'care-worker app unavailable; workforce owner records renewal evidence manually',
      },
      evidenceType: 'COMPLIANCE_RENEWAL_EVIDENCE_REQUESTED',
      relatedResourceType: 'compliance_renewal_booking',
      legalCritical: true,
    },
    {
      id: 'renewal-compliance-review',
      label: 'Renewal booking compliance review',
      actionType: 'create_task',
      responsibility: 'workforce_roster',
      capabilityId: 'roster.manage',
      preferredSurfaces: ['web'],
      condition: 'workforceComplianceReviewRequired',
      evidenceType: 'COMPLIANCE_RENEWAL_BOOKING_REVIEW_RECORDED',
      relatedResourceType: 'compliance_renewal_booking',
      legalCritical: true,
    },
  ],
};

export const WORKER_COMPLIANCE_RENEWAL_BOOKING_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: WORKER_COMPLIANCE_RENEWAL_BOOKING_WORKFLOW_DEFINITION,
    sourceTable: 'compliance_renewal_bookings',
    sourceResourceType: 'compliance_renewal_booking',
    requiredFields: ['tenant_id', 'staff_id', 'booking_id', 'cert_type', 'status'],
    idempotencyKey: 'tenant_id:booking_id:status:compliance.renewal.booked',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'worker_compliance_renewal_booking', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'roster.manage',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.shift.execute',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.documents.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
      responsibilityManifestContract('workforce_roster', 'fallback_to_accountable_actor'),
    ],
    acknowledgements: [
      {
        id: 'compliance-renewal-evidence-acknowledgement',
        stepId: 'renewal-evidence-request',
        responsibility: 'frontline_assigned_worker',
        completesWorkflow: false,
        evidenceType: 'COMPLIANCE_RENEWAL_EVIDENCE_REQUESTED',
      },
    ],
    linkedRecords: {
      'renewal-evidence-request': ['compliance_renewal_bookings', 'worker_certifications'],
      'renewal-compliance-review': [
        'compliance_renewal_bookings',
        'worker_certifications',
        'worker_screenings',
      ],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.renewalEvidenceBeforeRostering',
          description:
            'Role-required renewal evidence must be reviewed before a non-compliant worker is rostered.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.workerRenewalEvidenceSubmission',
          description:
            'Mobile worker renewal evidence submission should be enabled where workers use the app.',
        },
      ],
      optional: [
        {
          key: 'workforce.localRenewalProviderCadence',
          description: 'Providers may configure local renewal provider and reminder cadence.',
        },
      ],
    },
    legalScope:
      'Worker certification renewal bookings, renewal evidence, and role-required roster gating',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const WORKER_COMPLIANCE_RENEWAL_BOOKING_WORKFLOW =
  WORKER_COMPLIANCE_RENEWAL_BOOKING_MANIFEST.definition;

const QUALITY_INDICATOR_GOVERNANCE_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'quality.indicator-governance.v1',
  title: 'Quality indicator, governance risk, and continuous-improvement workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'quality.indicator_variance_detected',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/compliance/quality', criticality: 'mandatory' },
    { path: '/compliance/continuous-improvement', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'quality-source-evidence',
      label: 'Quality source evidence',
      actionType: 'record_evidence',
      responsibility: 'quality_governance',
      capabilityId: 'quality.gpms.access',
      preferredSurfaces: ['web'],
      evidenceType: 'QUALITY_SOURCE_EVIDENCE_RECORDED',
      relatedResourceType: 'quality_indicator',
      legalCritical: true,
    },
    {
      id: 'quality-indicator-review',
      label: 'Quality indicator review',
      actionType: 'create_task',
      responsibility: 'quality_governance',
      capabilityId: 'quality.gpms.access',
      preferredSurfaces: ['web'],
      evidenceType: 'QUALITY_INDICATOR_REVIEW_RECORDED',
      relatedResourceType: 'quality_indicator',
      legalCritical: true,
    },
    {
      id: 'continuous-improvement-action',
      label: 'Continuous-improvement action',
      actionType: 'create_task',
      responsibility: 'quality_governance',
      capabilityId: 'quality.gpms.access',
      preferredSurfaces: ['web'],
      evidenceType: 'CONTINUOUS_IMPROVEMENT_ACTION_RECORDED',
      relatedResourceType: 'continuous_improvement_plan',
      legalCritical: true,
    },
    {
      id: 'quality-portal-or-manual-transparency',
      label: 'Portal or manual transparency evidence',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.safety.view',
      preferredSurfaces: ['client-portal'],
      condition: 'clientVisibleChange',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason: 'client portal unavailable; transparency communication is recorded manually',
      },
      evidenceType: 'QUALITY_TRANSPARENCY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'quality_indicator',
      legalCritical: true,
    },
  ],
};

export const QUALITY_INDICATOR_GOVERNANCE_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: QUALITY_INDICATOR_GOVERNANCE_WORKFLOW_DEFINITION,
    sourceTable: 'quality_indicator_records',
    sourceResourceType: 'quality_indicator',
    requiredFields: ['tenant_id', 'submission_id', 'domain', 'trend'],
    idempotencyKey:
      'tenant_id:submission_id:domain:source_record_id:quality.indicator_variance_detected',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'quality_indicator_governance', tier: 'mandatory' },
    surfaces: QUALITY_WEB_MOBILE_PORTAL_SURFACES,
    responsibilities: [
      responsibilityManifestContract('quality_governance', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('manual_communication', 'manual_evidence_required'),
    ],
    notifications: [
      {
        id: 'quality-portal-transparency',
        stepId: 'quality-portal-or-manual-transparency',
        recipientResponsibility: 'manual_communication',
        surface: 'client-portal',
        acknowledgementCompletesWorkflow: false,
        fallbackStepId: 'quality-portal-or-manual-transparency',
      },
    ],
    linkedRecords: {
      'quality-source-evidence': [
        'quality_indicator_records',
        'quality_indicator_submissions',
        'qi_adl_assessments',
        'qi_consumer_experience_responses',
        'qi_continence_assessments',
        'qi_hospitalisation_events',
        'qi_quality_of_life_responses',
      ],
      'quality-indicator-review': ['quality_indicator_records', 'quality_indicator_submissions'],
      'continuous-improvement-action': ['continuous_improvement_plans', 'qi_improvement_actions'],
      'quality-portal-or-manual-transparency': ['star_ratings', 'governance_meetings'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'responsibility.qualityGovernance',
          description: 'Quality indicator and improvement workflows need an accountable owner.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'enabledModules.continuousImprovement',
          description: 'Continuous-improvement actions should be enabled with quality indicators.',
        },
      ],
      optional: [
        {
          key: 'quality.localBoardReporting',
          description: 'Providers may add local governance-meeting reporting steps.',
        },
      ],
    },
    legalScope:
      'Quality indicators, star ratings, governance risks, conduct, and continuous improvement',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const QUALITY_INDICATOR_GOVERNANCE_WORKFLOW =
  QUALITY_INDICATOR_GOVERNANCE_MANIFEST.definition;

const DOCUMENT_CONSENT_AGREEMENT_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'documents.consent-agreement-acknowledgement.v1',
  title: 'Consent, service agreement, policy, and document acknowledgement workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'documents.signature_required',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/compliance/policies', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'document-source-evidence',
      label: 'Document source evidence',
      actionType: 'record_evidence',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'DOCUMENT_SOURCE_EVIDENCE_RECORDED',
      relatedResourceType: 'service_agreement',
      legalCritical: true,
    },
    {
      id: 'client-document-signature-or-manual',
      label: 'Client document signature or manual evidence',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.documents.view',
      preferredSurfaces: ['client-portal'],
      condition: 'clientSignatureRequired',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason: 'client portal unavailable; document signature is captured manually',
      },
      evidenceType: 'CLIENT_DOCUMENT_SIGNATURE_OR_MANUAL_EVIDENCE',
      relatedResourceType: 'service_agreement',
      legalCritical: true,
    },
    {
      id: 'staff-policy-acknowledgement',
      label: 'Staff policy acknowledgement',
      actionType: 'request_acknowledgement',
      responsibility: 'frontline_assigned_worker',
      capabilityId: 'frontline.shift.execute',
      preferredSurfaces: ['care-worker-app'],
      condition: 'staffPolicyAcknowledgementRequired',
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'care_coordination',
        reason: 'care-worker app unavailable; staff acknowledgement is recorded manually',
      },
      evidenceType: 'STAFF_POLICY_ACKNOWLEDGEMENT',
      relatedResourceType: 'policy_acknowledgements',
      legalCritical: true,
    },
  ],
};

export const DOCUMENT_CONSENT_AGREEMENT_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: DOCUMENT_CONSENT_AGREEMENT_WORKFLOW_DEFINITION,
    sourceTable: 'service_agreements',
    sourceResourceType: 'service_agreement',
    requiredFields: ['tenant_id', 'document_id', 'document_type', 'source_record_type'],
    idempotencyKey: 'tenant_id:source_record_type:document_id:documents.signature_required',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'documents_consent_agreement_acknowledgement', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'client.care.workspace',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.shift.execute',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.documents.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('manual_communication', 'manual_evidence_required'),
      responsibilityManifestContract('frontline_assigned_worker', 'manual_evidence_required'),
    ],
    acknowledgements: [
      {
        id: 'staff-policy-acknowledgement',
        stepId: 'staff-policy-acknowledgement',
        responsibility: 'frontline_assigned_worker',
        completesWorkflow: false,
        evidenceType: 'STAFF_POLICY_ACKNOWLEDGEMENT',
      },
    ],
    documents: [
      {
        id: 'client-service-agreement',
        stepId: 'client-document-signature-or-manual',
        required: true,
        documentType: 'service_agreement_or_consent',
        fallbackEvidenceType: 'CLIENT_DOCUMENT_SIGNATURE_OR_MANUAL_EVIDENCE',
      },
    ],
    linkedRecords: {
      'document-source-evidence': [
        'service_agreements',
        'consents',
        'workplace_policies',
        'documents',
      ],
      'client-document-signature-or-manual': [
        'service_agreements',
        'consents',
        'consent_register',
        'documents',
      ],
      'staff-policy-acknowledgement': ['workplace_policies', 'policy_acknowledgements'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.documentEvidenceRequired',
          description: 'Document signing/acknowledgement must create durable evidence.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.clientPortalDocuments',
          description: 'Use portal document visibility where the provider offers a portal.',
        },
      ],
      optional: [
        {
          key: 'documents.localSigningTemplates',
          description: 'Providers can add local templates without weakening evidence requirements.',
        },
      ],
    },
    legalScope: 'Consents, service agreements, policy acknowledgements, and document visibility',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const DOCUMENT_CONSENT_AGREEMENT_WORKFLOW = DOCUMENT_CONSENT_AGREEMENT_MANIFEST.definition;

const WHISTLEBLOWER_CLOSEOUT_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'whistleblower.intake-review-closeout.v1',
  title: 'Whistleblower protected intake, review, and closeout workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'whistleblower.report_received',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/compliance/whistleblower', criticality: 'mandatory' },
    { path: '/compliance/governance', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'protected-intake-evidence',
      label: 'Protected intake evidence',
      actionType: 'record_evidence',
      responsibility: 'quality_governance',
      capabilityId: 'incident.review',
      preferredSurfaces: ['web'],
      evidenceType: 'WHISTLEBLOWER_PROTECTED_INTAKE_EVIDENCE',
      relatedResourceType: 'whistleblower_report',
      legalCritical: true,
    },
    {
      id: 'protected-recipient-review',
      label: 'Protected recipient review',
      actionType: 'create_task',
      responsibility: 'quality_governance',
      capabilityId: 'incident.review',
      preferredSurfaces: ['web'],
      evidenceType: 'WHISTLEBLOWER_PROTECTED_RECIPIENT_REVIEW',
      relatedResourceType: 'whistleblower_report',
      legalCritical: true,
    },
    {
      id: 'whistleblower-closeout-evidence',
      label: 'Protected closeout evidence',
      actionType: 'record_evidence',
      responsibility: 'tenant_accountable_person',
      capabilityId: 'tenant.configure',
      preferredSurfaces: ['web'],
      evidenceType: 'WHISTLEBLOWER_CLOSEOUT_EVIDENCE',
      relatedResourceType: 'whistleblower_report',
      legalCritical: true,
    },
  ],
};

export const WHISTLEBLOWER_CLOSEOUT_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: WHISTLEBLOWER_CLOSEOUT_WORKFLOW_DEFINITION,
    sourceTable: 'whistleblower_reports',
    sourceResourceType: 'whistleblower_report',
    requiredFields: ['tenant_id', 'report_id', 'received_at', 'is_anonymous'],
    idempotencyKey: 'tenant_id:report_id:whistleblower.report_received',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'whistleblower_intake_review_closeout', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'incident.review',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'incident.create',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.rights.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('quality_governance', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('tenant_accountable_person', 'fallback_to_accountable_actor'),
    ],
    approvals: [
      {
        id: 'whistleblower-closeout-approval',
        stepId: 'whistleblower-closeout-evidence',
        responsibility: 'tenant_accountable_person',
        providerConfigured: true,
      },
    ],
    linkedRecords: {
      'protected-intake-evidence': ['whistleblower_reports'],
      'protected-recipient-review': ['whistleblower_reports', 'whistleblower_recipients'],
      'whistleblower-closeout-evidence': ['whistleblower_policies', 'audit_logs'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.whistleblowerProtection',
          description:
            'Protected intake and access control must not be relaxed by workflow settings.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'responsibility.protectedRecipient',
          description: 'Providers should nominate protected recipient routing where applicable.',
        },
      ],
      optional: [
        {
          key: 'whistleblower.localBoardEscalation',
          description:
            'Providers may add local escalation evidence without exposing protected details.',
        },
      ],
    },
    legalScope: 'Whistleblower reports, protected recipients, policy evidence, and closeout',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const WHISTLEBLOWER_CLOSEOUT_WORKFLOW = WHISTLEBLOWER_CLOSEOUT_MANIFEST.definition;

const INVOICE_ISSUE_REVIEW_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'finance.invoice-issue-review.v1',
  title: 'Invoice issue, portal/manual delivery, and dispute review workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'finance.invoice_ready',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/finance', criticality: 'mandatory' },
    { path: '/finance/invoices', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'invoice-readiness-review',
      label: 'Invoice readiness review',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      evidenceType: 'INVOICE_READINESS_REVIEW_RECORDED',
      relatedResourceType: 'invoice',
      legalCritical: true,
    },
    {
      id: 'invoice-client-delivery',
      label: 'Invoice client delivery or manual evidence',
      actionType: 'surface_projection',
      responsibility: 'manual_communication',
      capabilityId: 'portal.financial.view',
      preferredSurfaces: ['client-portal'],
      fallback: {
        surface: 'web',
        actionType: 'manual_evidence',
        responsibility: 'manual_communication',
        reason: 'client portal unavailable; invoice delivery evidence is recorded manually',
      },
      evidenceType: 'INVOICE_DELIVERY_OR_MANUAL_COMMUNICATION',
      relatedResourceType: 'invoice',
      legalCritical: true,
    },
    {
      id: 'invoice-dispute-review',
      label: 'Invoice dispute review',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      condition: 'invoiceDisputeRaised',
      evidenceType: 'INVOICE_DISPUTE_REVIEW_RECORDED',
      relatedResourceType: 'invoice',
      legalCritical: true,
    },
  ],
};

export const INVOICE_ISSUE_REVIEW_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: INVOICE_ISSUE_REVIEW_WORKFLOW_DEFINITION,
    sourceTable: 'invoices',
    sourceResourceType: 'invoice',
    requiredFields: ['tenant_id', 'client_id', 'invoice_id', 'invoice_status'],
    idempotencyKey: 'tenant_id:invoice_id:invoice_status:finance.invoice_ready',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'finance_invoice_issue_review', tier: 'mandatory' },
    surfaces: FINANCE_WEB_PORTAL_SURFACES,
    responsibilities: [
      responsibilityManifestContract('finance_review', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('manual_communication', 'manual_evidence_required'),
    ],
    notifications: [
      {
        id: 'invoice-client-delivery',
        stepId: 'invoice-client-delivery',
        recipientResponsibility: 'manual_communication',
        surface: 'client-portal',
        acknowledgementCompletesWorkflow: false,
        fallbackStepId: 'invoice-client-delivery',
      },
    ],
    approvals: [
      {
        id: 'invoice-readiness-approval',
        stepId: 'invoice-readiness-review',
        responsibility: 'finance_review',
        providerConfigured: true,
      },
    ],
    linkedRecords: {
      'invoice-readiness-review': ['invoices', 'claims'],
      'invoice-client-delivery': ['invoices', 'client_contacts'],
      'invoice-dispute-review': ['invoices', 'audit_logs'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.invoiceDeliveryEvidence',
          description: 'Invoice issue and delivery must create evidence before closeout.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.clientPortalInvoices',
          description: 'Use portal invoice visibility where the provider offers a portal.',
        },
      ],
      optional: [
        {
          key: 'finance.localDisputeApproval',
          description: 'Providers may add local dispute approval steps without changing amounts.',
        },
      ],
    },
    legalScope: 'Invoices, client/family delivery evidence, disputes, and finance closeout',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const INVOICE_ISSUE_REVIEW_WORKFLOW = INVOICE_ISSUE_REVIEW_MANIFEST.definition;

const CLIENT_FUNDING_TRANSITION_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'client.funding-transition.v1',
  title: 'Client funding transition, successor care plan, finance, and apply workflow',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'client.funding_transition_recorded',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/home-care', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'funding-blocker-resolution',
      label: 'Resolve funding change blockers',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'CLIENT_FUNDING_TRANSITION_BLOCKERS_RESOLVED',
      relatedResourceType: 'client_funding_transition',
      legalCritical: true,
    },
    {
      id: 'funding-finance-reconciliation',
      label: 'Reconcile affected delivered services',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      condition: 'financeReconciliationRequired',
      evidenceType: 'CLIENT_FUNDING_FINANCE_RECONCILIATION',
      relatedResourceType: 'client_funding_transition',
      legalCritical: true,
    },
    {
      id: 'funding-independent-approval',
      label: 'Approve high-risk funding change',
      actionType: 'create_task',
      responsibility: 'finance_review',
      capabilityId: 'sah.statement.issue',
      preferredSurfaces: ['web'],
      condition: 'independentApprovalRequired',
      evidenceType: 'CLIENT_FUNDING_TRANSITION_APPROVED',
      relatedResourceType: 'client_funding_transition',
      legalCritical: true,
    },
    {
      id: 'funding-apply-when-due',
      label: 'Apply funding change when effective',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'CLIENT_FUNDING_TRANSITION_APPLIED',
      relatedResourceType: 'client_funding_transition',
      legalCritical: true,
    },
    {
      id: 'funding-successor-plan-readiness',
      label: 'Complete successor care plan and program readiness',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'successorPlanOrSahReadinessRequired',
      evidenceType: 'CLIENT_FUNDING_SUCCESSOR_PLAN_READY',
      relatedResourceType: 'care_plan',
      legalCritical: true,
    },
  ],
};

export const CLIENT_FUNDING_TRANSITION_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: CLIENT_FUNDING_TRANSITION_WORKFLOW_DEFINITION,
    sourceTable: 'client_funding_transitions',
    sourceResourceType: 'client_funding_transition',
    requiredFields: [
      'tenant_id',
      'client_id',
      'transition_id',
      'operation',
      'effective_date',
      'status',
      'blockers',
    ],
    idempotencyKey: 'tenant_id:transition_id:status:client.funding_transition_recorded',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'client_funding_transition', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'client.care.workspace',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'client.care.workspace',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.financial.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('finance_review', 'fallback_to_accountable_actor'),
    ],
    approvals: [
      {
        id: 'funding-independent-approval',
        stepId: 'funding-independent-approval',
        responsibility: 'finance_review',
        providerConfigured: true,
      },
    ],
    linkedRecords: {
      'funding-blocker-resolution': [
        'client_funding_transitions',
        'care_plans',
        'care_plan_service_inclusions',
      ],
      'funding-finance-reconciliation': [
        'client_funding_transitions',
        'services_delivered',
        'claims',
        'invoices',
      ],
      'funding-independent-approval': [
        'client_funding_transitions',
        'client_funding_transition_events',
      ],
      'funding-apply-when-due': ['client_funding_transitions', 'program_enrollments'],
      'funding-successor-plan-readiness': [
        'client_funding_transitions',
        'client_funding_contexts',
        'care_plans',
      ],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.fundingTransitionHistoricalMoney',
          description: 'Historical payer and posted-money evidence must remain immutable.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'responsibility.fundingFinanceReview',
          description: 'Configure a finance reviewer for payer removals and replacements.',
        },
      ],
      optional: [
        {
          key: 'fundingTransition.localApprovalEvidence',
          description: 'Providers may record additional local approval evidence.',
        },
      ],
    },
    legalScope:
      'Client payer changes, care-plan allocations, finance evidence, and effective-date apply',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const CLIENT_FUNDING_TRANSITION_WORKFLOW = CLIENT_FUNDING_TRANSITION_MANIFEST.definition;

const SERVICE_EXECUTION_RECONCILIATION_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'service-execution.reconciliation-review.v1',
  title: 'Service-execution reconciliation review',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'service_execution.reconciliation_opened',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/tasks', criticality: 'mandatory' },
    { path: '/clients', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'reconciliation-review',
      label: 'Review and resolve preserved worker service evidence',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'service.execution.reconcile',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'SERVICE_DELIVERY_RECONCILIATION_RESOLVED',
      relatedResourceType: 'service_delivery_reconciliation_case',
      legalCritical: true,
    },
  ],
};

export const SERVICE_EXECUTION_RECONCILIATION_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: SERVICE_EXECUTION_RECONCILIATION_WORKFLOW_DEFINITION,
    sourceTable: 'service_delivery_reconciliation_cases',
    sourceResourceType: 'service_delivery_reconciliation_case',
    requiredFields: [
      'tenant_id',
      'reconciliation_case_id',
      'occurrence_id',
      'reason_code',
      'status',
    ],
    idempotencyKey: 'tenant_id:reconciliation_case_id:service_execution.reconciliation_opened',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'service_execution_reconciliation', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'service.execution.reconcile',
        required: true,
        degradedUatCaseId: 'service-execution-reconciliation-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.shift.execute',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'service-execution-reconciliation-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.care.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'service-execution-reconciliation-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
    ],
    linkedRecords: {
      'reconciliation-review': [
        'service_delivery_reconciliation_cases',
        'service_delivery_execution_snapshots',
        'service_delivery_evidence_bundles',
      ],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'responsibility.serviceExecutionReconciliation',
          description:
            'A coordinator, team leader, case manager, facility manager or tenant administrator must own rejected worker evidence.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'surface.careWorkerApp',
          description:
            'Workers should retain the original offline submission until the coordinator resolves its preserved case.',
        },
      ],
      optional: [],
    },
    legalScope:
      'Preservation, accountable review and evidence-bound resolution of rejected or offline service-execution submissions',
    uatCases: [
      {
        id: 'service-execution-reconciliation-full',
        title: 'Rejected worker evidence reaches accountable review',
        path: 'Submit stale or conflicting worker evidence, process the outbox event, open the generated task and resolve the preserved case.',
        expected:
          'One tenant-scoped coordinator task opens the controlled queue and closes only after the case has accepted, superseding or rejection evidence.',
        degraded: false,
      },
      {
        id: 'service-execution-reconciliation-mobile-disabled',
        title: 'Manual worker submission with mobile unavailable',
        path: 'Record a rejected worker submission through the authorised web fallback.',
        expected:
          'The same preserved case and accountable web reconciliation path is used; no evidence is discarded or marked delivered.',
        degraded: true,
      },
      {
        id: 'service-execution-reconciliation-portal-disabled',
        title: 'Portal unavailable during internal reconciliation',
        path: 'Resolve a preserved worker submission while the client portal is disabled.',
        expected:
          'Internal reconciliation remains web-owned and does not depend on portal acknowledgement.',
        degraded: true,
      },
      {
        id: 'service-execution-reconciliation-web-only',
        title: 'Small provider accountable-role fallback',
        path: 'Run without a dedicated coordinator but with a facility or tenant accountable manager.',
        expected:
          'The task falls back to the named accountable role; with no eligible actor the event fails closed and remains operationally visible.',
        degraded: true,
      },
    ],
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const SERVICE_EXECUTION_RECONCILIATION_WORKFLOW =
  SERVICE_EXECUTION_RECONCILIATION_MANIFEST.definition;

const GOVERNMENT_INTEGRATION_FOLLOW_THROUGH_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'government-integration.follow-through.v1',
  title: 'HI/B2G accountable integration follow-through',
  program: 'CROSS_PROGRAM',
  triggerEvent: 'hi.patient.demographic_review',
  supportedOrgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
  moduleRequirements: [
    { path: '/tasks', criticality: 'mandatory' },
    { path: '/settings/integrations', criticality: 'strongly_recommended' },
  ],
  steps: [
    {
      id: 'gov-integration-review',
      label: 'Complete accountable HI/B2G review or reconciliation',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'always',
      evidenceType: 'GOVERNMENT_INTEGRATION_DOMAIN_SUCCESS',
      relatedResourceType: 'gov_integration_follow_through_outbox',
      legalCritical: true,
    },
  ],
};

export const GOVERNMENT_INTEGRATION_FOLLOW_THROUGH_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: GOVERNMENT_INTEGRATION_FOLLOW_THROUGH_WORKFLOW_DEFINITION,
    sourceTable: 'gov_integration_follow_through_outbox',
    sourceResourceType: 'gov_integration_follow_through',
    requiredFields: ['tenant_id', 'event_id', 'review_type', 'dedupe_key', 'source_version'],
    idempotencyKey: 'tenant_id:dedupe_key:review_type',
    programApplicability: {
      fundingPrograms: ['SAH', 'RAC', 'MIXED', 'PRIVATE'],
      orgTypes: ['HOME_CARE', 'RESIDENTIAL', 'MIXED'],
    },
    workflowBlock: { id: 'government_integration_follow_through', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'client.care.workspace',
        required: true,
        degradedUatCaseId: 'government-integration-follow-through-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'frontline.shift.execute',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'government-integration-follow-through-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.care.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'government-integration-follow-through-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
    ],
    linkedRecords: {
      'gov-integration-review': [
        'gov_integration_follow_through_outbox',
        'hi_patient_identity_duplicate_reviews',
        'b2g_import_review_candidates',
        'b2g_report_submissions',
      ],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'responsibility.governmentIntegrationFollowThrough',
          description:
            'Qualified clinical, intake or compliance operators must own HI/B2G exception follow-through; privileged configuration admins are not the operational fallback.',
        },
      ],
      stronglyRecommended: [],
      optional: [],
    },
    legalScope:
      'Accountable HI/B2G duplicate, import, reporting and connection follow-through without notification-ack domain closure',
    uatCases: [
      {
        id: 'government-integration-follow-through-full',
        title: 'Rejected RN report and HI duplicate each yield one scoped task',
        path: 'Emit P02 events, process notification_events, open tasks and close only on domain success.',
        expected:
          'Deduped tasks survive notification failure and deep-link to correcting workflows.',
        degraded: false,
      },
      {
        id: 'government-integration-follow-through-mobile-disabled',
        title: 'HI/B2G follow-through with mobile unavailable',
        path: 'Process a P02 notification event while the care-worker app surface is disabled.',
        expected:
          'Accountable web tasks and deep links remain the sole correction path; no new mobile routes are introduced.',
        degraded: true,
      },
      {
        id: 'government-integration-follow-through-portal-disabled',
        title: 'Portal unavailable during HI/B2G reconciliation',
        path: 'Resolve a rejected RN or QI report while the client portal is disabled.',
        expected:
          'Internal compliance operators reconcile through web reporting routes without portal acknowledgement.',
        degraded: true,
      },
      {
        id: 'government-integration-follow-through-web-only',
        title: 'Small provider unassigned-queue fallback',
        path: 'Emit a P02 event when no qualified operator is active for the tenant.',
        expected:
          'One visible unassigned task is created; privileged configuration admins are not auto-assigned.',
        degraded: true,
      },
    ],
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const GOVERNMENT_INTEGRATION_FOLLOW_THROUGH_WORKFLOW =
  GOVERNMENT_INTEGRATION_FOLLOW_THROUGH_MANIFEST.definition;

const SAH_EOL_ACTIVATION_WORKFLOW_DEFINITION: WorkflowDefinition = {
  id: 'sah.eol-activation.v1',
  title: 'SAH End-of-Life plan-first activation orchestration',
  program: 'SAH',
  triggerEvent: 'sah_eol.apply_due',
  supportedOrgTypes: ['HOME_CARE', 'MIXED'],
  moduleRequirements: [
    { path: '/clients', criticality: 'mandatory' },
    { path: '/care-plans', criticality: 'mandatory' },
    { path: '/home-care', criticality: 'mandatory' },
  ],
  steps: [
    {
      id: 'eol-apply-when-due',
      label: 'Apply the sealed End-of-Life change set through the atomic boundary',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      evidenceType: 'SAH_EOL_APPLIED',
      relatedResourceType: 'sah_eol_change_set',
      legalCritical: true,
    },
    {
      id: 'eol-readiness-drift-resolution',
      label: 'Resolve readiness drift or blockers on a sealed proposal',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'readinessDriftOrBlocked',
      evidenceType: 'SAH_EOL_READINESS_RESTORED',
      relatedResourceType: 'sah_eol_change_set',
      legalCritical: true,
    },
    {
      id: 'eol-independent-activation',
      label: 'Confirm the independent activator or governed exception',
      actionType: 'create_task',
      responsibility: 'clinical_review',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'independentActivatorRequired',
      evidenceType: 'SAH_EOL_INDEPENDENT_ACTIVATION_CONFIRMED',
      relatedResourceType: 'sah_eol_change_set',
      legalCritical: true,
    },
    {
      id: 'eol-closeout-follow-through',
      label: 'Complete death/exit/expiry closeout and claim-deadline tasks',
      actionType: 'create_task',
      responsibility: 'care_coordination',
      capabilityId: 'client.care.workspace',
      preferredSurfaces: ['web'],
      condition: 'closeoutRequired',
      evidenceType: 'SAH_EOL_CLOSEOUT_RECORDED',
      relatedResourceType: 'sah_eol_change_set',
      legalCritical: true,
    },
  ],
};

export const SAH_EOL_ACTIVATION_MANIFEST = {
  ...makeManifestOnlyWorkflow({
    definition: SAH_EOL_ACTIVATION_WORKFLOW_DEFINITION,
    sourceTable: 'sah_eol_change_sets',
    sourceResourceType: 'sah_eol_change_set',
    requiredFields: [
      'tenant_id',
      'client_id',
      'change_set_id',
      'event_kind',
      'effective_date',
      'status',
    ],
    idempotencyKey: 'tenant_id:change_set_id:event_kind:sweep_date',
    programApplicability: {
      fundingPrograms: ['SAH'],
      orgTypes: ['HOME_CARE', 'MIXED'],
    },
    workflowBlock: { id: 'sah_eol_activation', tier: 'mandatory' },
    surfaces: [
      {
        surface: 'web',
        capabilityId: 'client.care.workspace',
        required: true,
        degradedUatCaseId: 'platform-web-only',
      },
      {
        surface: 'care-worker-app',
        capabilityId: 'client.care.workspace',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-mobile-disabled',
      },
      {
        surface: 'client-portal',
        capabilityId: 'portal.financial.view',
        required: false,
        fallbackSurface: 'web',
        fallbackAction: 'manual_evidence',
        degradedUatCaseId: 'platform-portal-disabled',
      },
    ],
    responsibilities: [
      responsibilityManifestContract('care_coordination', 'fallback_to_accountable_actor'),
      responsibilityManifestContract('clinical_review', 'fail_closed'),
    ],
    approvals: [
      {
        id: 'eol-independent-activation',
        stepId: 'eol-independent-activation',
        responsibility: 'clinical_review',
        providerConfigured: true,
      },
    ],
    linkedRecords: {
      'eol-apply-when-due': [
        'sah_eol_change_sets',
        'sah_eol_readiness_seals',
        'sah_eol_change_set_commands',
      ],
      'eol-readiness-drift-resolution': [
        'sah_eol_change_sets',
        'sah_eol_readiness_seals',
        'sah_eol_outbox_events',
      ],
      'eol-independent-activation': ['sah_eol_change_sets', 'sah_eol_apply_exception_grants'],
      'eol-closeout-follow-through': ['sah_eol_change_sets', 'sah_pathway_funding_envelopes'],
    },
    providerConfiguration: {
      mandatory: [
        {
          key: 'rules.sahEolNeverAutoApply',
          description:
            'The due sweep creates work only; a human applies through the single atomic boundary.',
        },
      ],
      stronglyRecommended: [
        {
          key: 'responsibility.sahEolIndependentActivator',
          description:
            'Configure an independent activator; small providers use the governed apply exception.',
        },
      ],
      optional: [
        {
          key: 'sahEol.localActivationEvidence',
          description: 'Providers may record additional local activation evidence.',
        },
      ],
    },
    legalScope:
      'End-of-Life change-set activation due dates, readiness drift, independent activation and closeout deadlines',
    uatCases: COMMON_PLATFORM_UAT_CASES,
  }),
  status: 'runtime_activation_ready',
} as const satisfies WorkflowManifest;

export const SAH_EOL_ACTIVATION_WORKFLOW = SAH_EOL_ACTIVATION_MANIFEST.definition;

/**
 * Thin, dispatcher-owned routes for assessment follow-through. These are not
 * standalone workflow products: each route hands an accepted, typed intent to
 * an existing domain owner. The owner remains responsible for its canonical
 * record, workflow instance, task idempotency and completion evidence.
 */
export type AssessmentFollowThroughIntentType =
  | 'assessment.follow_on_required'
  | 'clinical.review_required'
  | 'care_plan.review_required'
  | 'clinical.referral_required'
  | 'client.profile_review_required'
  | 'client.access_review_required'
  | 'restrictive_practice.review_required'
  | 'home_safety.review_required'
  | 'service.inclusion_review_required'
  | 'sah.support_plan_review_required'
  | 'sah.budget_review_required'
  | 'sah.classification_review_required'
  | 'sah.restorative_review_required'
  | 'sah.at_hm_review_required'
  | 'sah.end_of_life_review_required'
  | 'rac.admission_review_required'
  | 'rac.clinical_review_required'
  | 'complaint.follow_up_required'
  | 'complaint.access_support_required';

export type AssessmentFollowThroughOwnerAdapter =
  | 'assessment_follow_on'
  | 'clinical_review'
  | 'care_plan_change'
  | 'third_party_referral'
  | 'client_profile'
  | 'client_access'
  | 'restrictive_practice_review'
  | 'home_safety'
  | 'service_inclusion'
  | 'sah_support_plan'
  | 'sah_budget'
  | 'sah_classification'
  | 'sah_restorative_pathway'
  | 'sah_athm'
  | 'sah_end_of_life'
  | 'rac_admission'
  | 'rac_clinical_review'
  | 'complaint'
  | 'complaint_access';

export interface AssessmentFollowThroughIntentEnvelope {
  source_assessment_id: string;
  mapping_id: string;
  mapping_hash: string;
  intent_type: AssessmentFollowThroughIntentType;
  /** Semantic domain-owner capability; not a UI/surface capability id. */
  owner_capability: string;
  idempotency_key: string;
  payload: Readonly<Record<string, unknown>>;
  care_plan_action_due_at: string;
  repeat_assessment_due_date?: string | null;
}

export interface AssessmentFollowThroughAdapterRoute {
  intentType: AssessmentFollowThroughIntentType;
  requiredSurfaceCapability: WorkflowCapabilityId;
  ownerAdapter: AssessmentFollowThroughOwnerAdapter;
  programs: readonly ('SAH' | 'RAC' | 'CROSS_PROGRAM')[];
  dispatchMode: 'OWNER_REVIEW';
}

export interface AssessmentFollowThroughAdapterManifest {
  id: string;
  program: 'SAH' | 'RAC';
  triggerEvent: 'structured_care_planner.owner_action_ready';
  sourceTable: 'structured_care_plan_owner_action';
  activationOwner: WorkflowManifestActivationOwner;
  directBrowserTaskWritesAllowed: false;
  directBusinessRecordMutationAllowed: false;
  routes: readonly AssessmentFollowThroughAdapterRoute[];
}

const CROSS_PROGRAM_ASSESSMENT_FOLLOW_THROUGH_ROUTES = [
  {
    intentType: 'assessment.follow_on_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'assessment_follow_on',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'clinical.review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'clinical_review',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'care_plan.review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'care_plan_change',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'clinical.referral_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'third_party_referral',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'client.profile_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'client_profile',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'client.access_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'client_access',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'restrictive_practice.review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'restrictive_practice_review',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'complaint.follow_up_required',
    requiredSurfaceCapability: 'complaint.create',
    ownerAdapter: 'complaint',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'complaint.access_support_required',
    requiredSurfaceCapability: 'complaint.create',
    ownerAdapter: 'complaint_access',
    programs: ['SAH', 'RAC', 'CROSS_PROGRAM'],
    dispatchMode: 'OWNER_REVIEW',
  },
] as const satisfies readonly AssessmentFollowThroughAdapterRoute[];

const SAH_ONLY_ASSESSMENT_FOLLOW_THROUGH_ROUTES = [
  {
    intentType: 'home_safety.review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'home_safety',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'service.inclusion_review_required',
    requiredSurfaceCapability: 'service.delivery.manage',
    ownerAdapter: 'service_inclusion',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'sah.support_plan_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'sah_support_plan',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'sah.budget_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'sah_budget',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'sah.classification_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'sah_classification',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'sah.restorative_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'sah_restorative_pathway',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'sah.at_hm_review_required',
    requiredSurfaceCapability: 'sah.athm.manage',
    ownerAdapter: 'sah_athm',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'sah.end_of_life_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'sah_end_of_life',
    programs: ['SAH'],
    dispatchMode: 'OWNER_REVIEW',
  },
] as const satisfies readonly AssessmentFollowThroughAdapterRoute[];

const RAC_ONLY_ASSESSMENT_FOLLOW_THROUGH_ROUTES = [
  {
    intentType: 'rac.admission_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'rac_admission',
    programs: ['RAC'],
    dispatchMode: 'OWNER_REVIEW',
  },
  {
    intentType: 'rac.clinical_review_required',
    requiredSurfaceCapability: 'client.care.workspace',
    ownerAdapter: 'rac_clinical_review',
    programs: ['RAC'],
    dispatchMode: 'OWNER_REVIEW',
  },
] as const satisfies readonly AssessmentFollowThroughAdapterRoute[];

export const SAH_ASSESSMENT_FOLLOW_THROUGH_ADAPTER_MANIFEST = {
  id: 'sah-assessment-follow-through-adapter-v1',
  program: 'SAH',
  triggerEvent: 'structured_care_planner.owner_action_ready',
  sourceTable: 'structured_care_plan_owner_action',
  activationOwner: 'notification_events_dispatcher',
  directBrowserTaskWritesAllowed: false,
  directBusinessRecordMutationAllowed: false,
  routes: [
    ...CROSS_PROGRAM_ASSESSMENT_FOLLOW_THROUGH_ROUTES,
    ...SAH_ONLY_ASSESSMENT_FOLLOW_THROUGH_ROUTES,
  ],
} as const satisfies AssessmentFollowThroughAdapterManifest;

export const RAC_ASSESSMENT_FOLLOW_THROUGH_ADAPTER_MANIFEST = {
  id: 'rac-assessment-follow-through-adapter-v1',
  program: 'RAC',
  triggerEvent: 'structured_care_planner.owner_action_ready',
  sourceTable: 'structured_care_plan_owner_action',
  activationOwner: 'notification_events_dispatcher',
  directBrowserTaskWritesAllowed: false,
  directBusinessRecordMutationAllowed: false,
  routes: [
    ...CROSS_PROGRAM_ASSESSMENT_FOLLOW_THROUGH_ROUTES,
    ...RAC_ONLY_ASSESSMENT_FOLLOW_THROUGH_ROUTES,
  ],
} as const satisfies AssessmentFollowThroughAdapterManifest;

export const ASSESSMENT_FOLLOW_THROUGH_ADAPTER_MANIFESTS = [
  SAH_ASSESSMENT_FOLLOW_THROUGH_ADAPTER_MANIFEST,
  RAC_ASSESSMENT_FOLLOW_THROUGH_ADAPTER_MANIFEST,
] as const satisfies readonly AssessmentFollowThroughAdapterManifest[];

export function resolveAssessmentFollowThroughAdapterRoute(input: {
  program: 'SAH' | 'RAC' | 'CROSS_PROGRAM';
  intentType: AssessmentFollowThroughIntentType;
  ownerCapability: string;
}): AssessmentFollowThroughAdapterRoute | null {
  const routes: readonly AssessmentFollowThroughAdapterRoute[] =
    input.program === 'SAH'
      ? SAH_ASSESSMENT_FOLLOW_THROUGH_ADAPTER_MANIFEST.routes
      : input.program === 'RAC'
        ? RAC_ASSESSMENT_FOLLOW_THROUGH_ADAPTER_MANIFEST.routes
        : CROSS_PROGRAM_ASSESSMENT_FOLLOW_THROUGH_ROUTES;
  const route = routes.find((candidate) => candidate.intentType === input.intentType) ?? null;
  return route && assessmentFollowThroughOwnerCapabilityIsValid(input) ? route : null;
}

export function assessmentFollowThroughOwnerCapabilityIsValid(input: {
  intentType: AssessmentFollowThroughIntentType;
  ownerCapability: string;
}): boolean {
  const capability = input.ownerCapability;
  switch (input.intentType) {
    case 'assessment.follow_on_required':
      return capability === 'clinical.assessment.follow_on';
    case 'clinical.review_required':
      return (
        [
          'clinical.medication.reconciliation',
          'clinical.advance-care-plan.review',
          'clinical.wound.review',
          'clinical.allergy.review',
          'clinical.incident.review',
          'clinical.representative.review',
          'clinical.funding.review',
          'clinical.other.review',
        ].includes(capability) ||
        /^canonical\.(care_plan|client_profile|medication|allergy|wound|incident|representative|advance_care_plan|funding|other)\.review$/.test(
          capability,
        )
      );
    case 'care_plan.review_required':
      return /^care_plan\.[a-z0-9_]+\.review$/.test(capability);
    case 'clinical.referral_required':
      return capability === 'clinical.third_party_referral';
    case 'client.profile_review_required':
      return capability === 'client.profile.review';
    case 'client.access_review_required':
      return capability === 'client.access.review';
    case 'restrictive_practice.review_required':
      return capability === 'clinical.restrictive-practice.review';
    case 'home_safety.review_required':
      return capability === 'clinical.home-safety.review';
    case 'service.inclusion_review_required':
      return capability === 'care_plan.service_inclusion.review';
    case 'sah.support_plan_review_required':
      return capability === 'sah.support_plan.review';
    case 'sah.budget_review_required':
      return capability === 'sah.budget.review';
    case 'sah.classification_review_required':
      return capability === 'sah.classification.review';
    case 'sah.restorative_review_required':
      return capability === 'sah.restorative.review';
    case 'sah.at_hm_review_required':
      return capability === 'sah.at-hm.review';
    case 'sah.end_of_life_review_required':
      return capability === 'sah.end-of-life.review';
    case 'rac.admission_review_required':
      return capability === 'rac.admission.review';
    case 'rac.clinical_review_required':
      return capability === 'rac.clinical.review';
    case 'complaint.follow_up_required':
      return capability === 'quality.complaint.follow-up';
    case 'complaint.access_support_required':
      return capability === 'quality.complaint-access.support';
  }
}

export const WORKFLOW_MANIFESTS = [
  SAH_REASSESSMENT_CARE_CONTEXT_MANIFEST,
  SAH_END_OF_LIFE_PATHWAY_MANIFEST,
  SAH_VACANCY_FIRST_ACCEPTANCE_MANIFEST,
  SAH_MONTHLY_STATEMENT_ISSUE_MANIFEST,
  SAH_CLAIM_BATCH_LIFECYCLE_MANIFEST,
  RAC_ADMISSION_GOVERNANCE_MANIFEST,
  RAC_DAILY_CARE_EXCEPTION_MANIFEST,
  GOVERNANCE_EVENT_CLOSEOUT_MANIFEST,
  CLINICAL_REVIEW_MANIFEST,
  CARE_PLAN_CHANGE_MANIFEST,
  RESTRICTIVE_PRACTICE_REVIEW_MANIFEST,
  WORKFORCE_ROSTER_PAYROLL_MANIFEST,
  WORKER_COMPLIANCE_ONBOARDING_MANIFEST,
  WORKER_COMPLIANCE_RENEWAL_BOOKING_MANIFEST,
  QUALITY_INDICATOR_GOVERNANCE_MANIFEST,
  DOCUMENT_CONSENT_AGREEMENT_MANIFEST,
  WHISTLEBLOWER_CLOSEOUT_MANIFEST,
  INVOICE_ISSUE_REVIEW_MANIFEST,
  CLIENT_FUNDING_TRANSITION_MANIFEST,
  SERVICE_EXECUTION_RECONCILIATION_MANIFEST,
  GOVERNMENT_INTEGRATION_FOLLOW_THROUGH_MANIFEST,
  SAH_EOL_ACTIVATION_MANIFEST,
] as const satisfies readonly WorkflowManifest[];

function hasRole(context: WorkflowCompileContext, role: UserRole): boolean {
  return context.activeRoles.includes(role);
}

function resolveResponsibility(
  responsibility: WorkflowResponsibility,
  context: WorkflowCompileContext,
) {
  const rule = RESPONSIBILITY_RULES[responsibility];
  const assignedRole = rule.cascade.find((role) => hasRole(context, role)) ?? null;
  const qualifiedCompleterRoles = rule.allowedCompleters.filter((role) => hasRole(context, role));
  return { assignedRole, qualifiedCompleterRoles, rule };
}

/**
 * D2 external clinical-review satisfier gate. Returns the administrative owner
 * when — and ONLY when — the full gate holds for the qualified-clinical-review
 * step. Called from inside the professional-scope block (which by construction
 * only fires when zero CLINICAL_LEAD/REGISTERED_NURSE roles are active), so an
 * in-house clinician always wins and other professional-scope steps are never
 * affected. Returns null otherwise → the caller keeps the fail-closed
 * NO_QUALIFIED_ACTOR block.
 */
function resolveExternalClinicalReviewSatisfier(
  definition: WorkflowDefinition,
  step: WorkflowStepDefinition,
  context: WorkflowCompileContext,
): { administrativeOwner: UserRole } | null {
  const cfg = context.externalClinicalReview;
  if (
    definition.id === 'clinical.review-continuity.v1' &&
    step.id === 'qualified-clinical-review' &&
    step.professionalScope === true &&
    context.orgType === 'HOME_CARE' &&
    cfg?.enabled === true &&
    cfg.providerId != null &&
    cfg.providerStatus === 'ACTIVE' &&
    cfg.providerType != null &&
    (EXTERNAL_CLINICAL_REVIEW_PROVIDER_TYPES as readonly string[]).includes(cfg.providerType)
  ) {
    const administrativeOwner = resolveResponsibility('care_coordination', context).assignedRole;
    if (!administrativeOwner) return null;
    return { administrativeOwner };
  }
  return null;
}

function hasSurface(context: WorkflowCompileContext, surface: WorkflowSurface): boolean {
  if (surface === 'web') return context.availableSurfaces.web !== false;
  return context.availableSurfaces[surface] === true;
}

function hasModule(context: WorkflowCompileContext, requiredPath: string): boolean {
  if (context.enabledModules === null) return true;
  return context.enabledModules.some(
    (modulePath) =>
      modulePath === requiredPath ||
      modulePath.startsWith(`${requiredPath}/`) ||
      requiredPath.startsWith(`${modulePath}/`),
  );
}

function conditionMatches(condition: WorkflowConditionKey | undefined, facts: WorkflowEventFacts) {
  switch (condition ?? 'always') {
    case 'always':
      return true;
    case 'scheduledWorkersAffected':
      return facts.scheduledWorkersAffected === true;
    case 'clientVisibleChange':
      return facts.clientVisibleChange === true;
    case 'safetyCriticalCareContextChange':
      return facts.safetyCriticalCareContextChange === true;
    case 'frontlineLodgementReceived':
      return facts.frontlineLodgementReceived === true;
    case 'portalLodgementReceived':
      return facts.portalLodgementReceived === true;
    case 'managerConfirmationConfigured':
      return facts.managerConfirmationConfigured === true;
    case 'clientCallConfigured':
      return facts.clientCallConfigured === true;
    case 'visitServiceEvidenceReady':
      return facts.visitServiceEvidenceReady === true;
    case 'statementClaimReadinessRequired':
      return facts.statementClaimReadinessRequired === true;
    case 'clientSignatureRequired':
      return facts.clientSignatureRequired === true;
    case 'staffPolicyAcknowledgementRequired':
      return facts.staffPolicyAcknowledgementRequired === true;
    case 'invoiceDisputeRaised':
      return facts.invoiceDisputeRaised === true;
    case 'workerEvidenceRequired':
      return facts.workerEvidenceRequired === true;
    case 'workforceComplianceReviewRequired':
      return facts.workforceComplianceReviewRequired === true;
    case 'onboardingDocumentAcknowledgementRequired':
      return facts.onboardingDocumentAcknowledgementRequired === true;
    case 'financeApprovalRequired':
      return facts.financeApprovalRequired === true;
    default:
      return false;
  }
}

function chooseSurface(
  step: WorkflowStepDefinition,
  context: WorkflowCompileContext,
): {
  surface: WorkflowSurface | null;
  fallback?: WorkflowCompileFallback;
  fallbackReason?: string;
  actionType: WorkflowActionType;
  responsibility?: WorkflowResponsibility;
} {
  const preferred = step.preferredSurfaces.find((surface) => hasSurface(context, surface));
  if (preferred) return { surface: preferred, actionType: step.actionType };

  if (step.fallback && hasSurface(context, step.fallback.surface)) {
    return {
      surface: step.fallback.surface,
      actionType: step.fallback.actionType,
      responsibility: step.fallback.responsibility,
      fallbackReason: step.fallback.reason,
      fallback: {
        code: 'SURFACE_UNAVAILABLE',
        message: step.fallback.reason,
        stepId: step.id,
        fromSurface: step.preferredSurfaces[0],
        toSurface: step.fallback.surface,
      },
    };
  }

  return { surface: null, actionType: step.actionType };
}

function compileStatus(
  blockers: readonly WorkflowCompileBlocker[],
  fallbacks: readonly WorkflowCompileFallback[],
): WorkflowCompileStatus {
  if (blockers.length > 0) return 'blocked';
  if (fallbacks.length > 0) return 'ready_with_fallbacks';
  return 'ready';
}

export function compileWorkflowDefinition(
  definition: WorkflowDefinition,
  context: WorkflowCompileContext,
  facts: WorkflowEventFacts = {},
): WorkflowCompileResult {
  const actions: WorkflowCompiledAction[] = [];
  const blockers: WorkflowCompileBlocker[] = [];
  const fallbacks: WorkflowCompileFallback[] = [];

  if (context.subscriptionActive !== true) {
    blockers.push({
      code: 'SUBSCRIPTION_INACTIVE',
      message: 'Tenant subscription is inactive; workflow activation must stop.',
    });
  }

  if (!definition.supportedOrgTypes.includes(context.orgType)) {
    blockers.push({
      code: 'ORG_TYPE_UNSUPPORTED',
      message: `${definition.id} does not support ${context.orgType}.`,
    });
  }

  for (const moduleRequirement of definition.moduleRequirements) {
    if (hasModule(context, moduleRequirement.path)) continue;
    if (moduleRequirement.criticality === 'mandatory') {
      blockers.push({
        code: 'MISSING_MANDATORY_MODULE',
        message: `Mandatory workflow module ${moduleRequirement.path} is unavailable.`,
        modulePath: moduleRequirement.path,
      });
    } else if (moduleRequirement.criticality === 'strongly_recommended') {
      fallbacks.push({
        code: 'STRONGLY_RECOMMENDED_MODULE_MISSING',
        message: `Workflow can run, but ${moduleRequirement.path} is unavailable and may require manual follow-through.`,
        modulePath: moduleRequirement.path,
      });
    }
  }

  for (const step of definition.steps) {
    if (!conditionMatches(step.condition, facts)) continue;

    const surfaceResolution = chooseSurface(step, context);
    const effectiveResponsibility = surfaceResolution.responsibility ?? step.responsibility;
    const { assignedRole, qualifiedCompleterRoles, rule } = resolveResponsibility(
      effectiveResponsibility,
      context,
    );

    if (!assignedRole) {
      blockers.push({
        code: 'NO_ACCOUNTABLE_ACTOR',
        message: `No active role is available for ${effectiveResponsibility}.`,
        stepId: step.id,
        responsibility: effectiveResponsibility,
      });
      continue;
    }

    const preferredRole = rule.cascade[0];
    if (preferredRole && assignedRole !== preferredRole) {
      fallbacks.push({
        code: 'ROLE_FALLBACK_USED',
        message: `${effectiveResponsibility} routed to ${assignedRole} because ${preferredRole} is unavailable.`,
        stepId: step.id,
        fromRole: preferredRole,
        toRole: assignedRole,
      });
    }

    if (step.professionalScope && qualifiedCompleterRoles.length === 0) {
      // D2: a HOME_CARE tenant with no in-house clinician may satisfy the
      // qualified-clinical-review step with external professional evidence (an
      // external clinician's report on a CLINICAL_REVIEW referral) when it has
      // opted in with an ACTIVE clinical provider. This is a per-step FALLBACK,
      // not a blocker — the workflow builds and the step is completed event-only
      // when the referral report arrives. The clinical allowedCompleters stay
      // CLINICAL_LEAD|REGISTERED_NURSE and professionalScope stays true; only
      // separate administrative fields are layered on.
      const externalSatisfier = resolveExternalClinicalReviewSatisfier(definition, step, context);
      if (externalSatisfier && surfaceResolution.surface) {
        fallbacks.push({
          code: 'EXTERNAL_PROFESSIONAL_SATISFIER_USED',
          message: `${step.label} will be satisfied by an external clinical-review report; ${externalSatisfier.administrativeOwner} obtains and files it (no in-house qualified clinician).`,
          stepId: step.id,
        });
        actions.push({
          stepId: step.id,
          label: step.label,
          actionType: surfaceResolution.actionType,
          surface: surfaceResolution.surface,
          // Clinical semantics preserved: responsibility + allowedCompleters
          // stay clinical; the administrative owner is carried separately.
          responsibility: step.responsibility,
          assignedRole: externalSatisfier.administrativeOwner,
          allowedCompleterRoles: rule.allowedCompleters,
          capabilityId: step.capabilityId,
          evidenceType: step.evidenceType,
          relatedResourceType: step.relatedResourceType,
          professionalScope: true,
          satisfierKind: 'EXTERNAL_PROFESSIONAL_EVIDENCE',
          administrativeResponsibility: 'care_coordination',
          completionMode: 'REFERRAL_REPORT_EVENT_ONLY',
          requiredReferralKind: 'CLINICAL_REVIEW',
        });
        continue;
      }

      blockers.push({
        code: 'NO_QUALIFIED_ACTOR',
        message: `${step.label} requires a qualified professional role before it can be completed.`,
        stepId: step.id,
        responsibility: step.responsibility,
      });
      actions.push({
        stepId: step.id,
        label: `${step.label} configuration gap`,
        actionType: 'configuration_gap',
        surface: 'web',
        responsibility: 'tenant_accountable_person',
        assignedRole,
        allowedCompleterRoles: ['TENANT_ADMIN', 'SYSTEM_ADMIN'],
        capabilityId: step.capabilityId,
        evidenceType: step.evidenceType,
        relatedResourceType: step.relatedResourceType,
      });
      continue;
    }

    if (!surfaceResolution.surface) {
      blockers.push({
        code: 'NO_AVAILABLE_SURFACE',
        message: `No available surface can host ${step.label}.`,
        stepId: step.id,
      });
      continue;
    }

    if (surfaceResolution.fallback) fallbacks.push(surfaceResolution.fallback);

    actions.push({
      stepId: step.id,
      label: step.label,
      actionType: surfaceResolution.actionType,
      surface: surfaceResolution.surface,
      responsibility: effectiveResponsibility,
      assignedRole,
      allowedCompleterRoles: qualifiedCompleterRoles.length
        ? qualifiedCompleterRoles
        : rule.allowedCompleters,
      capabilityId: step.capabilityId,
      evidenceType: step.evidenceType,
      relatedResourceType: step.relatedResourceType,
      fallbackReason: surfaceResolution.fallbackReason,
    });
  }

  return {
    definitionId: definition.id,
    status: compileStatus(blockers, fallbacks),
    actions,
    blockers,
    fallbacks,
  };
}
